import { isOpenableUrl } from "@/lib/open-document-tab";
import { operatorStamped } from "@/lib/operator-stamped";
import { useState, useMemo, useEffect, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, supabaseUrl } from "../../lib/supabase";
import {
  approveBookingForContract,
  formatContractEdgeError,
  invokeCreateContractForBooking,
  invokeSendContractToTenant,
} from "../../lib/adminCreateContract";
import { TenantRowActions } from "./TenantRowActions";
import { isTestAccount } from "@/lib/is-test-account";
import { useTestAccountFilters } from "@/hooks/v2/useTestAccountFilters";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TenantProtocolsSection } from "@/components/admin/v2/tenant/TenantProtocolsSection";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IssueChatThread } from "@/components/issues/IssueChatThread";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Search,
  Download,
  Mail,
  Phone,
  Calendar,
  MapPin,
  User,
  Eye,
  Edit,
  CheckCircle,
  FileText,
  File,
  ShieldCheck,
  AlertCircle,
  CreditCard,
  X,
  CheckCircle2,
  XCircle,
  IdCard,
  Receipt,
  Lock,
  MessageSquare,
  Archive,
  ArchiveRestore,
  ChevronDown,
  TriangleAlert,
  Upload,
  Loader2,
  Trash2,
  Paperclip,
  RefreshCw,
  MoreHorizontal,
  Pencil,
  Plus,
  Replace,
  LogIn,
  Save,
  StickyNote,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { openProofInNewTab } from "@/lib/proof-url";
import { openStoredFile } from "@/lib/storage-url";
import { openTenantDocument } from "@/lib/tenant-document-url";
import {
  contractsBucketPathFromPublicUrl,
  generateStorageFileName,
} from "@/lib/document-storage-filename";
import { notifyTenantDocumentStatusEmail } from "@/lib/notify-tenant-document-email";
import { ReassignRoomDialog } from "@/components/admin/v2/tenant/ReassignRoomDialog";
import {
  TenantProfileDialog,
  type TenantProfileTenant,
} from "@/components/admin/v2/tenant/TenantProfileDialog";
import { AdminNotesSection } from "@/components/admin/v2/tenant/AdminNotesSection";
import { Switch } from "@/components/ui/switch";
import {
  isOpenRentStatus,
  getDisplayedRentEUR,
  getExpectedDepositEUR,
  getFullMonthlyRentFromBooking,
  isDepositFullyPaid,
  isPaymentEffectivelyPaid,
  resolveDepositState,
  roundRentEUR,
  splitRentLineItems,
} from "@/lib/rent";
import {
  effectivePaymentStatus,
  isEffectivelyPaid,
  paymentStatusInput,
} from "@/lib/paymentStatus";
import {
  expectedRentCentsForMonth,
  repriceRentRows,
} from "@/lib/rent-room-sync";
import { recordManualRentPaidInLedger } from "@/lib/manual-rent-paid";
import {
  CLOSED_LEASE_STATUSES,
  isBookingContractSigned,
  resolveLeasePipelineStatus,
  type LeasePipelineStatus,
} from "@/lib/leaseStatus";
import {
  tenantJourneyStageMeta,
  tenantJourneyStageLabel,
  isPreMoveInJourneyStage,
  TENANT_JOURNEY_STAGES,
  type TenantJourneyStageId,
} from "@/lib/tenantJourney";
// The stage badge now lives beside the tenant components so the room view and
// the room history render the identical pill — see TenantJourneyStageBadge.
import { TenantJourneyStageBadge } from "@/components/admin/v2/tenant/TenantJourneyStageBadge";
// The settlement fields the journey needs are declared once, next to the
// signal assembly that reads them.
import {
  resolveTenantJourneyFromSources,
  type DepositSettlementLite,
} from "@/lib/tenantJourneySignals";
import { tenantInfoGapLabel } from "@/lib/tenantInfoGaps";
import { isMissingTable } from "@/hooks/v2/useDepositSettlement";
import { isMissingColumn } from "@/hooks/v2/useContractFlowReady";
import { PROPERTY_RENT_PAID_STATS_KEY } from "@/hooks/v2/usePropertyRentPaidStats";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";
import { isSupersededByReopen } from "@/lib/protocol";
import { cn } from "@/lib/utils";
import { parsePhoneQuery, phoneMatchesQuery } from "@/lib/phone-search";
import { invalidateOccupancyViews } from "@/hooks/v2/useProperties";

/**
 * A submitted handover protocol, reduced to what deciding "does this still
 * stand?" needs. `submitted_at` alone is not enough: an admin recording a
 * walkthrough backdates it, so `isSupersededByReopen` needs `recorded_at` (the
 * real filing moment) and `auto_submitted_at` too.
 */
interface SubmittedProtocolLite {
  kind: string;
  status: "submitted";
  submitted_at: string | null;
  recorded_at: string | null;
  auto_submitted_at: string | null;
}

/**
 * COL-227 follow-up: any admin action that declares rent "paid" must record it
 * in the payments ledger FIRST — booking columns alone leave PaymentsHub, the
 * tenant dashboard and revenue analytics showing the month unpaid. Throws on
 * ledger failure so the caller aborts instead of recreating the drift.
 * Bookings without a tenant account or rent amount are skipped (nothing to
 * attribute in the per-user ledger) — the booking flags still apply.
 */
async function recordBookingPaidInLedger(bookingId: number, note: string) {
  const { data: b, error } = await supabase
    .from("bookings")
    .select("user_id, monthly_rent, rent_due_date")
    .eq("id", bookingId)
    .maybeSingle();
  if (error) throw error;
  if (!b) throw new Error(`Booking #${bookingId} not found`);
  const monthlyRentEur = Number(
    (b as { monthly_rent: number | null }).monthly_rent ?? 0
  );
  if (!Number.isFinite(monthlyRentEur) || monthlyRentEur <= 0) {
    console.warn(
      `Booking #${bookingId} has no monthly rent — ledger row not created.`
    );
    return;
  }
  const {
    data: { user: adminUser },
  } = await supabase.auth.getUser();
  const { error: ledgerErr } = await recordManualRentPaidInLedger({
    bookingId,
    tenantUserId: (b as { user_id: string | null }).user_id ?? null,
    rentDueDay: (b as { rent_due_date: number | null }).rent_due_date ?? null,
    monthlyRentEur,
    adminUserId: adminUser?.id ?? null,
    note,
  });
  if (ledgerErr) throw ledgerErr;
}

const LEASE_PIPELINE_STATUS_STYLES: Record<
  LeasePipelineStatus,
  { label: string; dot: string; badge: string }
> = {
  active: {
    label: "Active",
    dot: "bg-emerald-500/80",
    badge:
      "border-emerald-200 bg-emerald-500/10 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200",
  },
  deposit_pending: {
    label: "Deposit pending",
    dot: "bg-blue-500/80",
    badge:
      "border-blue-200 bg-blue-500/10 text-blue-700 dark:border-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  },
  signature_pending: {
    label: "Signature pending",
    dot: "bg-amber-500/80",
    badge:
      "border-amber-200 bg-amber-500/10 text-amber-700 dark:border-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  },
  notice_given: {
    label: "Notice given",
    dot: "bg-orange-500/80",
    badge:
      "border-orange-200 bg-orange-500/10 text-orange-700 dark:border-orange-800 dark:bg-orange-500/15 dark:text-orange-200",
  },
  move_out_pending: {
    label: "Move-out pending",
    dot: "bg-orange-500/80",
    badge:
      "border-orange-200 bg-orange-500/10 text-orange-700 dark:border-orange-800 dark:bg-orange-500/15 dark:text-orange-200",
  },
  moved_out: {
    label: "Moved out",
    dot: "bg-slate-400/80",
    badge:
      "border-slate-200 bg-slate-500/10 text-slate-600 dark:border-slate-700 dark:bg-slate-500/15 dark:text-slate-300",
  },
  terminated: {
    label: "Terminated",
    dot: "bg-rose-500/80",
    badge:
      "border-rose-200 bg-rose-500/10 text-rose-700 dark:border-rose-800 dark:bg-rose-500/15 dark:text-rose-200",
  },
  cancelled: {
    label: "Cancelled",
    dot: "bg-rose-500/80",
    badge:
      "border-rose-200 bg-rose-500/10 text-rose-700 dark:border-rose-800 dark:bg-rose-500/15 dark:text-rose-200",
  },
  archived: {
    label: "Archived",
    dot: "bg-slate-400/80",
    badge:
      "border-slate-200 bg-slate-500/10 text-slate-600 dark:border-slate-700 dark:bg-slate-500/15 dark:text-slate-300",
  },
  draft: {
    label: "Draft",
    dot: "bg-slate-400/80",
    badge:
      "border-slate-200 bg-slate-500/10 text-slate-600 dark:border-slate-700 dark:bg-slate-500/15 dark:text-slate-300",
  },
};

const LEASE_PIPELINE_STATUS_KEYS: Record<LeasePipelineStatus, string> = {
  active: "admin.tenants.leasePipeline.active",
  deposit_pending: "admin.tenants.leasePipeline.depositPending",
  signature_pending: "admin.tenants.leasePipeline.signaturePending",
  notice_given: "admin.tenants.leasePipeline.noticeGiven",
  move_out_pending: "admin.tenants.leasePipeline.moveOutPending",
  moved_out: "admin.tenants.leasePipeline.movedOut",
  terminated: "admin.tenants.leasePipeline.terminated",
  cancelled: "admin.tenants.leasePipeline.cancelled",
  archived: "admin.tenants.leasePipeline.archived",
  draft: "admin.tenants.leasePipeline.draft",
};

function leasePipelineStatusDisplayLabel(
  status: LeasePipelineStatus,
  t: (key: string, fallback?: string) => string
): string {
  const style =
    LEASE_PIPELINE_STATUS_STYLES[status] ??
    LEASE_PIPELINE_STATUS_STYLES.signature_pending;
  const key =
    LEASE_PIPELINE_STATUS_KEYS[status] ??
    LEASE_PIPELINE_STATUS_KEYS.signature_pending;
  return t(key, style.label);
}

function TenantLeasePipelineStatusBadge({
  status,
  className,
}: {
  status: LeasePipelineStatus;
  className?: string;
}) {
  const { t } = useLanguage();
  const style =
    LEASE_PIPELINE_STATUS_STYLES[status] ??
    LEASE_PIPELINE_STATUS_STYLES.signature_pending;
  return (
    <Badge
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5",
        style.badge,
        className
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", style.dot)} />
      {leasePipelineStatusDisplayLabel(status, t)}
    </Badge>
  );
}

/** True when contract was supplied outside a completed DocuSign flow (admin/tenant PDF, legacy URL, or DB flag). */
function computeShowManualContractLabel(booking: any): boolean {
  if (!booking) return false;
  if (booking.manual_contract_upload) return true;
  const signedUrl = !!(
    booking.signed_contract_url && String(booking.signed_contract_url).trim()
  );
  const legacyContractUrl = !!(
    booking.contract_url && String(booking.contract_url).trim()
  );
  const hasDocusign = !!(
    booking.docusign_envelope_id && String(booking.docusign_envelope_id).trim()
  );
  const hasSignedAt = !!booking.contract_signed_at;
  // DocuSign completion sets contract_signed_at; uploads via dashboard/admin often do not while still awaiting approval.
  if (signedUrl && (!hasDocusign || !hasSignedAt)) return true;
  if (legacyContractUrl && !signedUrl) return true;
  return false;
}

function TenantPipelineContractStatusBadge({
  contractStatus,
  className,
}: {
  contractStatus?: string | null;
  className?: string;
}) {
  const { t } = useLanguage();
  const cs = String(contractStatus || "not_ready")
    .trim()
    .toLowerCase();
  if (cs === "signed" || cs === "approved") {
    return (
      <Badge
        className={cn(
          "bg-emerald-50 text-emerald-700 border-emerald-200",
          className
        )}
      >
        {t("admin.tenants.contractSignedBadge", "Contract Signed")}
      </Badge>
    );
  }
  // Generated but not released to the tenant — never say "Ready to Sign" here,
  // the tenant cannot see or sign it yet. Slate, NOT the blue used below: in this
  // pipeline blue means "the ball is with the tenant", and a draft is the one
  // contract state where it is entirely with us. Same colour for both read as the
  // same stage at a glance, which is the misreading that matters here — an admin
  // scanning the list would think the tenant had been asked to sign.
  if (cs === "draft") {
    return (
      <Badge
        className={cn(
          "bg-slate-100 text-slate-700 border-slate-200",
          className
        )}
      >
        {t("admin.tenants.draftNotSentBadge", "Draft — not sent")}
      </Badge>
    );
  }
  if (cs === "ready_to_sign" || cs === "envelope_draft") {
    return (
      <Badge
        className={cn("bg-blue-50 text-blue-700 border-blue-200", className)}
      >
        {t("admin.tenants.readyToSignBadge", "Ready to Sign")}
      </Badge>
    );
  }
  if (cs === "awaiting_approval") {
    return (
      <Badge
        className={cn("bg-amber-50 text-amber-700 border-amber-200", className)}
      >
        {t("admin.tenants.waitingForApprovalBadge", "Waiting for approval")}
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className={className}>
      {t("admin.tenants.notReadyBadge", "Not Ready")}
    </Badge>
  );
}

function notifyContractReadyEmailOutcome(body: Record<string, unknown>) {
  if (body?.contractReadyEmailSent === true) return;
  const skipped = body?.contractReadyEmailSkipped;
  const err = body?.contractReadyEmailError;
  if (skipped == null && err == null) return;
  const description = [
    skipped != null && skipped !== "" ? `Reason: ${String(skipped)}` : null,
    err != null && err !== "" ? String(err) : null,
  ]
    .filter(Boolean)
    .join(" ");
  toast.warning(
    'Contract saved; the tenant may not have received the "ready to sign" email.',
    {
      description: description || undefined,
      duration: 14000,
    }
  );
}

// Tenant Documents Management Component for Personal Information Section
const TenantDocumentsManagement = ({ tenant }: { tenant: any }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState<
    "id_passport" | "proof_of_income"
  >("id_passport");
  const [uploadTitle, setUploadTitle] = useState("");
  const uploadFileRef = useRef<HTMLInputElement>(null);
  const [docToDelete, setDocToDelete] = useState<any>(null);

  // Fetch documents for this tenant
  const { data: documents, isLoading } = useQuery({
    queryKey: ["tenant-documents-overview", tenant?.userId, tenant?.id],
    queryFn: async () => {
      if (!tenant) return [];

      const queries: PromiseLike<any>[] = [];

      if (tenant.userId) {
        queries.push(
          supabase
            .from("tenant_documents")
            .select("*")
            .eq("user_id", tenant.userId)
            .order("created_at", { ascending: false })
        );
      }

      if (tenant.id) {
        queries.push(
          supabase
            .from("tenant_documents")
            .select("*")
            .eq("booking_id", tenant.id)
            .order("created_at", { ascending: false })
        );
      }

      if (queries.length === 0) return [];

      const results = await Promise.all(queries);
      const allDocs: any[] = [];
      for (const result of results) {
        if (result.error) throw result.error;
        if (result.data) {
          allDocs.push(...result.data);
        }
      }

      const uniqueDocs = Array.from(
        new Map(allDocs.map((doc: any) => [doc.id, doc])).values()
      ).sort((a: any, b: any) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      return uniqueDocs;
    },
    enabled: !!tenant,
  });

  const uploadTenantDocument = useMutation({
    mutationFn: async ({
      file,
      documentType,
      title,
    }: {
      file: File;
      documentType: "id_passport" | "proof_of_income";
      title: string;
    }) => {
      if (!tenant?.userId) {
        throw new Error(
          "This booking has no linked tenant account; documents cannot be attached."
        );
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (file.size > 10 * 1024 * 1024)
        throw new Error("File exceeds 10MB limit");

      const storageFileName = generateStorageFileName(file.name, tenant.userId);
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(storageFileName, file, {
          contentType: file.type || "application/octet-stream",
          upsert: false,
        });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(storageFileName);
      const now = new Date().toISOString();
      const row = operatorStamped<"tenant_documents">({
        user_id: tenant.userId,
        document_type: documentType,
        title: title.trim() || file.name,
        file_url: urlData.publicUrl,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || null,
        uploaded_by: user.id,
        // Filed FROM the admin area, so the owner portal credits CoLiviq and
        // not the tenant. uploaded_by cannot carry this: an admin who is also a
        // tenant of the unit writes the same uuid either way.
        source: "admin",
        status: "pending",
        created_at: now,
        updated_at: now,
        ...(tenant.id && { booking_id: tenant.id }),
      });

      const { error: insertError } = await supabase
        .from("tenant_documents")
        .insert(row);
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents-overview", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
      if (tenant?.userId) {
        queryClient.invalidateQueries({
          queryKey: ["identity-documents", tenant.userId],
        });
        queryClient.invalidateQueries({
          queryKey: ["salary-documents", tenant.userId],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["identity-documents"] });
      queryClient.invalidateQueries({ queryKey: ["salary-documents"] });
      invalidateOccupancyViews(queryClient);
      toast.success("Document uploaded");
      setUploadDialogOpen(false);
      setUploadTitle("");
      if (uploadFileRef.current) uploadFileRef.current.value = "";
    },
    onError: (error: any) => {
      toast.error(
        error.message || t("admin.tenants.uploadFailedToast", "Upload failed")
      );
    },
  });

  const removeTenantDocumentMutation = useMutation({
    mutationFn: async (doc: { id: string; file_url?: string | null }) => {
      if (doc.file_url) {
        const path = contractsBucketPathFromPublicUrl(doc.file_url);
        if (path) {
          const { error: storageErr } = await supabase.storage
            .from("contracts")
            .remove([path]);
          if (storageErr) console.warn("Storage remove:", storageErr);
        }
      }
      const { error } = await supabase
        .from("tenant_documents")
        .delete()
        .eq("id", doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents-overview", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
      if (tenant?.userId) {
        queryClient.invalidateQueries({
          queryKey: ["identity-documents", tenant.userId],
        });
        queryClient.invalidateQueries({
          queryKey: ["salary-documents", tenant.userId],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["identity-documents"] });
      queryClient.invalidateQueries({ queryKey: ["salary-documents"] });
      invalidateOccupancyViews(queryClient);
      toast.success("Document removed");
      setDocToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove document");
    },
  });

  const updateDocumentStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      notes,
      docTitle,
      docDocumentType,
    }: {
      id: string;
      status: string;
      notes?: string;
      docTitle?: string | null;
      docDocumentType?: string | null;
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("tenant_documents")
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user?.id || null,
          notes: notes || null,
        })
        .eq("id", id);

      if (error) throw error;
      return { status, docTitle, docDocumentType, notes, id };
    },
    onSuccess: async (result) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents-overview", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
      if (tenant?.userId) {
        queryClient.invalidateQueries({
          queryKey: ["identity-documents", tenant.userId],
        });
        queryClient.invalidateQueries({
          queryKey: ["salary-documents", tenant.userId],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["identity-documents"] });
      queryClient.invalidateQueries({ queryKey: ["salary-documents"] });
      invalidateOccupancyViews(queryClient);
      toast.success("Document status updated");
      setRejectDialogOpen(false);
      setRejectionReason("");
      setSelectedDoc(null);
      if (result.status === "approved" || result.status === "rejected") {
        await notifyTenantDocumentStatusEmail(
          tenant?.userId,
          result.status as "approved" | "rejected",
          {
            tenantName: tenant?.profile?.name || tenant?.guestName,
            documentId: result.id,
            documents: [
              {
                title: result.docTitle,
                documentType: result.docDocumentType,
                status: result.status as "approved" | "rejected",
              },
            ],
            rejectionNotes:
              result.status === "rejected" ? result.notes : undefined,
          }
        );
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update document");
    },
  });

  const handleApprove = (doc: any) => {
    updateDocumentStatus.mutate({
      id: doc.id,
      status: "approved",
      docTitle: doc.title || doc.file_name,
      docDocumentType: doc.document_type,
    });
  };

  const handleRejectClick = (doc: any) => {
    setSelectedDoc(doc);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = () => {
    if (selectedDoc && rejectionReason.trim()) {
      updateDocumentStatus.mutate({
        id: selectedDoc.id,
        status: "rejected",
        notes: rejectionReason.trim(),
        docTitle: selectedDoc.title || selectedDoc.file_name,
        docDocumentType: selectedDoc.document_type,
      });
    }
  };

  // file_url is the public URL stored at upload; signed on click (COL-436).
  const openTenantDocUrl = async (doc: any) => {
    if (!doc.file_url) return;
    if (!(await openTenantDocument(String(doc.file_url).trim()))) {
      toast.error(
        "Failed to open document. Please try downloading it instead."
      );
    }
  };

  const renderTenantDocRowActions = (doc: any) => (
    <div className="flex items-center gap-2 flex-shrink-0">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => openTenantDocUrl(doc)}
        title="View"
      >
        <Eye className="w-4 h-4" />
      </Button>
      {doc.status === "pending" && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleApprove(doc)}
            disabled={updateDocumentStatus.isPending}
            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
            title="Approve"
          >
            <CheckCircle2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleRejectClick(doc)}
            disabled={updateDocumentStatus.isPending}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            title="Reject"
          >
            <XCircle className="w-4 h-4" />
          </Button>
        </>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setDocToDelete(doc)}
        disabled={removeTenantDocumentMutation.isPending}
        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
        title="Remove document"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  const identityDocs =
    documents?.filter((doc: any) => doc.document_type === "id_passport") || [];
  const salaryDocs =
    documents?.filter((doc: any) => doc.document_type === "proof_of_income") ||
    [];

  const openUploadDialog = (docType: "id_passport" | "proof_of_income") => {
    if (!tenant?.userId) {
      toast.error(
        "No tenant account is linked to this booking; documents cannot be attached."
      );
      return;
    }
    setUploadDocType(docType);
    setUploadTitle("");
    setUploadDialogOpen(true);
  };

  if (!tenant) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-muted-foreground">
          Select a tenant to view documents
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Identity Documents */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <IdCard className="w-5 h-5 text-primary" />
            <h4 className="font-semibold text-foreground">
              Identity Documents
            </h4>
            <Badge variant="secondary">{identityDocs.length}</Badge>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => openUploadDialog("id_passport")}
          >
            <Upload className="w-4 h-4" />
            Add
          </Button>
        </div>
        {identityDocs.length === 0 ? (
          <p className="text-sm text-muted-foreground pl-7">
            No identity documents uploaded
          </p>
        ) : (
          <div className="space-y-2 pl-7">
            {identityDocs.map((doc: any) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 rounded-lg border border-border/30 bg-background/60 hover:bg-background/80 transition-all"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <File className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {doc.title || doc.file_name || "Document"}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {doc.status === "pending" && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                          Pending
                        </Badge>
                      )}
                      {doc.status === "approved" && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          Approved
                        </Badge>
                      )}
                      {doc.status === "rejected" && (
                        <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs">
                          Rejected
                        </Badge>
                      )}
                    </div>
                    {doc.status === "rejected" && doc.notes && (
                      <p className="text-xs text-destructive mt-1">
                        Reason: {doc.notes}
                      </p>
                    )}
                  </div>
                </div>
                {renderTenantDocRowActions(doc)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Salary Statements */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" />
            <h4 className="font-semibold text-foreground">Salary Statements</h4>
            <Badge variant="secondary">{salaryDocs.length}</Badge>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => openUploadDialog("proof_of_income")}
          >
            <Upload className="w-4 h-4" />
            Add
          </Button>
        </div>
        {salaryDocs.length === 0 ? (
          <p className="text-sm text-muted-foreground pl-7">
            No salary statements uploaded
          </p>
        ) : (
          <div className="space-y-2 pl-7">
            {salaryDocs.map((doc: any) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 rounded-lg border border-border/30 bg-background/60 hover:bg-background/80 transition-all"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <File className="w-4 h-4 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {doc.title || doc.file_name || "Document"}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {doc.status === "pending" && (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                          Pending
                        </Badge>
                      )}
                      {doc.status === "approved" && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          Approved
                        </Badge>
                      )}
                      {doc.status === "rejected" && (
                        <Badge className="bg-destructive/10 text-destructive border-destructive/20 text-xs">
                          Rejected
                        </Badge>
                      )}
                    </div>
                    {doc.status === "rejected" && doc.notes && (
                      <p className="text-xs text-destructive mt-1">
                        Reason: {doc.notes}
                      </p>
                    )}
                  </div>
                </div>
                {renderTenantDocRowActions(doc)}
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog
        open={!!docToDelete}
        onOpenChange={(open) => {
          if (!open) setDocToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this document?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes "
              {docToDelete?.title || docToDelete?.file_name || "this file"}"
              from the tenant's documents and removes the file from storage.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={removeTenantDocumentMutation.isPending}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={removeTenantDocumentMutation.isPending}
              onClick={() => {
                if (docToDelete)
                  removeTenantDocumentMutation.mutate(docToDelete);
              }}
            >
              {removeTenantDocumentMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing…
                </>
              ) : (
                "Remove"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(open) => {
          setUploadDialogOpen(open);
          if (!open && uploadFileRef.current) uploadFileRef.current.value = "";
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add document for tenant</DialogTitle>
            <DialogDescription>
              {uploadDocType === "id_passport" &&
                "Identity document (e.g. government ID)"}
              {uploadDocType === "proof_of_income" &&
                "Salary or proof of income"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="admin-upload-title">Title</Label>
              <Input
                id="admin-upload-title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="e.g. Government ID"
              />
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <input
                ref={uploadFileRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif"
                className="text-sm w-full"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && !uploadTitle.trim()) {
                    setUploadTitle(f.name.replace(/\.[^/.]+$/, ""));
                  }
                }}
              />
              <p className="text-xs text-muted-foreground">
                Max 10 MB. PDF or images.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const file = uploadFileRef.current?.files?.[0];
                if (!file) {
                  toast.error("Choose a file");
                  return;
                }
                uploadTenantDocument.mutate({
                  file,
                  documentType: uploadDocType,
                  title: uploadTitle.trim() || file.name,
                });
              }}
              disabled={uploadTenantDocument.isPending}
            >
              {uploadTenantDocument.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading…
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Document</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this document. This will be
              visible to the tenant.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason *</Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter the reason for rejection..."
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectionReason("");
                setSelectedDoc(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={
                !rejectionReason.trim() || updateDocumentStatus.isPending
              }
            >
              {updateDocumentStatus.isPending
                ? "Rejecting..."
                : "Reject Document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Tenant Documents Tab Component
const TenantDocumentsTab = ({ tenant }: { tenant: any }) => {
  const queryClient = useQueryClient();

  // Fetch documents for this tenant
  const { data: documents, isLoading } = useQuery({
    queryKey: ["tenant-documents", tenant?.userId, tenant?.id],
    queryFn: async () => {
      if (!tenant) return [];

      // Fetch documents by user_id and/or booking_id
      // We need to get documents that match either user_id OR booking_id
      const queries: PromiseLike<any>[] = [];

      if (tenant.userId) {
        queries.push(
          supabase
            .from("tenant_documents")
            .select("*")
            .eq("user_id", tenant.userId)
            .order("created_at", { ascending: false })
        );
      }

      if (tenant.id) {
        queries.push(
          supabase
            .from("tenant_documents")
            .select("*")
            .eq("booking_id", tenant.id)
            .order("created_at", { ascending: false })
        );
      }

      if (queries.length === 0) return [];

      // Execute all queries
      const results = await Promise.all(queries);

      // Combine results and check for errors
      const allDocs: any[] = [];
      for (const result of results) {
        if (result.error) throw result.error;
        if (result.data) {
          allDocs.push(...result.data);
        }
      }

      // Deduplicate by id and sort by created_at
      const uniqueDocs = Array.from(
        new Map(allDocs.map((doc: any) => [doc.id, doc])).values()
      ).sort((a: any, b: any) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      return uniqueDocs;
    },
    enabled: !!tenant,
  });

  const updateDocumentStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      notes,
      docTitle,
      docDocumentType,
    }: {
      id: string;
      status: string;
      notes?: string;
      docTitle?: string | null;
      docDocumentType?: string | null;
    }) => {
      const { error } = await supabase
        .from("tenant_documents")
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: (await supabase.auth.getUser()).data.user?.id || null,
          notes: notes || null,
        })
        .eq("id", id);

      if (error) throw error;
      return { status, docTitle, docDocumentType, notes, id };
    },
    onSuccess: async (result) => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-documents-overview", tenant?.userId, tenant?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
      toast.success("Document status updated");
      if (result.status === "approved" || result.status === "rejected") {
        await notifyTenantDocumentStatusEmail(
          tenant?.userId,
          result.status as "approved" | "rejected",
          {
            tenantName: tenant?.profile?.name || tenant?.guestName,
            documentId: result.id,
            documents: [
              {
                title: result.docTitle,
                documentType: result.docDocumentType,
                status: result.status as "approved" | "rejected",
              },
            ],
            rejectionNotes:
              result.status === "rejected" ? result.notes : undefined,
          }
        );
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update document");
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      approved: (
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
          Approved
        </Badge>
      ),
      rejected: (
        <Badge className="bg-destructive/10 text-destructive border-destructive/20">
          Rejected
        </Badge>
      ),
      pending: (
        <Badge className="bg-amber-50 text-amber-700 border-amber-200">
          Pending
        </Badge>
      ),
      expired: <Badge variant="secondary">Expired</Badge>,
    };
    return variants[status] || <Badge variant="secondary">{status}</Badge>;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      contract: "Contract",
      id_passport: "ID/Passport",
      proof_of_income: "Proof of Income",
      employment_letter: "Employment Letter",
      reference: "Reference",
      other: "Other",
    };
    return labels[type] || type;
  };

  /* ── helpers ── */
  const getFileIconColor = (type: string) => {
    const map: Record<string, string> = {
      contract: "bg-blue-500/10 text-blue-600",
      id_passport: "bg-violet-500/10 text-violet-600",
      proof_of_income: "bg-emerald-500/10 text-emerald-600",
      employment_letter: "bg-orange-500/10 text-orange-600",
      reference: "bg-cyan-500/10 text-cyan-600",
      other: "bg-muted text-muted-foreground",
    };
    return map[type] || "bg-muted text-muted-foreground";
  };

  const getStatusChip = (status: string) => {
    if (status === "approved")
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          <CheckCircle2 className="w-3 h-3" /> Approved
        </span>
      );
    if (status === "rejected")
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 border border-red-200 px-2 py-0.5 text-[11px] font-semibold text-red-700">
          <XCircle className="w-3 h-3" /> Rejected
        </span>
      );
    if (status === "pending")
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />{" "}
          Pending
        </span>
      );
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground border border-border">
        {status}
      </span>
    );
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-2xl border border-border/60 bg-card p-5 flex items-center gap-4"
          >
            <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-8 w-20 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border/60 bg-muted/20 flex flex-col items-center justify-center py-16 gap-3">
        <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-muted">
          <FileText className="w-6 h-6 text-muted-foreground/40" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-foreground">
            No documents yet
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Documents uploaded by this tenant will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/40 bg-muted/20">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-500/10">
            <FileText className="w-4 h-4 text-blue-500" />
          </div>
          <span className="text-sm font-semibold">Uploaded Documents</span>
        </div>
        <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-primary/10 px-1.5 text-[11px] font-bold text-primary">
          {documents.length}
        </span>
      </div>

      {/* list */}
      <div className="divide-y divide-border/40">
        {documents.map((doc: any) => (
          <div
            key={doc.id}
            className="p-5 flex items-start gap-4 hover:bg-muted/20 transition-colors"
          >
            {/* file icon */}
            <div
              className={cn(
                "flex items-center justify-center w-10 h-10 rounded-xl shrink-0",
                getFileIconColor(doc.document_type)
              )}
            >
              <FileText className="w-5 h-5" />
            </div>

            {/* main content */}
            <div className="flex-1 min-w-0">
              {/* name + badges */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <p
                  className="text-sm font-semibold truncate max-w-[260px]"
                  title={doc.title || doc.file_name}
                >
                  {doc.title || doc.file_name || "Untitled"}
                </p>
                <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/60 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                  {getDocumentTypeLabel(doc.document_type)}
                </span>
                {getStatusChip(doc.status)}
              </div>

              {/* meta strip */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <File className="w-3 h-3" />
                  {formatFileSize(doc.file_size)}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Uploaded {fmtDate(doc.created_at)}
                </span>
                {doc.reviewed_at && (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                    Reviewed {fmtDate(doc.reviewed_at)}
                  </span>
                )}
                {doc.expires_at && (
                  <span className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-amber-500" />
                    Expires {fmtDate(doc.expires_at)}
                  </span>
                )}
              </div>

              {/* rejection notes */}
              {doc.notes && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200/60 px-3 py-2">
                  <TriangleAlert className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">{doc.notes}</p>
                </div>
              )}

              {/* approve / reject actions for pending docs */}
              {doc.status === "pending" && (
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    size="sm"
                    className="h-7 rounded-lg text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() =>
                      updateDocumentStatus.mutate({
                        id: doc.id,
                        status: "approved",
                        docTitle: doc.title || doc.file_name,
                        docDocumentType: doc.document_type,
                      })
                    }
                    disabled={updateDocumentStatus.isPending}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 rounded-lg text-xs gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => {
                      const notes = prompt(
                        "Enter rejection reason (optional):"
                      );
                      updateDocumentStatus.mutate({
                        id: doc.id,
                        status: "rejected",
                        notes: notes || undefined,
                        docTitle: doc.title || doc.file_name,
                        docDocumentType: doc.document_type,
                      });
                    }}
                    disabled={updateDocumentStatus.isPending}
                  >
                    <XCircle className="w-3.5 h-3.5" /> Reject
                  </Button>
                </div>
              )}
            </div>

            {/* view / download buttons */}
            {doc.file_url && (
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-lg"
                  title="View document"
                  onClick={async () => {
                    const url = String(doc.file_url || "").trim();
                    if (!url) {
                      toast.error("Invalid document URL");
                      return;
                    }
                    // Signed on click: the stored URL is public (COL-436).
                    if (!(await openTenantDocument(url))) {
                      toast.error("Failed to open document.");
                    }
                  }}
                >
                  <Eye className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-lg"
                  title="Download document"
                  onClick={async () => {
                    // `<a download>` is ignored cross-origin; a signed URL
                    // with download set asks storage for the attachment.
                    const ok = await openTenantDocument(doc.file_url, {
                      download: doc.file_name || doc.title || true,
                    });
                    if (!ok) toast.error("Failed to open document.");
                  }}
                >
                  <Download className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Headline "current rent" for admin: when a payment row exists for the calendar month, its `amount_cents`
 * is what we bill (per-month edits, Stripe checkout, tenant dashboard). Otherwise fall back to booking terms.
 */
async function resolveRoomIdForBookingTenant(
  supabaseClient: typeof supabase,
  tenant: any,
  bookingId: string | number
): Promise<number | null> {
  const roomId = tenant?.room?.id != null ? Number(tenant.room.id) : null;
  if (Number.isFinite(roomId) && roomId > 0) return roomId;
  const fromBooking =
    tenant?.booking?.room_id != null ? Number(tenant.booking.room_id) : null;
  if (Number.isFinite(fromBooking) && fromBooking > 0) return fromBooking;
  const { data: row } = await (supabaseClient as any)
    .from("bookings")
    .select("room_id")
    .eq("id", bookingId)
    .maybeSingle();
  const rid = row?.room_id != null ? Number(row.room_id) : null;
  return Number.isFinite(rid) && rid > 0 ? rid : null;
}

/** Booking terms only (`monthly_rent` / room list price), not payment-line edits or `displayedRent`. */
function getBookingContractMonthlyRentForDisplay(tenant: any): number {
  if (tenant?.monthlyRent != null && tenant.monthlyRent !== "") {
    return Number(tenant.monthlyRent);
  }
  if (tenant?.room?.price != null) {
    return Number(tenant.room.price);
  }
  return 0;
}

function resolveExpectedMonthlyRentForDisplay(
  tenant: any,
  _currentMonthPayment?: { amount_cents?: number | null } | null
): number {
  void _currentMonthPayment;
  return getBookingContractMonthlyRentForDisplay(tenant);
}

// Tenant Payments Tab — rental & deposit details, paid fraction, next due, room details
const TenantPaymentsTab = ({ tenant }: { tenant: any }) => {
  const { t } = useLanguage();
  const { data: payments, isLoading } = useQuery({
    queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
    queryFn: async () => {
      const FIELDS =
        // marked_paid_at / marked_paid_by are what make an off-platform
        // settlement evidence rather than a claim — the canonical paid rule
        // reads them, so a row fetched without them can never resolve to paid
        // (#1326).
        "id, booking_id, type, amount_cents, received_cents, status, due_date, paid_at, stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by, description, created_at";
      const bookingId = tenant?.id != null ? Number(tenant.id) : null;

      if (tenant?.userId) {
        // Primary path: fetch by user_id then filter to this booking.
        const { data, error } = await supabase
          .from("payments")
          .select(FIELDS)
          .eq("user_id", tenant.userId)
          .in("type", ["rent", "deposit"])
          .order("due_date", { ascending: false });
        if (error) throw error;
        const list = data || [];
        if (bookingId !== null) {
          return list.filter(
            (p: any) =>
              Number(p.booking_id) === bookingId || p.booking_id == null
          );
        }
        return list;
      }

      // Fallback: no linked auth user — fetch by booking_id only
      if (bookingId !== null) {
        const { data, error } = await supabase
          .from("payments")
          .select(FIELDS)
          .eq("booking_id", bookingId)
          .in("type", ["rent", "deposit"])
          .order("due_date", { ascending: false });
        if (error) throw error;
        return data || [];
      }

      return [];
    },
    enabled: !!tenant?.userId || !!tenant?.id,
  });

  const queryClient = useQueryClient();
  const paymentIds = useMemo(
    () => (payments || []).map((p: any) => p.id).filter(Boolean),
    [payments]
  );

  const { data: proofsByPaymentId = {} } = useQuery<Record<string, any>>({
    queryKey: [
      "tenant-activity-payment-proofs",
      tenant?.userId,
      tenant?.id,
      paymentIds.join(","),
    ],
    queryFn: async () => {
      if (!paymentIds.length) return {};
      const { data, error } = await supabase
        .from("payment_proofs")
        .select(
          "id, payment_id, file_path, file_name, status, reviewed_at, created_at, rejection_reason"
        )
        .in("payment_id", paymentIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const map: Record<string, any> = {};
      (data || []).forEach((row: any) => {
        if (map[row.payment_id]) return;
        map[row.payment_id] = row;
      });
      return map;
    },
    enabled: (!!tenant?.userId || !!tenant?.id) && paymentIds.length > 0,
  });

  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [proofTarget, setProofTarget] = useState<{
    id: string;
    type: "rent" | "deposit";
  } | null>(null);
  const proofFileRef = useRef<HTMLInputElement>(null);
  const [proofUploading, setProofUploading] = useState(false);
  const [proofProgress, setProofProgress] = useState(0);
  const [proofToDelete, setProofToDelete] = useState<any>(null);
  const [ensuringRentKey, setEnsuringRentKey] = useState<string | null>(null);
  const [ensuringDeposit, setEnsuringDeposit] = useState(false);
  const [showAllRentMonths, setShowAllRentMonths] = useState(false);
  // Per-payment admin notes (COL-245): which month row has its notes panel open,
  // and the resolved payment id it's scoped to (created on demand if missing).
  const [notesOpenKey, setNotesOpenKey] = useState<string | null>(null);
  const [notesPaymentId, setNotesPaymentId] = useState<string | null>(null);
  const [ensuringNotesKey, setEnsuringNotesKey] = useState<string | null>(null);
  const [rentPaymentEditOpen, setRentPaymentEditOpen] = useState(false);
  const [rentPaymentEditTarget, setRentPaymentEditTarget] = useState<{
    y: number;
    m: number;
    label: string;
    paymentId: string;
  } | null>(null);
  const [rentPaymentEditAmount, setRentPaymentEditAmount] = useState("");
  const [rentPaymentEditDueDate, setRentPaymentEditDueDate] = useState("");
  const [rentPaymentEditProrateMode, setRentPaymentEditProrateMode] = useState<
    "auto" | "preset" | "custom"
  >("auto");
  const [rentPaymentEditProrateCustomPct, setRentPaymentEditProrateCustomPct] =
    useState("");
  const [
    rentPaymentEditFirstMonthOverride,
    setRentPaymentEditFirstMonthOverride,
  ] = useState("");
  const [rentPaymentEditFirstMonthAuto, setRentPaymentEditFirstMonthAuto] =
    useState(true);
  const [rentPaymentEditComputedAmount, setRentPaymentEditComputedAmount] =
    useState<number | null>(null);

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat("en-EU", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(cents / 100);

  const formatDate = (dateStr: string | null) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "—";

  /**
   * `due_date` is a DATE in Postgres and comes to the client as `YYYY-MM-DD`.
   * `new Date('YYYY-MM-DD')` is parsed as UTC midnight and can shift to the prior day/month in negative timezones.
   * For month-based logic (Next payment, month rows), treat it as a pure calendar date.
   */
  const parseDateOnly = (iso: string) =>
    new Date(`${String(iso).slice(0, 10)}T00:00:00.000Z`);

  /** Stripe card/SEPA checkout; everything else (bank transfer, admin, cash) is Manual for *logic* (status rules). */
  const getPaymentMethodLabel = (p: any): "Stripe" | "Manual" => {
    if (p?.stripe_payment_intent_id || p?.stripe_charge_id) return "Stripe";
    return "Manual";
  };

  /**
   * Payment history: only show Stripe / Manual when meaningful.
   * Non-Stripe rows without proof are not "manual payment" yet — show em dash.
   */
  const getPaymentHistoryMethodLabel = (
    p: any,
    proof: any | null | undefined
  ): "Stripe" | "Manual" | null => {
    if (p?.stripe_payment_intent_id || p?.stripe_charge_id) return "Stripe";
    if (proof) return "Manual";
    return null;
  };

  /** UI: show Stripe when paid via Stripe; show Manual only when a proof document exists; otherwise em dash (e.g. after delete or before upload). */
  const renderPaymentMethodCell = (
    p: any | null | undefined,
    proof: any | null | undefined
  ) => {
    if (!p) return <span className="text-xs text-muted-foreground">—</span>;
    const isStripe = !!(p.stripe_payment_intent_id || p.stripe_charge_id);
    if (isStripe) {
      const pi = p.stripe_payment_intent_id
        ? String(p.stripe_payment_intent_id)
        : "";
      const ch = p.stripe_charge_id ? String(p.stripe_charge_id) : "";
      return (
        <div className="flex min-h-[44px] flex-col justify-center gap-1">
          <span className="inline-flex w-fit items-center rounded-full border border-violet-200/90 bg-violet-500/[0.08] px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-violet-800 dark:border-violet-800/80 dark:text-violet-200">
            Stripe
          </span>
          {(pi || ch) && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-fit max-w-[168px] truncate text-left font-mono text-[10px] leading-snug text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
                  title="View transaction IDs"
                >
                  {pi
                    ? `${pi.slice(0, 22)}${pi.length > 22 ? "…" : ""}`
                    : ch
                      ? `ch_${ch.slice(0, 18)}…`
                      : ""}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 space-y-2 p-3" align="start">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Stripe
                </p>
                {pi ? (
                  <div>
                    <p className="text-[10px] font-medium text-muted-foreground">
                      Payment Intent
                    </p>
                    <p className="break-all font-mono text-xs">{pi}</p>
                  </div>
                ) : null}
                {ch ? (
                  <div>
                    <p className="text-[10px] font-medium text-muted-foreground">
                      Charge
                    </p>
                    <p className="break-all font-mono text-xs">{ch}</p>
                  </div>
                ) : null}
              </PopoverContent>
            </Popover>
          )}
        </div>
      );
    }
    if (!proof) {
      return (
        <div className="flex min-h-[44px] items-center">
          <span className="text-xs text-muted-foreground">—</span>
        </div>
      );
    }
    return (
      <div className="flex min-h-[44px] items-center">
        <span className="inline-flex items-center rounded-full border border-slate-200/90 bg-slate-500/[0.08] px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-slate-700 dark:border-slate-600 dark:text-slate-300">
          Manual
        </span>
      </div>
    );
  };

  /**
   * Manual (non-Stripe) payments marked paid via proof should show Pending once proof is removed.
   * Stripe-paid rows stay driven by card/SEPA status. Delegates to the canonical
   * effectivePaymentStatus (COL-227) so this can never drift from PaymentsHub
   * or the tenant Dashboard again.
   */
  const getEffectivePaymentStatus = (
    p: any | null | undefined,
    proof: any | null | undefined
  ): string => {
    if (!p) return "pending";
    return effectivePaymentStatus(
      paymentStatusInput(p, proof, {
        isStripe: getPaymentMethodLabel(p) === "Stripe",
      })
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const map: Record<string, { className: string; label: string }> = {
      paid: {
        className:
          "bg-emerald-500/10 text-emerald-700 border-emerald-200 dark:border-emerald-800 dark:text-emerald-400",
        label: "Paid",
      },
      pending: {
        className:
          "bg-amber-500/10 text-amber-700 border-amber-200 dark:border-amber-800 dark:text-amber-400",
        label: "Pending",
      },
      scheduled: {
        className:
          "bg-slate-500/10 text-slate-600 border-slate-200 dark:border-slate-700 dark:text-slate-400",
        label: "Scheduled",
      },
      overdue: {
        className:
          "bg-red-500/10 text-red-700 border-red-200 dark:border-red-800 dark:text-red-400",
        label: "Overdue",
      },
      failed: {
        className:
          "bg-red-500/10 text-red-700 border-red-200 dark:border-red-800 dark:text-red-400",
        label: "Failed",
      },
      canceled: {
        className:
          "bg-slate-400/10 text-slate-500 border-slate-200 dark:text-slate-500",
        label: "Canceled",
      },
    };
    const c = map[status] || {
      className: "bg-muted text-muted-foreground border-border",
      label: status,
    };
    return (
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${c.className}`}
      >
        {c.label}
      </span>
    );
  };

  const depositPayments =
    payments?.filter((p: any) => p.type === "deposit") || [];
  const rentPayments = payments?.filter((p: any) => p.type === "rent") || [];
  const forThisBooking = (p: any) =>
    tenant?.id == null || Number(p.booking_id) === Number(tenant.id);
  /**
   * Stripe: trust the DB status. Manual/bank transfer: an APPROVED proof is
   * authoritative. Delegates to the canonical rule (COL-227) so admin metrics,
   * PaymentsHub and the tenant Dashboard can never disagree.
   */
  const isPaymentCountedAsPaidForMetrics = (p: any) => {
    if (!p) return false;
    return isEffectivelyPaid(
      paymentStatusInput(p, proofsByPaymentId[String(p.id)] ?? null, {
        isStripe: getPaymentMethodLabel(p) === "Stripe",
      })
    );
  };
  // All deposit rows for this booking (paid + unpaid) — the source of truth.
  const depositPaymentsThisBooking = depositPayments.filter(forThisBooking);
  const depositPaidConfirmed = depositPaymentsThisBooking.filter((p: any) =>
    isPaymentCountedAsPaidForMetrics(p)
  );
  // Contractual expected deposit (stored deposit_amount; room base / 2× rent
  // fallback; minus any tenant-specific deposit discount, COL-360).
  const expectedDeposit = getExpectedDepositEUR({
    monthly_rent: tenant?.booking?.monthly_rent,
    deposit_amount: tenant?.booking?.deposit_amount,
    deposit_discount_mode: tenant?.booking?.deposit_discount_mode,
    deposit_discount_value: tenant?.booking?.deposit_discount_value,
    rooms: tenant?.room
      ? { price: tenant.room.price, deposit_amount: tenant.room.deposit_amount }
      : null,
  });
  const depositState = resolveDepositState({
    depositPayments: depositPaymentsThisBooking,
    isEffectivelyPaid: isPaymentCountedAsPaidForMetrics,
    expectedDepositEUR: expectedDeposit,
  });
  const expectedDepositCents = depositState.expectedCents;
  const totalDepositPaidCents = depositState.paidCents;
  const depositFullyPaid = depositState.status === "paid";
  const paidDeposit = depositFullyPaid
    ? (depositPaidConfirmed[0] ?? null)
    : null;
  const paidRents = rentPayments.filter(
    (p: any) => forThisBooking(p) && isPaymentCountedAsPaidForMetrics(p)
  );
  const lastPaidRent = (() => {
    const list = [...paidRents].filter((p: any) => !!p?.due_date);
    if (list.length === 0) return null;
    list.sort(
      (a: any, b: any) =>
        new Date(b.due_date).getTime() - new Date(a.due_date).getTime()
    );
    return list[0] ?? null;
  })();
  const fullContractRent =
    tenant?.monthlyRent != null
      ? Number(tenant.monthlyRent)
      : tenant?.room?.price != null
        ? Number(tenant.room.price)
        : 0;
  const currentMonthIso = new Date().toISOString().slice(0, 7);
  const currentMonthRentPaymentForBooking = rentPayments.find((p: any) => {
    if (!forThisBooking(p) || !p?.due_date) return false;
    return String(p.due_date).slice(0, 7) === currentMonthIso;
  });
  const expectedRent = resolveExpectedMonthlyRentForDisplay(
    tenant,
    currentMonthRentPaymentForBooking
  );

  // Contract length in months (start to end inclusive)
  const contractMonths = (() => {
    const start = tenant?.startDate ? new Date(tenant.startDate) : null;
    const end = tenant?.endDate ? new Date(tenant.endDate) : null;
    if (!start || !end || end < start) return null;
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) +
      1;
    return Math.max(1, months);
  })();
  const paidMonths = paidRents.length;

  // Next payment: pick the earliest unpaid rent month (most reliable).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Months (YYYY-MM of due_date) that already have a confirmed paid rent row.
  // Prevents a duplicate pending row for the same month from being flagged as next due
  // when its sibling Stripe/manual row for that same due-month is already paid (e.g. paid early).
  const paidRentMonthKeys = new Set<string>(
    paidRents
      .map((p: any) => (p?.due_date ? String(p.due_date).slice(0, 7) : ""))
      .filter(Boolean)
  );
  const unpaidRent = rentPayments.filter((p: any) => {
    if (!forThisBooking(p) || !p.due_date) return false;
    if (paidRentMonthKeys.has(String(p.due_date).slice(0, 7))) return false;
    const proof = proofsByPaymentId[String(p.id)];
    const eff = getEffectivePaymentStatus(p, proof);
    // COL-417: via the shared list, so a part-paid month still counts as owed.
    return isOpenRentStatus(eff);
  });
  const nextPayment = (() => {
    if (!unpaidRent.length) return null;
    const sorted = [...unpaidRent].sort(
      (a: any, b: any) =>
        parseDateOnly(a.due_date).getTime() -
        parseDateOnly(b.due_date).getTime()
    );
    // Prefer due today/future; else earliest overdue.
    const futureOrToday = sorted.find(
      (p: any) => parseDateOnly(p.due_date).getTime() >= today.getTime()
    );
    return futureOrToday || sorted[0] || null;
  })();
  // When no payment record: rent starts on move-in date — use that as next payment date
  const moveIn = tenant?.startDate ? new Date(tenant.startDate) : null;
  if (moveIn) moveIn.setHours(0, 0, 0, 0);
  const nextAfterLastPaid = lastPaidRent?.due_date
    ? parseDateOnly(lastPaidRent.due_date)
    : null;
  if (nextAfterLastPaid)
    nextAfterLastPaid.setMonth(nextAfterLastPaid.getMonth() + 1);
  const nextPaymentDueDate = nextPayment?.due_date
    ? parseDateOnly(nextPayment.due_date)
    : expectedRent
      ? nextAfterLastPaid ||
        (moveIn && moveIn >= today
          ? moveIn
          : new Date(today.getFullYear(), today.getMonth() + 1, 1))
      : null;
  const nextPaymentDueLabel = nextPaymentDueDate
    ? `Due on ${nextPaymentDueDate.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`
    : null;
  const nextPaymentPeriodLabel = nextPaymentDueDate
    ? `${nextPaymentDueDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" })} Rent`
    : null;

  // Room details for rent card
  const roomTitle = tenant?.room?.title
    ? tenant.room.title.toLowerCase().startsWith("room")
      ? tenant.room.title
      : `Room ${tenant.room.title}`
    : null;
  const propertyTitle = tenant?.property?.title ?? null;
  const roomDetails =
    [propertyTitle, roomTitle].filter(Boolean).join(" · ") || "—";

  const monthSlots = useMemo(() => {
    const startIso = tenant?.moveInDate || tenant?.startDate;
    const endIso = tenant?.moveOutDate || tenant?.endDate;
    if (!startIso) return [] as { y: number; m: number; label: string }[];
    const start = new Date(startIso);
    if (Number.isNaN(start.getTime())) return [];
    const end = endIso
      ? new Date(endIso)
      : (() => {
          const d = new Date(start);
          d.setMonth(d.getMonth() + 11);
          return d;
        })();
    if (Number.isNaN(end.getTime()) || end < start) return [];
    const slots: { y: number; m: number; label: string }[] = [];
    let y = start.getFullYear();
    let mo = start.getMonth();
    const endY = end.getFullYear();
    const endM = end.getMonth();
    while (y < endY || (y === endY && mo <= endM)) {
      slots.push({
        y,
        m: mo,
        label: new Date(y, mo, 1).toLocaleDateString("en-GB", {
          month: "long",
          year: "numeric",
        }),
      });
      mo++;
      if (mo > 11) {
        mo = 0;
        y++;
      }
    }
    return slots;
  }, [
    tenant?.moveInDate,
    tenant?.startDate,
    tenant?.moveOutDate,
    tenant?.endDate,
  ]);

  const depositPaymentForBooking = useMemo(() => {
    const list = (payments || []).filter(
      (p: any) =>
        p.type === "deposit" &&
        (tenant?.id == null || Number(p.booking_id) === Number(tenant.id))
    );
    if (!list.length) return null;
    return [...list].sort(
      (a: any, b: any) =>
        new Date(b.created_at || b.due_date || 0).getTime() -
        new Date(a.created_at || a.due_date || 0).getTime()
    )[0];
  }, [payments, tenant?.id]);

  /**
   * Single deposit status badge used by BOTH the summary card and the deposit table,
   * so they can never disagree. paid/partial come straight from the shared resolver;
   * for a still-owed deposit we surface the underlying row status (overdue/scheduled)
   * when it's more specific than a plain "Due".
   */
  const renderDepositStatusBadge = () => {
    if (depositState.status === "paid") return getPaymentStatusBadge("paid");
    if (depositState.status === "partial") {
      return (
        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
          Partial
        </span>
      );
    }
    const rowStatus = depositPaymentForBooking
      ? getEffectivePaymentStatus(
          depositPaymentForBooking,
          proofsByPaymentId[String(depositPaymentForBooking.id)]
        )
      : null;
    if (rowStatus === "overdue" || rowStatus === "scheduled")
      return getPaymentStatusBadge(rowStatus);
    return (
      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
        Due
      </span>
    );
  };

  const fullMonthlyRentForBooking = getFullMonthlyRentFromBooking({
    monthly_rent: tenant?.monthlyRent,
    rooms: tenant?.room,
  } as any);

  const moveInIsoForBooking = (tenant?.moveInDate ||
    tenant?.startDate ||
    null) as string | null;
  const isSameYearMonth = (
    aIso: string | null | undefined,
    y: number,
    m: number
  ) => {
    const a = (aIso || "").trim().slice(0, 7);
    const b = `${y}-${String(m + 1).padStart(2, "0")}`;
    return !!a && a === b;
  };
  const isEditingFirstMonth = !!(
    rentPaymentEditTarget &&
    isSameYearMonth(
      moveInIsoForBooking,
      rentPaymentEditTarget.y,
      rentPaymentEditTarget.m
    )
  );

  const getAmountCentsForMonth = (y: number, m: number) =>
    expectedRentCentsForMonth({
      fullMonthlyRent: fullMonthlyRentForBooking,
      moveInIso: tenant?.moveInDate || tenant?.startDate,
      firstMonthOverride: tenant?.firstMonthRentOverride ?? null,
      dueYear: y,
      dueMonthIndex0: m,
      discount: tenant?.booking,
    });

  const findRentForMonth = (y: number, m: number) =>
    rentPayments.find((p: any) => {
      if (!forThisBooking(p) || !p.due_date) return false;
      const d = String(p.due_date).slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false;
      const py = parseInt(d.slice(0, 4), 10);
      const pm = parseInt(d.slice(5, 7), 10) - 1;
      return py === y && pm === m;
    });

  // A proof opens through a signed URL minted on click (COL-436): the bucket
  // is going private, so there is no public link to hand the button.
  const openProof = async (proof: { file_path?: string | null } | null) => {
    if (!(await openProofInNewTab(proof?.file_path))) {
      toast.error(t("admin.docs.openFailed", "Could not open the document."));
    }
  };

  const formatDateTimeShort = (iso: string | null | undefined) =>
    iso
      ? new Date(iso).toLocaleString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  // Authoritative DB lookup for an existing rent row in a calendar month
  // (in-memory `rentPayments` can be stale), so we never create a second one.
  const fetchRentRowForMonth = async (y: number, m: number) => {
    const monthStart = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const nextStart =
      m === 11 ? `${y + 1}-01-01` : `${y}-${String(m + 2).padStart(2, "0")}-01`;
    const { data } = await supabase
      .from("payments")
      .select("*")
      .eq("booking_id", tenant!.id)
      .eq("type", "rent")
      .gte("due_date", monthStart)
      .lt("due_date", nextStart)
      .not("status", "in", "(canceled,failed)")
      .order("status", { ascending: false }) // 'paid' before 'pending'
      .limit(1);
    return (data && data[0]) || null;
  };

  const ensureRentPaymentForMonth = async (
    y: number,
    m: number,
    label: string
  ) => {
    const existing =
      findRentForMonth(y, m) || (await fetchRentRowForMonth(y, m));
    if (existing) return existing;
    const due_date = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const amount_cents = getAmountCentsForMonth(y, m);
    const { data, error } = await supabase
      .from("payments")
      .insert(
        operatorStamped<"payments">({
          user_id: tenant!.userId,
          booking_id: tenant!.id,
          type: "rent",
          amount_cents,
          currency: "eur",
          status: "pending",
          due_date,
          description: `Rent — ${label}`,
        })
      )
      .select()
      .single();
    if (error) {
      // Unique index (one rent row per booking per month) tripped by a race —
      // return the row that won instead of surfacing an error.
      if ((error as any)?.code === "23505") {
        const raced = await fetchRentRowForMonth(y, m);
        if (raced) {
          await queryClient.invalidateQueries({
            queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
          });
          return raced;
        }
      }
      throw error;
    }
    await queryClient.invalidateQueries({
      queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
    });
    return data;
  };

  const updateRentPaymentMutation = useMutation({
    mutationFn: async (args: {
      paymentId: string;
      amountCents: number;
      dueDate: string;
    }) => {
      const { paymentId, amountCents, dueDate } = args;
      const { error } = await supabase
        .from("payments")
        .update({
          amount_cents: amountCents,
          due_date: dueDate,
        })
        .eq("id", paymentId);
      if (error) throw error;
      await queryClient.invalidateQueries({
        queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["booking-rent-payments-pricing"],
      });
      await queryClient.invalidateQueries({ queryKey: ["booking-for-room"] });
      await queryClient.invalidateQueries({
        queryKey: ["user-properties-display-rent"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["admin-bookings-rent-payments"],
      });
      await queryClient.invalidateQueries({
        queryKey: [PROPERTY_RENT_PAID_STATS_KEY],
      });
      // Can flip a company_collects group's payout readiness (COL-461).
      invalidateOwnerCaches(queryClient);
      return true;
    },
  });

  const updateFirstMonthOverrideMutation = useMutation({
    mutationFn: async (args: {
      bookingId: string | number;
      firstMonthOverride: number | null;
    }) => {
      const { bookingId, firstMonthOverride } = args;
      const { error } = await supabase
        .from("bookings")
        .update({ first_month_rent_override: firstMonthOverride })
        .eq("id", Number(bookingId));
      if (error) throw error;
      await queryClient.invalidateQueries({
        queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
      });
      // The override feeds the property card's "expected" rent for this
      // month (getExpectedRentCentsForDueCalendarMonth), so its % pill must
      // not keep showing the pre-override figure.
      await queryClient.invalidateQueries({
        queryKey: [PROPERTY_RENT_PAID_STATS_KEY],
      });
      // Also feeds the owners-list payout-readiness tile (COL-461).
      invalidateOwnerCaches(queryClient);
      return true;
    },
  });

  // Fetch the single existing deposit row for this booking straight from the DB
  // (authoritative, not React state) so we never create a second one.
  const fetchExistingDepositRow = async () => {
    const { data } = await supabase
      .from("payments")
      .select("*")
      .eq("booking_id", tenant!.id)
      .eq("type", "deposit")
      .order("status", { ascending: false }) // 'paid' before 'pending'
      .order("created_at", { ascending: true })
      .limit(1);
    return (data && data[0]) || null;
  };

  const ensureDepositPayment = async () => {
    if (depositPaymentForBooking) return depositPaymentForBooking;
    // Re-check the DB (state may be stale) before inserting.
    const existing = await fetchExistingDepositRow();
    if (existing) return existing;
    const moveInIso = (tenant?.moveInDate ||
      tenant?.startDate ||
      new Date().toISOString().slice(0, 10)) as string;
    const due = String(moveInIso).slice(0, 10);
    const { data, error } = await supabase
      .from("payments")
      .insert(
        operatorStamped<"payments">({
          user_id: tenant!.userId,
          booking_id: tenant!.id,
          type: "deposit",
          amount_cents: expectedDepositCents,
          currency: "eur",
          status: "pending",
          due_date: due,
          description: "Security deposit",
        })
      )
      .select()
      .single();
    if (error) {
      // Unique index (one deposit per booking) tripped by a concurrent create —
      // fall back to the row that won the race instead of surfacing an error.
      if ((error as any)?.code === "23505") {
        const raced = await fetchExistingDepositRow();
        if (raced) {
          await queryClient.invalidateQueries({
            queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
          });
          return raced;
        }
      }
      throw error;
    }
    await queryClient.invalidateQueries({
      queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
    });
    return data;
  };

  const uploadProofForPayment = async (paymentId: string) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");
    const file = proofFileRef.current?.files?.[0];
    if (!file) throw new Error("Choose a file");
    const maxSize = 10 * 1024 * 1024;
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];
    if (file.size > maxSize) throw new Error("File must be 10 MB or less.");
    if (!validTypes.includes(file.type))
      throw new Error("File must be PDF or image (JPEG, PNG, WebP).");

    setProofUploading(true);
    setProofProgress(10);
    const ext = file.name.split(".").pop() || "pdf";
    const safeName = file.name.replace(/\s+/g, "-");
    const storagePath = `payment-proofs/${paymentId}/${Date.now()}-admin-${safeName}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from("contracts")
      .upload(storagePath, file, {
        contentType: file.type || "application/octet-stream",
        upsert: false,
      });
    if (uploadError) throw uploadError;
    setProofProgress(70);
    const { error: insertError } = await supabase
      .from("payment_proofs")
      .insert({
        payment_id: paymentId,
        file_path: storagePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        status: "approved",
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
        admin_comment: "Uploaded by admin",
      });
    if (insertError) {
      await supabase.storage.from("contracts").remove([storagePath]);
      throw insertError;
    }
    setProofProgress(100);
    await queryClient.invalidateQueries({
      queryKey: ["tenant-activity-payment-proofs"],
      exact: false,
    });
    await queryClient.invalidateQueries({
      queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
    });
    toast.success("Payment proof uploaded.");
    setProofDialogOpen(false);
    setProofTarget(null);
    setProofProgress(0);
    setProofUploading(false);
    if (proofFileRef.current) proofFileRef.current.value = "";
  };

  const removeProofMutation = useMutation({
    mutationFn: async (proof: {
      id: string;
      file_path?: string | null;
      payment_id?: string;
    }) => {
      const paymentId = proof.payment_id as string | undefined;
      let isStripe = false;
      if (paymentId) {
        const { data: payRow } = await supabase
          .from("payments")
          .select("stripe_payment_intent_id, stripe_charge_id")
          .eq("id", paymentId)
          .maybeSingle();
        isStripe = !!(
          payRow?.stripe_payment_intent_id || payRow?.stripe_charge_id
        );
      }
      if (proof.file_path) {
        const { error: storageErr } = await supabase.storage
          .from("contracts")
          .remove([proof.file_path]);
        if (storageErr)
          console.warn("Storage remove payment proof:", storageErr);
      }
      const { error } = await supabase
        .from("payment_proofs")
        .delete()
        .eq("id", proof.id);
      if (error) throw error;
      if (paymentId && !isStripe) {
        const { error: updErr } = await supabase
          .from("payments")
          .update({
            status: "pending",
            paid_at: null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", paymentId);
        if (updErr) throw updErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["tenant-activity-payment-proofs"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["tenant-payments", tenant?.userId, tenant?.id],
      });
      toast.success("Proof removed");
      setProofToDelete(null);
    },
    onError: (e: any) => toast.error(e?.message || "Failed to remove proof"),
  });

  if (!tenant?.userId && !tenant?.id) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CreditCard className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            No payment data
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            No booking associated with this tenant.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Security deposit card */}
        <Card className="overflow-hidden border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/50 dark:to-slate-800/30 dark:border-slate-700/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200">
                Security deposit
              </CardTitle>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700/50">
                <CreditCard className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {formatCurrency(depositState.displayCents)}
            </div>
            <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400">
              <p>
                <span className="font-medium text-slate-500 dark:text-slate-500">
                  Expected:{" "}
                </span>
                {formatCurrency(depositState.expectedCents)}
              </p>
              {totalDepositPaidCents > 0 ? (
                <>
                  <p>
                    <span className="font-medium text-slate-500 dark:text-slate-500">
                      Amount paid:{" "}
                    </span>
                    {formatCurrency(totalDepositPaidCents)}
                  </p>
                  {depositPaidConfirmed[0]?.paid_at && (
                    <p>
                      <span className="font-medium text-slate-500 dark:text-slate-500">
                        Paid on:{" "}
                      </span>
                      {formatDate(depositPaidConfirmed[0].paid_at)}
                    </p>
                  )}
                </>
              ) : paidDeposit ? (
                <>
                  <p>
                    <span className="font-medium text-slate-500 dark:text-slate-500">
                      Amount paid:{" "}
                    </span>
                    {formatCurrency(paidDeposit.amount_cents)}
                  </p>
                  {paidDeposit.paid_at && (
                    <p>
                      <span className="font-medium text-slate-500 dark:text-slate-500">
                        Paid on:{" "}
                      </span>
                      {formatDate(paidDeposit.paid_at)}
                    </p>
                  )}
                </>
              ) : depositPayments.length > 0 ? (
                <p>
                  <span className="font-medium text-slate-500 dark:text-slate-500">
                    Due:{" "}
                  </span>
                  {formatDate(depositPayments[0].due_date)}
                </p>
              ) : (
                <p className="text-muted-foreground">
                  No deposit payment recorded
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              {renderDepositStatusBadge()}
            </div>
          </CardContent>
        </Card>

        {/* Rent card: amount, monthly rent, room details, paid X/Y months */}
        <Card className="overflow-hidden border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/50 dark:to-slate-800/30 dark:border-slate-700/50 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200">
                Rent
              </CardTitle>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100/80 dark:bg-slate-700/50 ring-1 ring-slate-200/50 dark:ring-slate-600/30">
                <Receipt className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-2xl font-semibold tabular-nums text-slate-900 dark:text-slate-100 tracking-tight">
              {expectedRent ? formatCurrency(expectedRent * 100) : "—"}
            </div>
            <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">
              Monthly rent
            </p>
            {tenant?.isPartialFirstMonthRent &&
              fullContractRent > 0 &&
              tenant.displayedRent != null &&
              Math.round(Number(tenant.displayedRent) * 100) <
                Math.round(fullContractRent * 100) && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  First-month invoice (pro-rata / scheduled):{" "}
                  {formatCurrency(
                    Math.round(Number(tenant.displayedRent) * 100)
                  )}
                </p>
              )}

            {/* Room details */}
            <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/50">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mb-1.5">
                Room
              </p>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {roomDetails}
              </p>
            </div>

            {/* Paid installments: progress + fraction */}
            <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/50">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mb-2">
                Months Of Rent Paid
              </p>
              {contractMonths != null ? (
                <div className="space-y-2.5">
                  <Progress
                    value={
                      contractMonths > 0
                        ? Math.round((paidMonths / contractMonths) * 100)
                        : 0
                    }
                    className="h-2 bg-slate-200/80 dark:bg-slate-700/50 [&>div]:bg-emerald-500 dark:[&>div]:bg-emerald-500"
                  />
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold tabular-nums text-sm">
                      {paidMonths}
                    </span>
                    <span className="text-slate-400 dark:text-slate-500 font-medium text-sm">
                      /
                    </span>
                    <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-200 text-sm">
                      {contractMonths}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                      months
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline gap-1.5">
                  <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300 font-semibold tabular-nums text-sm">
                    {paidMonths}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                    paid
                  </span>
                </div>
              )}
            </div>

            {/* Next payment date — e.g. February 2026 Rent, Due on 2 February 2026 */}
            {(nextPaymentPeriodLabel || nextPaymentDueLabel) && (
              <div className="pt-2 border-t border-slate-200/60 dark:border-slate-700/50">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium mb-1.5">
                  Next payment
                </p>
                {nextPaymentPeriodLabel && (
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {nextPaymentPeriodLabel}
                  </p>
                )}
                {nextPaymentDueLabel && (
                  <p className="flex items-center gap-1.5 mt-1 text-sm text-slate-600 dark:text-slate-400">
                    <Calendar className="w-4 h-4 shrink-0" />
                    {nextPaymentDueLabel}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment history — all rent & deposit rows (Stripe vs Manual; proof actions live under Rent/Deposit tables) */}
      <Card className="overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-b from-white to-slate-50/80 shadow-md shadow-slate-900/[0.04] dark:border-slate-700/60 dark:from-slate-900/50 dark:to-slate-950/40 dark:shadow-none">
        <CardHeader className="border-b border-slate-100/90 bg-slate-50/50 pb-4 dark:border-slate-800 dark:bg-slate-900/30">
          <CardTitle className="text-lg font-semibold tracking-tight">
            Payment history
          </CardTitle>
          <CardDescription className="text-sm mt-1">
            Rental and deposit transactions for this tenancy
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {!payments?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Receipt className="mb-3 h-10 w-10 text-muted-foreground/50" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                No payments yet
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Payment records will appear here once created
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200/80 bg-muted/30 hover:bg-muted/30 dark:border-slate-700/50">
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Type
                    </TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Amount
                    </TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Due
                    </TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Paid
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Payment method
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(payments as any[]).map((p: any) => {
                    const proof = proofsByPaymentId[String(p.id)];
                    const method = getPaymentHistoryMethodLabel(p, proof);
                    return (
                      <TableRow
                        key={p.id}
                        className="border-b border-slate-100/90 transition-colors last:border-0 hover:bg-slate-50/90 dark:border-slate-800/80 dark:hover:bg-slate-800/30"
                      >
                        <TableCell className="font-medium capitalize">
                          {p.type === "rent" ? "Rent" : "Security deposit"}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {formatCurrency(p.amount_cents)}
                        </TableCell>
                        <TableCell>
                          {getPaymentStatusBadge(
                            getEffectivePaymentStatus(p, proof)
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(p.due_date)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {getEffectivePaymentStatus(p, proof) === "paid" &&
                          p.paid_at
                            ? formatDate(p.paid_at)
                            : "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {method == null ? (
                            <span className="text-xs">—</span>
                          ) : (
                            <span
                              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                                method === "Stripe"
                                  ? "border-violet-200 bg-violet-500/10 text-violet-800 dark:border-violet-800 dark:text-violet-300"
                                  : "border-slate-200 bg-slate-500/10 text-slate-700 dark:border-slate-600 dark:text-slate-300"
                              }`}
                            >
                              {method}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rent payments — one row per month from move-in; upload proof per month */}
      <Card className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <CardHeader className="space-y-1 border-b border-slate-100 pb-4 dark:border-slate-800">
          <CardTitle className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50">
            Rent payments
          </CardTitle>
          <CardDescription className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            <span className="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
              <Calendar
                className="h-3.5 w-3.5 shrink-0 text-slate-400"
                aria-hidden
              />
              <span className="font-medium">
                Move-in {formatDate(tenant.moveInDate || tenant.startDate)}
              </span>
            </span>
            <span className="text-slate-300 dark:text-slate-600" aria-hidden>
              ·
            </span>
            <span>
              One row per calendar month. Bank transfers require a proof file.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {monthSlots.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
              <Receipt className="mb-3 h-10 w-10 text-muted-foreground/50" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                No month list yet
              </p>
              <p className="mt-1 max-w-sm text-xs text-muted-foreground">
                Set move-in and move-out on the booking to generate one row per
                month (starting from the move-in month).
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200/80 bg-muted/30 hover:bg-muted/30 dark:border-slate-700/50">
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Month
                    </TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Amount
                    </TableHead>
                    <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Status
                    </TableHead>
                    <TableHead className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Payment method
                    </TableHead>
                    <TableHead className="min-w-[280px] w-[32%] text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(showAllRentMonths
                    ? monthSlots
                    : monthSlots.slice(0, 5)
                  ).map((slot) => {
                    const rent = findRentForMonth(slot.y, slot.m);
                    const amountCents =
                      rent?.amount_cents ??
                      getAmountCentsForMonth(slot.y, slot.m);
                    const pid = rent?.id ? String(rent.id) : "";
                    const proof = pid ? proofsByPaymentId[pid] : null;
                    const hasProofFile = !!proof?.file_path;
                    const rowKey = `${slot.y}-${slot.m}`;
                    const notesOpen = notesOpenKey === rowKey;
                    return (
                      <Fragment key={rowKey}>
                        <TableRow className="border-b border-slate-100/90 transition-colors odd:bg-white even:bg-slate-50/50 last:border-0 hover:bg-slate-100/60 dark:odd:bg-slate-950 dark:even:bg-slate-900/25 dark:border-slate-800/80 dark:hover:bg-slate-800/40">
                          <TableCell className="font-semibold text-foreground">
                            {slot.label}
                          </TableCell>
                          <TableCell className="tabular-nums text-muted-foreground">
                            {formatCurrency(amountCents)}
                          </TableCell>
                          <TableCell>
                            {rent
                              ? getPaymentStatusBadge(
                                  getEffectivePaymentStatus(rent, proof)
                                )
                              : getPaymentStatusBadge("pending")}
                          </TableCell>
                          <TableCell>
                            {renderPaymentMethodCell(rent, proof)}
                          </TableCell>
                          <TableCell className="align-middle py-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 min-w-[4.25rem] gap-1 rounded-md border-slate-200 px-2.5 text-xs font-medium"
                                disabled={ensuringRentKey === rowKey}
                                onClick={async () => {
                                  try {
                                    setEnsuringRentKey(rowKey);
                                    let p = rent;
                                    if (!p)
                                      p = await ensureRentPaymentForMonth(
                                        slot.y,
                                        slot.m,
                                        slot.label
                                      );
                                    const computed =
                                      getAmountCentsForMonth(slot.y, slot.m) /
                                      100;
                                    setRentPaymentEditTarget({
                                      y: slot.y,
                                      m: slot.m,
                                      label: slot.label,
                                      paymentId: String(p.id),
                                    });
                                    setRentPaymentEditComputedAmount(computed);
                                    setRentPaymentEditAmount(
                                      String(
                                        (Number(p.amount_cents) ||
                                          amountCents) / 100
                                      )
                                    );
                                    setRentPaymentEditDueDate(
                                      String(
                                        p.due_date ||
                                          `${slot.y}-${String(slot.m + 1).padStart(2, "0")}-01`
                                      ).slice(0, 10)
                                    );
                                    const existingOverride =
                                      tenant?.firstMonthRentOverride != null &&
                                      String(
                                        tenant.firstMonthRentOverride
                                      ).trim() !== ""
                                        ? String(tenant.firstMonthRentOverride)
                                        : "";
                                    setRentPaymentEditFirstMonthOverride(
                                      existingOverride
                                    );
                                    setRentPaymentEditFirstMonthAuto(
                                      existingOverride.trim() === ""
                                    );
                                    setRentPaymentEditOpen(true);
                                  } catch (e: any) {
                                    toast.error(
                                      e?.message || "Could not open edit"
                                    );
                                  } finally {
                                    setEnsuringRentKey(null);
                                  }
                                }}
                              >
                                {ensuringRentKey === rowKey ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Edit className="h-3.5 w-3.5" />
                                )}
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1 rounded-md border-slate-200 px-2.5 text-xs font-medium"
                                disabled={ensuringRentKey === rowKey}
                                onClick={async () => {
                                  try {
                                    setEnsuringRentKey(rowKey);
                                    let p = rent;
                                    if (!p)
                                      p = await ensureRentPaymentForMonth(
                                        slot.y,
                                        slot.m,
                                        slot.label
                                      );
                                    setProofTarget({ id: p.id, type: "rent" });
                                    setProofDialogOpen(true);
                                    if (proofFileRef.current)
                                      proofFileRef.current.value = "";
                                  } catch (e: any) {
                                    toast.error(
                                      e?.message || "Could not open upload"
                                    );
                                  } finally {
                                    setEnsuringRentKey(null);
                                  }
                                }}
                              >
                                {ensuringRentKey === rowKey ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Upload className="h-3.5 w-3.5" />
                                )}
                                {proof ? "Replace" : "Upload"}
                              </Button>
                              {proof ? (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-8 gap-1 rounded-md border-slate-200 px-2.5 text-xs font-medium"
                                    >
                                      <Paperclip className="h-3.5 w-3.5" />
                                      Proof
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className="w-80 p-0"
                                    align="end"
                                  >
                                    <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
                                      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                        Payment proof
                                      </p>
                                    </div>
                                    <div className="space-y-3 p-3">
                                      <div className="flex gap-2">
                                        <File className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                        <div className="min-w-0 flex-1">
                                          <p
                                            className="break-words text-sm font-medium leading-snug"
                                            title={proof.file_name}
                                          >
                                            {proof.file_name}
                                          </p>
                                          {proof?.created_at ? (
                                            <p className="mt-1 text-[11px] text-muted-foreground">
                                              Uploaded{" "}
                                              {formatDateTimeShort(
                                                proof.created_at
                                              )}
                                            </p>
                                          ) : null}
                                        </div>
                                      </div>
                                      <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
                                        <Button
                                          type="button"
                                          variant="default"
                                          size="sm"
                                          className="h-8 flex-1 gap-1.5 sm:flex-none"
                                          disabled={!hasProofFile}
                                          onClick={() => void openProof(proof)}
                                        >
                                          <Eye className="h-3.5 w-3.5" />
                                          View
                                        </Button>
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className="h-8 flex-1 gap-1.5 border-red-200 text-red-700 hover:bg-red-50 sm:flex-none dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30"
                                          onClick={() =>
                                            setProofToDelete(proof)
                                          }
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                          Delete
                                        </Button>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              ) : null}
                              <Button
                                type="button"
                                variant={notesOpen ? "secondary" : "outline"}
                                size="sm"
                                className="h-8 gap-1 rounded-md border-slate-200 px-2.5 text-xs font-medium"
                                disabled={ensuringNotesKey === rowKey}
                                onClick={async () => {
                                  if (notesOpen) {
                                    setNotesOpenKey(null);
                                    setNotesPaymentId(null);
                                    return;
                                  }
                                  try {
                                    setEnsuringNotesKey(rowKey);
                                    let p = rent;
                                    if (!p)
                                      p = await ensureRentPaymentForMonth(
                                        slot.y,
                                        slot.m,
                                        slot.label
                                      );
                                    setNotesPaymentId(String(p.id));
                                    setNotesOpenKey(rowKey);
                                  } catch (e: any) {
                                    toast.error(
                                      e?.message || "Could not open notes"
                                    );
                                  } finally {
                                    setEnsuringNotesKey(null);
                                  }
                                }}
                              >
                                {ensuringNotesKey === rowKey ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <StickyNote className="h-3.5 w-3.5" />
                                )}
                                Notes
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {notesOpen && notesPaymentId ? (
                          <TableRow className="border-b border-slate-100/90 bg-slate-50/60 hover:bg-slate-50/60 dark:border-slate-800/80 dark:bg-slate-900/30 dark:hover:bg-slate-900/30">
                            <TableCell colSpan={5} className="p-3">
                              <AdminNotesSection
                                paymentId={notesPaymentId}
                                title={`Notes — ${slot.label}`}
                                compact
                              />
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              {monthSlots.length > 5 ? (
                <div className="flex items-center justify-center border-t border-slate-200/70 bg-white/60 px-4 py-3 text-sm dark:border-slate-800/70 dark:bg-slate-950/30">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => setShowAllRentMonths((v) => !v)}
                  >
                    {showAllRentMonths
                      ? "View less"
                      : `See more (${monthSlots.length - 5} more)`}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deposit — single security deposit row with proof */}
      <Card className="overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-b from-white to-slate-50/80 shadow-md shadow-slate-900/[0.04] dark:border-slate-700/60 dark:from-slate-900/50 dark:to-slate-950/40 dark:shadow-none">
        <CardHeader className="border-b border-slate-100/90 bg-slate-50/50 pb-4 dark:border-slate-800 dark:bg-slate-900/30">
          <CardTitle className="text-lg font-semibold tracking-tight">
            Deposit payments
          </CardTitle>
          <CardDescription className="text-sm mt-1">
            Security deposit for this tenancy — upload bank transfer proof.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200/80 bg-muted/30 hover:bg-muted/30 dark:border-slate-700/50">
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Type
                  </TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Amount
                  </TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Payment method
                  </TableHead>
                  <TableHead className="min-w-[260px] text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Payment proof
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="border-slate-100 transition-colors hover:bg-slate-50/90 dark:border-slate-800/80 dark:hover:bg-slate-800/30">
                  <TableCell className="font-semibold">
                    Security deposit
                  </TableCell>
                  <TableCell className="tabular-nums text-muted-foreground">
                    {formatCurrency(expectedDepositCents)}
                  </TableCell>
                  <TableCell>{renderDepositStatusBadge()}</TableCell>
                  <TableCell>
                    {depositPaymentForBooking ? (
                      renderPaymentMethodCell(
                        depositPaymentForBooking,
                        proofsByPaymentId[String(depositPaymentForBooking.id)]
                      )
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-2 py-1 sm:flex-row sm:flex-wrap sm:items-center">
                      {depositPaymentForBooking ? (
                        <>
                          {proofsByPaymentId[
                            String(depositPaymentForBooking.id)
                          ] ? (
                            <div className="inline-flex max-w-full flex-wrap items-center gap-2 rounded-xl border border-slate-200/90 bg-white px-2.5 py-2 text-xs shadow-sm dark:border-slate-600/50 dark:bg-slate-900/60">
                              <File className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <span
                                className="truncate"
                                title={
                                  proofsByPaymentId[
                                    String(depositPaymentForBooking.id)
                                  ].file_name
                                }
                              >
                                {
                                  proofsByPaymentId[
                                    String(depositPaymentForBooking.id)
                                  ].file_name
                                }
                              </span>
                              <Button
                                type="button"
                                variant="default"
                                size="sm"
                                className="h-7 gap-1 px-2"
                                title="View uploaded document"
                                disabled={
                                  !proofsByPaymentId[
                                    String(depositPaymentForBooking.id)
                                  ]?.file_path
                                }
                                onClick={() =>
                                  void openProof(
                                    proofsByPaymentId[
                                      String(depositPaymentForBooking.id)
                                    ]
                                  )
                                }
                              >
                                <Eye className="h-3 w-3" />
                                View
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 gap-1 px-2 text-destructive hover:bg-destructive/10"
                                onClick={() =>
                                  setProofToDelete(
                                    proofsByPaymentId[
                                      String(depositPaymentForBooking.id)
                                    ]
                                  )
                                }
                              >
                                <Trash2 className="h-3 w-3" />
                                Delete
                              </Button>
                            </div>
                          ) : null}
                          {proofsByPaymentId[
                            String(depositPaymentForBooking.id)
                          ]?.created_at ? (
                            <span className="text-[11px] text-muted-foreground">
                              Uploaded{" "}
                              {formatDateTimeShort(
                                proofsByPaymentId[
                                  String(depositPaymentForBooking.id)
                                ].created_at
                              )}
                            </span>
                          ) : null}
                        </>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-fit gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-900 dark:text-blue-400 dark:hover:bg-blue-950/40"
                        disabled={ensuringDeposit}
                        onClick={async () => {
                          try {
                            setEnsuringDeposit(true);
                            const p = await ensureDepositPayment();
                            setProofTarget({ id: p.id, type: "deposit" });
                            setProofDialogOpen(true);
                            if (proofFileRef.current)
                              proofFileRef.current.value = "";
                          } catch (e: any) {
                            toast.error(
                              e?.message || "Could not prepare deposit payment"
                            );
                          } finally {
                            setEnsuringDeposit(false);
                          }
                        }}
                      >
                        {ensuringDeposit ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Upload className="h-3.5 w-3.5" />
                        )}
                        {depositPaymentForBooking &&
                        proofsByPaymentId[String(depositPaymentForBooking.id)]
                          ? "Replace proof"
                          : "Upload proof"}
                      </Button>
                      <Button
                        type="button"
                        variant={
                          notesOpenKey === "deposit" ? "secondary" : "outline"
                        }
                        size="sm"
                        className="w-fit gap-1.5"
                        disabled={ensuringNotesKey === "deposit"}
                        onClick={async () => {
                          if (notesOpenKey === "deposit") {
                            setNotesOpenKey(null);
                            setNotesPaymentId(null);
                            return;
                          }
                          try {
                            setEnsuringNotesKey("deposit");
                            const p =
                              depositPaymentForBooking ??
                              (await ensureDepositPayment());
                            setNotesPaymentId(String(p.id));
                            setNotesOpenKey("deposit");
                          } catch (e: any) {
                            toast.error(e?.message || "Could not open notes");
                          } finally {
                            setEnsuringNotesKey(null);
                          }
                        }}
                      >
                        {ensuringNotesKey === "deposit" ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <StickyNote className="h-3.5 w-3.5" />
                        )}
                        Notes
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {notesOpenKey === "deposit" && notesPaymentId ? (
                  <TableRow className="border-slate-100 bg-slate-50/60 hover:bg-slate-50/60 dark:border-slate-800/80 dark:bg-slate-900/30 dark:hover:bg-slate-900/30">
                    <TableCell colSpan={5} className="p-3">
                      <AdminNotesSection
                        paymentId={notesPaymentId}
                        title="Notes — Security deposit"
                        compact
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={proofDialogOpen}
        onOpenChange={(open) => {
          setProofDialogOpen(open);
          if (!open) {
            setProofTarget(null);
            setProofProgress(0);
            setProofUploading(false);
            if (proofFileRef.current) proofFileRef.current.value = "";
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload payment proof</DialogTitle>
            <DialogDescription>
              PDF or image (max 10 MB). Proof is stored on this{" "}
              {proofTarget?.type === "deposit" ? "deposit" : "rent"} payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>File</Label>
              <input
                ref={proofFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="text-sm w-full"
              />
              <p className="text-xs text-muted-foreground">
                PDF or images (JPEG, PNG, WebP).
              </p>
            </div>
            {proofUploading ? <Progress value={proofProgress} /> : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setProofDialogOpen(false)}
              disabled={proofUploading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={proofUploading || !proofTarget}
              onClick={async () => {
                if (!proofTarget?.id) return;
                try {
                  await uploadProofForPayment(proofTarget.id);
                } catch (e: any) {
                  setProofUploading(false);
                  setProofProgress(0);
                  toast.error(
                    e?.message ||
                      t("admin.tenants.uploadFailedToast", "Upload failed")
                  );
                }
              }}
            >
              {proofUploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rentPaymentEditOpen}
        onOpenChange={(open) => {
          setRentPaymentEditOpen(open);
          if (!open) {
            setRentPaymentEditTarget(null);
            setRentPaymentEditAmount("");
            setRentPaymentEditDueDate("");
            setRentPaymentEditProrateMode("auto");
            setRentPaymentEditProrateCustomPct("");
            setRentPaymentEditFirstMonthOverride("");
            setRentPaymentEditFirstMonthAuto(true);
            setRentPaymentEditComputedAmount(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("admin.tenants.editRentTitle", "Edit rent")}
            </DialogTitle>
            <DialogDescription>
              Update this month's rent payment amount and due date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Amount (EUR)</Label>
              <Input
                value={rentPaymentEditAmount}
                onChange={(e) => setRentPaymentEditAmount(e.target.value)}
                inputMode="decimal"
                placeholder="600.00"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Due date</Label>
              <Input
                value={rentPaymentEditDueDate}
                onChange={(e) => setRentPaymentEditDueDate(e.target.value)}
                placeholder="YYYY-MM-DD"
              />
              <p className="text-xs text-muted-foreground">
                Format: YYYY-MM-DD
              </p>
            </div>
            <div className="space-y-2 pt-2 border-t border-border/60">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label>Pro-rate (optional)</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Quick-set this month's amount based on the full contract
                    rent.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  {[
                    { id: "reset", label: "Auto", pct: null },
                    { id: "25", label: "25%", pct: 0.25 },
                    { id: "50", label: "50%", pct: 0.5 },
                    { id: "75", label: "75%", pct: 0.75 },
                    { id: "100", label: "100%", pct: 1 },
                  ].map((opt) => (
                    <Button
                      key={opt.id}
                      type="button"
                      variant={opt.pct == null ? "outline" : "outline"}
                      size="sm"
                      className="h-8 rounded-full px-3"
                      onClick={() => {
                        if (opt.pct == null) {
                          setRentPaymentEditProrateMode("auto");
                          setRentPaymentEditProrateCustomPct("");
                          const fallback =
                            rentPaymentEditComputedAmount ??
                            Math.round(fullMonthlyRentForBooking * 100) / 100;
                          setRentPaymentEditAmount(String(fallback));
                          return;
                        }
                        setRentPaymentEditProrateMode("preset");
                        setRentPaymentEditProrateCustomPct("");
                        const suggested =
                          Math.round(
                            fullMonthlyRentForBooking * opt.pct * 100
                          ) / 100;
                        setRentPaymentEditAmount(String(suggested));
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant={
                      rentPaymentEditProrateMode === "custom"
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    className="h-8 rounded-full px-3"
                    onClick={() => {
                      setRentPaymentEditProrateMode("custom");
                      if (rentPaymentEditProrateCustomPct.trim() === "")
                        setRentPaymentEditProrateCustomPct("100");
                    }}
                  >
                    Custom
                  </Button>
                </div>
              </div>
              {rentPaymentEditProrateMode === "custom" ? (
                <div className="mt-3 flex items-center gap-2">
                  <Input
                    value={rentPaymentEditProrateCustomPct}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setRentPaymentEditProrateCustomPct(raw);
                      const pct = Number(String(raw).replace(",", "."));
                      if (!Number.isFinite(pct)) return;
                      const clamped = Math.max(0, Math.min(200, pct));
                      const suggested =
                        Math.round(
                          fullMonthlyRentForBooking * (clamped / 100) * 100
                        ) / 100;
                      setRentPaymentEditAmount(String(suggested));
                    }}
                    inputMode="decimal"
                    placeholder="100"
                    className="h-9 w-28"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <span className="text-xs text-muted-foreground">
                    of full rent
                  </span>
                </div>
              ) : null}
            </div>

            {isEditingFirstMonth ? (
              <div className="space-y-2 pt-2 border-t border-border/60">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label>First month override (optional)</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Also save an override on the booking (used for pro‑rata
                      calculations).
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      type="button"
                      variant={
                        rentPaymentEditFirstMonthAuto ? "default" : "outline"
                      }
                      size="sm"
                      className="h-8 rounded-full px-3"
                      onClick={() => {
                        setRentPaymentEditFirstMonthAuto(true);
                        setRentPaymentEditFirstMonthOverride("");
                      }}
                    >
                      Auto
                    </Button>
                    <Button
                      type="button"
                      variant={
                        !rentPaymentEditFirstMonthAuto ? "default" : "outline"
                      }
                      size="sm"
                      className="h-8 rounded-full px-3"
                      onClick={() => setRentPaymentEditFirstMonthAuto(false)}
                    >
                      Custom
                    </Button>
                  </div>
                </div>
                <Input
                  value={rentPaymentEditFirstMonthOverride}
                  onChange={(e) => {
                    setRentPaymentEditFirstMonthAuto(false);
                    setRentPaymentEditFirstMonthOverride(e.target.value);
                  }}
                  inputMode="decimal"
                  placeholder={String(
                    Math.round(fullMonthlyRentForBooking * 100) / 100
                  )}
                  disabled={rentPaymentEditFirstMonthAuto}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={
                updateRentPaymentMutation.isPending ||
                updateFirstMonthOverrideMutation.isPending
              }
              onClick={() => setRentPaymentEditOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                updateRentPaymentMutation.isPending ||
                updateFirstMonthOverrideMutation.isPending ||
                !rentPaymentEditTarget?.paymentId ||
                !rentPaymentEditAmount.trim() ||
                !rentPaymentEditDueDate.trim()
              }
              onClick={async () => {
                const target = rentPaymentEditTarget;
                if (!target?.paymentId) return;
                const parsed = Number(
                  String(rentPaymentEditAmount).replace(",", ".")
                );
                // Allow zero (e.g. waived month), but not negative / NaN.
                if (!Number.isFinite(parsed) || parsed < 0) {
                  toast.error("Enter a valid amount (0 or more).");
                  return;
                }
                const due = String(rentPaymentEditDueDate).trim().slice(0, 10);
                if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) {
                  toast.error("Enter a valid due date (YYYY-MM-DD).");
                  return;
                }
                try {
                  const moveYm = String(moveInIsoForBooking || "")
                    .trim()
                    .slice(0, 7);
                  const dueYm = due.slice(0, 7);
                  const inMoveInMonth = !!moveYm && dueYm === moveYm;
                  /** First calendar month: charge clearly below full monthly → pro‑rata / partial; do not push that onto `rooms.price`. */
                  const looksLikeProRataOnly =
                    inMoveInMonth &&
                    fullMonthlyRentForBooking > 0 &&
                    parsed < fullMonthlyRentForBooking - 0.01;
                  const prevRow = (payments || []).find(
                    (p: any) => String(p.id) === String(target.paymentId)
                  );
                  const prevCents =
                    prevRow?.amount_cents != null
                      ? Number(prevRow.amount_cents)
                      : null;
                  const newCents = Math.round(parsed * 100);
                  const amountChanged =
                    prevCents == null ||
                    !Number.isFinite(prevCents) ||
                    prevCents !== newCents;
                  let dbRoomPriceDrift = false;
                  let roomIdForDrift: number | null = null;
                  if (tenant?.id != null) {
                    roomIdForDrift = await resolveRoomIdForBookingTenant(
                      supabase,
                      tenant,
                      tenant.id
                    );
                    if (roomIdForDrift != null) {
                      const { data: roomRow } = await supabase
                        .from("rooms")
                        .select("price")
                        .eq("id", roomIdForDrift)
                        .maybeSingle();
                      const dbPrice =
                        roomRow?.price != null ? Number(roomRow.price) : NaN;
                      dbRoomPriceDrift =
                        Number.isFinite(dbPrice) &&
                        dbPrice > 0 &&
                        Math.abs(parsed - dbPrice) >= 0.02;
                    }
                  }
                  /**
                   * Keep public listing (`rooms.price`) and contract rent aligned with saved rent rows.
                   * Sync when the amount changed or DB listing price still disagrees with the saved rent
                   * (covers missing `tenant.room` in UI and "re-save same amount" repair).
                   */
                  const shouldSyncListingRent =
                    parsed > 0 &&
                    !looksLikeProRataOnly &&
                    tenant?.id != null &&
                    (amountChanged || dbRoomPriceDrift);

                  let firstMonthOverrideAfter: number | null =
                    tenant?.firstMonthRentOverride != null &&
                    String(tenant.firstMonthRentOverride).trim() !== ""
                      ? Number(tenant.firstMonthRentOverride)
                      : null;

                  await updateRentPaymentMutation.mutateAsync({
                    paymentId: target.paymentId,
                    amountCents: Math.round(parsed * 100),
                    dueDate: due,
                  });
                  if (isEditingFirstMonth && tenant?.id != null) {
                    let override: number | null = null;
                    if (
                      !rentPaymentEditFirstMonthAuto &&
                      rentPaymentEditFirstMonthOverride.trim() !== ""
                    ) {
                      const o = Number(
                        String(rentPaymentEditFirstMonthOverride).replace(
                          ",",
                          "."
                        )
                      );
                      if (!Number.isFinite(o) || o <= 0) {
                        toast.error(
                          "Enter a valid first month override amount, or choose Auto."
                        );
                        return;
                      }
                      override = Math.round(o * 100) / 100;
                    }
                    await updateFirstMonthOverrideMutation.mutateAsync({
                      bookingId: tenant.id,
                      firstMonthOverride: override,
                    });
                    firstMonthOverrideAfter = override;
                  }
                  if (shouldSyncListingRent) {
                    // Deposit is contractually fixed at signing — a rent edit
                    // must not rewrite bookings.deposit_amount.
                    const { error: bookErr } = await supabase
                      .from("bookings")
                      .update({
                        monthly_rent: parsed,
                      })
                      .eq("id", tenant.id);
                    if (bookErr) throw bookErr;
                    let roomId = roomIdForDrift;
                    if (roomId == null) {
                      roomId = await resolveRoomIdForBookingTenant(
                        supabase,
                        tenant,
                        tenant.id
                      );
                    }
                    if (roomId != null) {
                      const { error: roomErr } = await supabase
                        .from("rooms")
                        .update({ price: parsed })
                        .eq("id", roomId);
                      if (roomErr) throw roomErr;
                    }
                    const moveInIsoForSync =
                      tenant?.moveInDate ||
                      tenant?.startDate ||
                      tenant?.booking?.move_in_date ||
                      tenant?.booking?.start_date ||
                      null;
                    const { data: rentRowsToSync, error: syncFetchErr } =
                      await supabase
                        .from("payments")
                        .select("id, due_date, amount_cents")
                        .eq("booking_id", tenant.id)
                        .eq("type", "rent")
                        .in("status", [
                          "pending",
                          "scheduled",
                          "overdue",
                          "failed",
                        ]);
                    if (syncFetchErr) throw syncFetchErr;
                    // repriceRentRows (not a raw base-rent recompute): a
                    // discounted tenant's pending rows must stay discounted,
                    // not get bumped back to the full amount just because a
                    // different month's payment row was edited here (COL-360).
                    const repriced = repriceRentRows({
                      rows: rentRowsToSync || [],
                      fullMonthlyRent: parsed,
                      moveInIso: moveInIsoForSync,
                      firstMonthOverride: firstMonthOverrideAfter,
                      discount: tenant?.booking,
                    });
                    for (const row of repriced) {
                      const { error: syncUpdErr } = await supabase
                        .from("payments")
                        .update({ amount_cents: row.amountCents })
                        .eq("id", row.id);
                      if (syncUpdErr) throw syncUpdErr;
                    }
                    await queryClient.invalidateQueries({
                      queryKey: ["rooms"],
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ["admin-all-rooms"],
                    });
                    await invalidateOccupancyViews(queryClient);
                    await queryClient.invalidateQueries({
                      queryKey: ["admin-bookings"],
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ["admin-analytics-bookings"],
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ["payments-upcoming"],
                    });
                    await queryClient.invalidateQueries({
                      queryKey: ["admin-payments"],
                    });
                    await queryClient.invalidateQueries({
                      queryKey: [PROPERTY_RENT_PAID_STATS_KEY],
                    });
                    // Also feeds the owners-list payout-readiness tile (COL-461).
                    invalidateOwnerCaches(queryClient);
                    if (tenant.userId) {
                      await queryClient.invalidateQueries({
                        queryKey: ["payments", tenant.userId],
                      });
                      await queryClient.invalidateQueries({
                        queryKey: ["tenant-payments", tenant.userId, tenant.id],
                      });
                    }
                  }
                  toast.success("Rent payment updated.");
                  setRentPaymentEditOpen(false);
                } catch (e: any) {
                  toast.error(e?.message || "Failed to update rent payment");
                }
              }}
            >
              {updateRentPaymentMutation.isPending ||
              updateFirstMonthOverrideMutation.isPending
                ? "Saving…"
                : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!proofToDelete}
        onOpenChange={(open) => !open && setProofToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this proof?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the file from storage and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeProofMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={removeProofMutation.isPending}
              onClick={() =>
                proofToDelete && removeProofMutation.mutate(proofToDelete)
              }
            >
              {removeProofMutation.isPending ? "Removing…" : "Remove"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

type TimelineEvent = {
  id: string;
  at: string; // ISO
  title: string;
  subtitle?: string | null;
  badge?: any;
  details?: any;
};

const TenantActivityTimelineTab = ({ tenant }: { tenant: any }) => {
  const { t } = useLanguage();
  const openContractFile = async (value: string, download = false) => {
    if (
      !(await openStoredFile(
        value,
        "contracts",
        download ? { download: true } : undefined
      ))
    ) {
      toast.error(t("admin.docs.openFailed", "Could not open the document."));
    }
  };
  const bookingId = tenant?.id != null ? Number(tenant.id) : null;
  const userId = tenant?.userId ?? null;
  const queryClient = useQueryClient();
  const [supportOpenId, setSupportOpenId] = useState<string | null>(null);
  const [proofUploadOpen, setProofUploadOpen] = useState(false);
  const [proofUploadPayment, setProofUploadPayment] = useState<{
    id: string;
    type: "rent" | "deposit";
  } | null>(null);
  const proofUploadFileRef = useRef<HTMLInputElement>(null);
  const [proofUploadProgress, setProofUploadProgress] = useState(0);
  const [proofUploading, setProofUploading] = useState(false);

  const formatMoney = (cents: number) =>
    new Intl.NumberFormat("en-EU", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format((cents || 0) / 100);

  const formatDateTime = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateOnly = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const statusBadge = (statusRaw: string | null | undefined) => {
    const status = String(statusRaw || "").toLowerCase();
    const map: Record<string, { className: string; label: string }> = {
      paid: {
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
        label: "Paid",
      },
      pending: {
        className: "bg-amber-50 text-amber-700 border-amber-200",
        label: "Pending",
      },
      scheduled: {
        className: "bg-slate-50 text-slate-700 border-slate-200",
        label: "Scheduled",
      },
      overdue: {
        className: "bg-red-50 text-red-700 border-red-200",
        label: "Overdue",
      },
      failed: {
        className: "bg-red-50 text-red-700 border-red-200",
        label: "Failed",
      },
      canceled: {
        className: "bg-slate-50 text-slate-600 border-slate-200",
        label: "Canceled",
      },
    };
    const c = map[status] || {
      className: "bg-muted text-muted-foreground border-border",
      label: status || "Unknown",
    };
    return (
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${c.className}`}
      >
        {c.label}
      </span>
    );
  };

  /** Stripe checkout / webhooks set intent or charge id — do not use paid_at (manual uploads have paid_at too). */
  const hasStripeIds = (p: any) =>
    !!(p?.stripe_payment_intent_id || p?.stripe_charge_id);

  const paymentMethodBadge = (p: any) => {
    const pmType = String(p?.payment_method?.type || "").toLowerCase();
    const manuallyMarkedPaid = !!tenant?.manuallyMarkedPaid;

    if (
      manuallyMarkedPaid &&
      !p?.stripe_payment_intent_id &&
      !p?.payment_method_id
    ) {
      return (
        <Badge
          variant="outline"
          className="bg-violet-50 text-violet-800 border-violet-200 text-xs font-medium"
        >
          Marked as Paid by Admin (Manual Update)
        </Badge>
      );
    }
    if (pmType === "card") {
      return (
        <Badge
          variant="outline"
          className="bg-sky-50 text-sky-800 border-sky-200 text-xs font-medium"
        >
          Visa / Card
        </Badge>
      );
    }
    if (pmType === "bank_transfer" || pmType === "sepa_debit") {
      return (
        <Badge
          variant="outline"
          className="bg-teal-50 text-teal-800 border-teal-200 text-xs font-medium"
        >
          Bank transfer / SEPA
        </Badge>
      );
    }
    if (hasStripeIds(p)) {
      return (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-800 border-indigo-200 text-xs font-medium"
        >
          Stripe
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-slate-50 text-slate-700 border-slate-200 text-xs font-medium"
      >
        Payment method unavailable
      </Badge>
    );
  };

  const { data: payments = [], isLoading: paymentsLoading } = useQuery<any[]>({
    queryKey: ["tenant-activity-payments", userId, bookingId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("payments")
        .select(
          `
          id,
          user_id,
          booking_id,
          type,
          amount_cents,
          status,
          due_date,
          paid_at,
          stripe_payment_intent_id,
          stripe_charge_id,
          marked_paid_at,
          marked_paid_by,
          payment_method_id,
          created_at,
          payment_method:payment_method_id (
            type,
            bank_name,
            last4
          )
        `
        )
        .eq("user_id", userId)
        .in("type", ["rent", "deposit"])
        .order("created_at", { ascending: false })
        .limit(250);
      if (error) throw error;
      const list = data || [];
      if (bookingId != null) {
        return list.filter(
          (p: any) => Number(p.booking_id) === bookingId || p.booking_id == null
        );
      }
      return list;
    },
    enabled: !!userId,
  });

  const { data: supportTickets = [], isLoading: supportLoading } = useQuery<
    any[]
  >({
    queryKey: ["tenant-activity-support-tickets", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("issues")
        .select("id,title,description,status,created_at,updated_at,priority")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
    staleTime: 15_000,
  });

  const { data: paymentsWarnings = [], isLoading: paymentsWarningsLoading } =
    useQuery<any[]>({
      queryKey: ["tenant-activity-payments-warnings", bookingId],
      queryFn: async () => {
        if (!bookingId) return [];
        const { data, error } = await supabase
          .from("payments_activity")
          .select(
            "id,event_type,stage,email_lang,email_to,email_subject,email_html,email_text,note,created_at,created_by"
          )
          .eq("booking_id", bookingId)
          .order("created_at", { ascending: false })
          .limit(200);
        if (error) throw error;
        return data || [];
      },
      enabled: !!bookingId,
      staleTime: 10_000,
    });

  const { activeSupportTickets, archivedSupportTickets } = useMemo(() => {
    const list = supportTickets || [];
    const archived = list.filter((t: any) => {
      const s = String(t?.status || "").toLowerCase();
      return s === "resolved" || s === "closed";
    });
    const active = list.filter((t: any) => !archived.includes(t));
    return { activeSupportTickets: active, archivedSupportTickets: archived };
  }, [supportTickets]);

  const paymentIds = useMemo(
    () => (payments || []).map((p: any) => p.id).filter(Boolean),
    [payments]
  );

  const { data: proofsByPaymentId = {}, isLoading: proofsLoading } = useQuery<
    Record<string, any>
  >({
    queryKey: [
      "tenant-activity-payment-proofs",
      userId,
      bookingId,
      paymentIds.join(","),
    ],
    queryFn: async () => {
      if (!paymentIds.length) return {};
      const { data, error } = await supabase
        .from("payment_proofs")
        .select(
          "id, payment_id, file_path, file_name, status, reviewed_at, created_at, rejection_reason"
        )
        .in("payment_id", paymentIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const map: Record<string, any> = {};
      (data || []).forEach((row: any) => {
        if (map[row.payment_id]) return; // keep newest (query is ordered desc)
        map[row.payment_id] = row;
      });
      return map;
    },
    enabled: !!userId && paymentIds.length > 0,
  });

  /**
   * Paid in UI only when: Stripe recorded (intent/charge id), or admin/tenant proof exists.
   * Rows marked paid in DB without Stripe ids and without proof show as pending (no false "Paid").
   */
  const getEffectivePaymentStatusActivity = (p: any): string => {
    const proof = proofsByPaymentId[String(p?.id)] ?? proofsByPaymentId[p?.id];
    if (!p) return "pending";
    if (hasStripeIds(p)) return String(p.status || "pending").toLowerCase();
    if (!proof && String(p.status || "").toLowerCase() === "paid")
      return "pending";
    return String(p.status || "pending").toLowerCase();
  };

  const paymentDateLineActivity = (p: any) => {
    const eff = getEffectivePaymentStatusActivity(p);
    if (eff === "paid" && p.paid_at)
      return `Paid on ${formatDateOnly(p.paid_at)}`;
    if (p.due_date) return `Due on ${formatDateOnly(p.due_date)}`;
    return `Created ${formatDateOnly(p.created_at)}`;
  };

  /** Single chip when clearly paid; else status + method (pending shows without fake Stripe). */
  const paymentStatusAndMethodBadges = (p: any) => {
    const eff = getEffectivePaymentStatusActivity(p);
    const proof = proofsByPaymentId[String(p.id)] ?? proofsByPaymentId[p.id];

    if (eff === "paid" && hasStripeIds(p)) {
      return (
        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:text-emerald-400">
          Paid · Stripe
        </span>
      );
    }
    if (eff === "paid" && proof) {
      return (
        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:text-emerald-400">
          Paid · Proof uploaded
        </span>
      );
    }
    return (
      <>
        {statusBadge(eff)}
        {paymentMethodBadge(p)}
      </>
    );
  };

  // Same canonical rule as every other surface (#1326) — a hand-rolled
  // "paid + a proof exists" copy here is exactly how this screen and the tenant
  // Dashboard drifted apart in the first place.
  const depositEffectivelyPaid = (p: any) =>
    isEffectivelyPaid(
      paymentStatusInput(
        p,
        proofsByPaymentId[String(p.id)] ?? proofsByPaymentId[p.id] ?? null,
        { isStripe: hasStripeIds(p) }
      )
    );

  const openProofUpload = (
    paymentId: string,
    paymentType: "rent" | "deposit"
  ) => {
    setProofUploadPayment({ id: paymentId, type: paymentType });
    setProofUploadProgress(0);
    setProofUploadOpen(true);
    if (proofUploadFileRef.current) proofUploadFileRef.current.value = "";
  };

  const uploadPaymentProofAsAdmin = async (paymentId: string, file: File) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];
    if (file.size > maxSize) throw new Error("File must be 10 MB or less.");
    if (!validTypes.includes(file.type))
      throw new Error("File must be PDF or image (JPEG, PNG, WebP).");

    setProofUploading(true);
    setProofUploadProgress(10);
    const ext = file.name.split(".").pop() || "pdf";
    const safeName = file.name.replace(/\s+/g, "-");
    const storagePath = `payment-proofs/${paymentId}/${Date.now()}-admin-${safeName}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from("contracts")
      .upload(storagePath, file, {
        contentType: file.type || "application/octet-stream",
        upsert: false,
      });
    if (uploadError) throw uploadError;
    setProofUploadProgress(70);

    const { error: insertError } = await supabase
      .from("payment_proofs")
      .insert({
        payment_id: paymentId,
        file_path: storagePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        status: "approved",
        reviewed_at: new Date().toISOString(),
        reviewed_by: user.id,
        admin_comment: "Uploaded by admin",
      });

    if (insertError) {
      await supabase.storage.from("contracts").remove([storagePath]);
      throw insertError;
    }

    setProofUploadProgress(100);
    queryClient.invalidateQueries({
      queryKey: ["tenant-activity-payment-proofs", userId, bookingId],
    });
    toast.success("Payment proof uploaded.");
    setTimeout(() => {
      setProofUploadOpen(false);
      setProofUploadPayment(null);
      setProofUploadProgress(0);
      setProofUploading(false);
    }, 250);
  };

  const { data: tenantDocs = [], isLoading: docsLoading } = useQuery<any[]>({
    queryKey: ["tenant-activity-documents", userId, bookingId],
    queryFn: async () => {
      if (!userId) return [];
      let q = supabase
        .from("tenant_documents")
        .select(
          "id, user_id, uploaded_by, booking_id, document_type, title, file_url, file_name, mime_type, created_at, reviewed_at, status"
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(250);
      if (bookingId != null) {
        q = q.or(`booking_id.eq.${bookingId},booking_id.is.null`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  const { data: auditLogsRaw = [], isLoading: auditLoading } = useQuery<any[]>({
    queryKey: [
      "tenant-activity-audit",
      bookingId,
      userId,
      paymentIds.join(","),
      tenantDocs.map((d: any) => d.id).join(","),
    ],
    queryFn: async () => {
      if (!bookingId) return [];
      const { data, error } = await supabase
        .from("audit_logs")
        .select(
          "id, action, resource_type, resource_id, old_values, new_values, created_at, user_id"
        )
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      const bId = String(bookingId);
      const pIds = new Set(paymentIds);
      const dIds = new Set((tenantDocs || []).map((d: any) => String(d.id)));
      return (data || []).filter((l: any) => {
        if (
          l.resource_type === "booking" &&
          String(l.resource_id || "") === bId
        )
          return true;
        if (
          l.resource_type === "payment" &&
          pIds.has(String(l.resource_id || ""))
        )
          return true;
        if (
          l.resource_type === "document" &&
          dIds.has(String(l.resource_id || ""))
        )
          return true;
        if (
          l.resource_type === "profile" &&
          userId &&
          String(l.resource_id || "") === String(userId)
        )
          return true;
        return false;
      });
    },
    enabled: !!bookingId,
  });

  const auditUserIds = useMemo(() => {
    const ids = new Set<string>();
    (auditLogsRaw || []).forEach((l: any) => {
      const id = l?.user_id ? String(l.user_id) : "";
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }, [auditLogsRaw]);

  const { data: auditProfiles = [], isLoading: auditProfilesLoading } =
    useQuery<any[]>({
      queryKey: ["tenant-activity-audit-profiles", auditUserIds.join(",")],
      queryFn: async () => {
        if (!auditUserIds.length) return [];
        const { data, error } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", auditUserIds);
        if (error) throw error;
        return data || [];
      },
      enabled: auditUserIds.length > 0,
    });

  const auditProfilesById = useMemo(() => {
    const map: Record<string, any> = {};
    (auditProfiles || []).forEach((p: any) => {
      if (p?.id) map[String(p.id)] = p;
    });
    return map;
  }, [auditProfiles]);

  const auditLogs = useMemo(() => {
    return (auditLogsRaw || []).map((l: any) => ({
      ...l,
      profiles: l?.user_id
        ? (auditProfilesById[String(l.user_id)] ?? null)
        : null,
    }));
  }, [auditLogsRaw, auditProfilesById]);

  const rentPayments = (payments || []).filter((p: any) => p.type === "rent");
  const depositPayments = (payments || []).filter(
    (p: any) => p.type === "deposit"
  );
  const depositState = resolveDepositState({
    depositPayments,
    isEffectivelyPaid: depositEffectivelyPaid,
    expectedDepositEUR: getExpectedDepositEUR({
      monthly_rent: tenant?.booking?.monthly_rent,
      deposit_amount: tenant?.booking?.deposit_amount,
      deposit_discount_mode: tenant?.booking?.deposit_discount_mode,
      deposit_discount_value: tenant?.booking?.deposit_discount_value,
      rooms: tenant?.room
        ? {
            price: tenant.room.price,
            deposit_amount: tenant.room.deposit_amount,
          }
        : null,
    }),
  });
  const expectedDepositCents = depositState.expectedCents;
  const totalDepositPaidCents = depositState.paidCents;

  const contractCompletion = (() => {
    const b = tenant?.booking || {};
    const manual =
      !!b.manual_contract_upload || computeShowManualContractLabel(b);
    const hasDocusign = !!(
      b.docusign_envelope_id && String(b.docusign_envelope_id).trim()
    );
    const methodLabel = manual
      ? "Contract Uploaded Manually"
      : hasDocusign
        ? "Signed via DocuSign"
        : "Contract completion method unknown";
    const signedAt = b.contract_signed_at || null;
    // signed_contract_url is tenant-writable: only ever open an http(s) link.
    const docUrl =
      [b.signed_contract_url, b.contract_url].find(
        (u): u is string => !!u && isOpenableUrl(u)
      ) ?? null;
    return { methodLabel, signedAt, docUrl, manual, hasDocusign };
  })();

  const rentBreakdown = (() => {
    const full = Number(tenant?.monthlyRent || 0);
    const billed = Number(tenant?.displayedRent ?? full);
    const isPartial =
      !!tenant?.isPartialFirstMonthRent &&
      full > 0 &&
      billed > 0 &&
      billed < full;
    if (!isPartial) return null;
    const { base, utilities } = splitRentLineItems(full, billed);
    return { full, billed, base, utilities };
  })();

  const rentEditEvents: TimelineEvent[] = (auditLogs || [])
    .filter(
      (l: any) =>
        l.resource_type === "booking" &&
        l.new_values &&
        (l.new_values.monthly_rent != null ||
          l.new_values.first_month_rent_override != null)
    )
    .map((l: any) => {
      const prev = l.old_values || {};
      const next = l.new_values || {};
      const prevRent =
        prev.monthly_rent != null ? Number(prev.monthly_rent) : null;
      const nextRent =
        next.monthly_rent != null ? Number(next.monthly_rent) : null;
      const prevOverride =
        prev.first_month_rent_override != null
          ? Number(prev.first_month_rent_override)
          : null;
      const nextOverride =
        next.first_month_rent_override != null
          ? Number(next.first_month_rent_override)
          : null;
      const who = l.profiles?.name || l.profiles?.email || "Admin/System";

      return {
        id: `rent-edit-${l.id}`,
        at: l.created_at,
        title: "Rent updated",
        subtitle: `By ${who}`,
        badge: (
          <Badge className="bg-blue-50 text-blue-700 border-blue-200">
            Rent adjustment
          </Badge>
        ),
        details: (
          <div className="grid gap-2 text-sm">
            {prevRent != null || nextRent != null ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 px-3 py-2">
                <span className="text-muted-foreground">
                  Previous rent → Updated rent
                </span>
                <span className="font-medium tabular-nums">
                  {prevRent != null ? `€${prevRent.toFixed(2)}` : "—"} →{" "}
                  {nextRent != null ? `€${nextRent.toFixed(2)}` : "—"}
                </span>
              </div>
            ) : null}
            {prevOverride != null || nextOverride != null ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 px-3 py-2">
                <span className="text-muted-foreground">
                  First month override
                </span>
                <span className="font-medium tabular-nums">
                  {prevOverride != null
                    ? `€${prevOverride.toFixed(2)}`
                    : "Auto"}{" "}
                  →{" "}
                  {nextOverride != null
                    ? `€${nextOverride.toFixed(2)}`
                    : "Auto"}
                </span>
              </div>
            ) : null}
          </div>
        ),
      };
    });

  const profileEditEvents: TimelineEvent[] = (auditLogs || [])
    .filter(
      (l: any) =>
        l.resource_type === "profile" &&
        userId &&
        String(l.resource_id || "") === String(userId)
    )
    .map((l: any) => {
      const prev = (l.old_values || {}) as any;
      const next = (l.new_values || {}) as any;
      const who = l.profiles?.name || l.profiles?.email || "Tenant";

      const changed: Array<{ label: string; from: any; to: any }> = [];
      if (prev?.name !== next?.name)
        changed.push({ label: "Name", from: prev?.name, to: next?.name });
      if (prev?.email !== next?.email)
        changed.push({ label: "Email", from: prev?.email, to: next?.email });

      return {
        id: `profile-edit-${l.id}`,
        at: l.created_at,
        title: "Profile updated",
        subtitle: `By ${who}`,
        badge: (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200">
            Profile change
          </Badge>
        ),
        details: changed.length ? (
          <div className="grid gap-2 text-sm">
            {changed.map((c) => (
              <div
                key={c.label}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 px-3 py-2"
              >
                <span className="text-muted-foreground">{c.label}</span>
                <span className="font-medium">
                  {c.from != null && String(c.from).trim()
                    ? String(c.from)
                    : "—"}{" "}
                  → {c.to != null && String(c.to).trim() ? String(c.to) : "—"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Profile details changed.
          </p>
        ),
      };
    });

  const paymentEvents: TimelineEvent[] = (payments || []).map((p: any) => {
    const proof = proofsByPaymentId?.[p.id] || null;
    const at = p.paid_at || p.due_date || p.created_at;
    const title = p.type === "deposit" ? "Deposit payment" : "Rent payment";
    const eff = getEffectivePaymentStatusActivity(p);
    const subtitleParts = [
      eff === "paid" && p.paid_at
        ? `Paid on ${formatDateOnly(p.paid_at)}`
        : p.due_date
          ? `Due on ${formatDateOnly(p.due_date)}`
          : null,
      formatMoney(Number(p.amount_cents || 0)),
    ].filter(Boolean);
    const proofPath = proof?.file_path ?? null;

    return {
      id: `payment-${p.id}`,
      at: String(at || new Date().toISOString()),
      title,
      subtitle: subtitleParts.join(" • "),
      badge: (
        <div className="flex items-center gap-2 flex-wrap">
          {paymentStatusAndMethodBadges(p)}
        </div>
      ),
      details: (
        <div className="space-y-3">
          {proof ? (
            <div className="rounded-xl border bg-background p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Payment proof</p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {proof.file_name || "Uploaded file"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {proofPath && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      // Signed on click (COL-436): the bucket is going private.
                      onClick={async () => {
                        if (!(await openProofInNewTab(proofPath))) {
                          toast.error(
                            t(
                              "admin.docs.openFailed",
                              "Could not open the document."
                            )
                          );
                        }
                      }}
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() =>
                      openProofUpload(
                        p.id,
                        p.type === "deposit" ? "deposit" : "rent"
                      )
                    }
                  >
                    <Upload className="w-4 h-4" />
                    Upload {p.type === "deposit" ? "deposit" : "rent"} proof
                  </Button>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 text-xs">
                <div className="rounded-lg border bg-muted/20 px-3 py-2">
                  <p className="text-muted-foreground">Upload date</p>
                  <p className="font-medium">
                    {formatDateTime(proof.created_at)}
                  </p>
                </div>
                <div className="rounded-lg border bg-muted/20 px-3 py-2">
                  <p className="text-muted-foreground">Viewed by admin</p>
                  <p className="font-medium">
                    {formatDateTime(proof.reviewed_at)}
                  </p>
                </div>
              </div>
              {proof.status && (
                <div className="mt-3 text-xs">
                  <span className="text-muted-foreground">Proof status: </span>
                  <span className="font-medium capitalize">
                    {String(proof.status).replaceAll("_", " ")}
                  </span>
                  {proof.rejection_reason ? (
                    <p className="mt-1 text-destructive">
                      <span className="font-medium">Rejection reason:</span>{" "}
                      {proof.rejection_reason}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground">
                No payment proof uploaded.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() =>
                  openProofUpload(
                    p.id,
                    p.type === "deposit" ? "deposit" : "rent"
                  )
                }
              >
                <Upload className="w-4 h-4" />
                Upload {p.type === "deposit" ? "deposit" : "rent"} proof
              </Button>
            </div>
          )}
        </div>
      ),
    };
  });

  const proofUploadTitle =
    proofUploadPayment?.type === "deposit"
      ? "Upload deposit proof"
      : "Upload rent proof";

  const contractEvents: TimelineEvent[] = [
    {
      id: `contract-${bookingId ?? "na"}`,
      at:
        tenant?.booking?.contract_signed_at ||
        tenant?.booking?.updated_at ||
        tenant?.booking?.created_at ||
        new Date().toISOString(),
      title: "Contract",
      subtitle: contractCompletion.methodLabel,
      badge: (
        <Badge
          variant="outline"
          className={
            contractCompletion.manual
              ? "bg-teal-50 text-teal-800 border-teal-200"
              : "bg-indigo-50 text-indigo-800 border-indigo-200"
          }
        >
          {contractCompletion.manual
            ? "Manual Upload"
            : contractCompletion.hasDocusign
              ? "DocuSign"
              : "Contract"}
        </Badge>
      ),
      details: (
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2 text-xs">
            <div className="rounded-lg border bg-muted/20 px-3 py-2">
              <p className="text-muted-foreground">
                {contractCompletion.manual ? "Upload date" : "Signing date"}
              </p>
              <p className="font-medium">
                {formatDateTime(contractCompletion.signedAt)}
              </p>
            </div>
            <div className="rounded-lg border bg-muted/20 px-3 py-2">
              <p className="text-muted-foreground">Document</p>
              <p className="font-medium">
                {contractCompletion.docUrl ? "Available" : "Not uploaded"}
              </p>
            </div>
          </div>
          {contractCompletion.docUrl ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                // Signed on click (COL-436): the contracts bucket is going
                // private, so the stored public URL is never opened directly.
                onClick={() => void openContractFile(contractCompletion.docUrl)}
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() =>
                  void openContractFile(contractCompletion.docUrl, true)
                }
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  const tenantMessageEvent: TimelineEvent[] = (() => {
    const msg = String(tenant?.booking?.description || "").trim();
    if (!msg) return [];
    const at =
      tenant?.booking?.created_at ||
      tenant?.booking?.start_date ||
      new Date().toISOString();
    return [
      {
        id: `tenant-message-${bookingId ?? "na"}`,
        at: String(at),
        title: "Tenant message",
        subtitle: "Tell us about yourself",
        badge: (
          <Badge className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200">
            Tenant note
          </Badge>
        ),
        details: (
          <div className="rounded-xl border bg-fuchsia-50/50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-fuchsia-900">
              <MessageSquare className="w-4 h-4" />
              Message
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-900 leading-relaxed">
              {msg}
            </p>
          </div>
        ),
      },
    ];
  })();

  const propertyRoomEvent: TimelineEvent[] = (() => {
    const propertyTitle = String(tenant?.property?.title || "").trim();
    const propertyAddress = String(tenant?.property?.address || "").trim();
    const propertyCity = String(tenant?.property?.city || "").trim();
    const roomTitleRaw = String(tenant?.room?.title || "").trim();
    const roomTitle = roomTitleRaw
      ? roomTitleRaw.toLowerCase().startsWith("room")
        ? roomTitleRaw
        : `Room ${roomTitleRaw}`
      : "";

    const moveInIso =
      tenant?.moveInDate ||
      tenant?.booking?.move_in_date ||
      tenant?.booking?.start_date ||
      tenant?.startDate ||
      null;
    const moveOutIso =
      tenant?.moveOutDate ||
      tenant?.booking?.move_out_date ||
      tenant?.booking?.end_date ||
      tenant?.endDate ||
      null;

    const hasAny =
      !!propertyTitle ||
      !!propertyAddress ||
      !!propertyCity ||
      !!roomTitle ||
      !!moveInIso ||
      !!moveOutIso;

    if (!hasAny) return [];

    const at = String(
      moveInIso || tenant?.booking?.created_at || new Date().toISOString()
    );
    const addressLine = [propertyAddress, propertyCity]
      .filter(Boolean)
      .join(", ");

    return [
      {
        id: `property-room-${bookingId ?? "na"}`,
        at,
        title: "Property & Room Details",
        subtitle: "Move-in / Move-out and address",
        badge: (
          <Badge className="bg-sky-50 text-sky-800 border-sky-200">
            Booking details
          </Badge>
        ),
        details: (
          <div className="rounded-xl border bg-sky-50/50 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border bg-background px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  Property
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {propertyTitle || "—"}
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{addressLine || "—"}</span>
                </p>
              </div>
              <div className="rounded-lg border bg-background px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  Room
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {roomTitle || "—"}
                </p>
              </div>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border bg-background px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  Move-in date
                </p>
                <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  {formatDateTime(moveInIso)}
                </p>
              </div>
              <div className="rounded-lg border bg-background px-3 py-2.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  Move-out date
                </p>
                <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  {formatDateTime(moveOutIso)}
                </p>
              </div>
            </div>
          </div>
        ),
      },
    ];
  })();

  const docEvents: TimelineEvent[] = (tenantDocs || []).map((d: any) => {
    const isManualAdminUpload =
      !!d?.uploaded_by &&
      !!d?.user_id &&
      String(d.uploaded_by) !== String(d.user_id);

    return {
      id: `doc-${d.id}`,
      at: d.created_at || new Date().toISOString(),
      title: "Tenant document uploaded",
      subtitle: `${d.title || d.document_type || "Document"}${d.document_type ? ` • ${d.document_type}` : ""}`,
      badge: isManualAdminUpload ? (
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="bg-sky-50 text-sky-700 border-sky-200"
          >
            Manual upload by admin
          </Badge>
          <Badge
            variant="outline"
            className="bg-slate-50 text-slate-700 border-slate-200"
          >
            {String(d.status || "uploaded").replaceAll("_", " ")}
          </Badge>
        </div>
      ) : (
        <Badge
          variant="outline"
          className="bg-slate-50 text-slate-700 border-slate-200"
        >
          {String(d.status || "uploaded").replaceAll("_", " ")}
        </Badge>
      ),
      details: (
        <div className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2 text-xs">
            <div className="rounded-lg border bg-muted/20 px-3 py-2">
              <p className="text-muted-foreground">Upload date</p>
              <p className="font-medium">{formatDateTime(d.created_at)}</p>
            </div>
            <div className="rounded-lg border bg-muted/20 px-3 py-2">
              <p className="text-muted-foreground">Viewed by admin</p>
              <p className="font-medium">{formatDateTime(d.reviewed_at)}</p>
            </div>
          </div>
          {d.file_url ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={async () => {
                  // Signed on click: the stored URL is public (COL-436).
                  if (!(await openTenantDocument(d.file_url))) {
                    toast.error("Failed to open document.");
                  }
                }}
              >
                <Eye className="w-4 h-4" />
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={async () => {
                  const ok = await openTenantDocument(d.file_url, {
                    download: d.file_name || d.title || true,
                  });
                  if (!ok) toast.error("Failed to open document.");
                }}
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            </div>
          ) : null}
        </div>
      ),
    };
  });

  const allEvents = useMemo(() => {
    const events = [
      ...propertyRoomEvent,
      ...tenantMessageEvent,
      ...profileEditEvents,
      ...paymentEvents,
      ...contractEvents,
      ...docEvents,
      ...rentEditEvents,
    ]
      .filter((e) => !!e.at)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    return events;
  }, [
    propertyRoomEvent,
    tenantMessageEvent,
    profileEditEvents,
    paymentEvents,
    contractEvents,
    docEvents,
    rentEditEvents,
  ]);

  const loading =
    paymentsLoading ||
    proofsLoading ||
    docsLoading ||
    auditLoading ||
    auditProfilesLoading ||
    supportLoading;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Receipt className="w-5 h-5" />
              Rent Payments
            </CardTitle>
            <CardDescription>
              All rent payment activity for this tenant
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {rentBreakdown ? (
              <div className="rounded-xl border bg-muted/20 p-4">
                <p className="text-sm font-semibold">
                  Pro-rated first month calculation
                </p>
                <div className="mt-2 grid gap-2 text-xs">
                  <div className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2">
                    <span className="text-muted-foreground">
                      Previous / full monthly rent
                    </span>
                    <span className="font-medium tabular-nums">
                      €{rentBreakdown.full.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2">
                    <span className="text-muted-foreground">
                      Updated / billed first month
                    </span>
                    <span className="font-medium tabular-nums">
                      €{rentBreakdown.billed.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2">
                    <span className="text-muted-foreground">
                      Breakdown (base)
                    </span>
                    <span className="font-medium tabular-nums">
                      €{rentBreakdown.base.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2">
                    <span className="text-muted-foreground">
                      Breakdown (utilities)
                    </span>
                    <span className="font-medium tabular-nums">
                      €{rentBreakdown.utilities.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}

            {rentPayments.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No rent payments recorded.
              </p>
            ) : (
              <div className="space-y-3">
                {rentPayments.slice(0, 6).map((p: any) => (
                  <div
                    key={p.id}
                    className="rounded-xl border bg-background p-4"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">
                          {formatMoney(Number(p.amount_cents || 0))}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {paymentDateLineActivity(p)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {paymentStatusAndMethodBadges(p)}
                      </div>
                    </div>
                    {proofsByPaymentId?.[p.id] ? (
                      <div className="mt-3 text-xs text-muted-foreground">
                        Proof uploaded{" "}
                        {formatDateOnly(proofsByPaymentId[p.id].created_at)} ·
                        Viewed{" "}
                        {formatDateOnly(proofsByPaymentId[p.id].reviewed_at)}
                      </div>
                    ) : null}
                  </div>
                ))}
                {rentPayments.length > 6 ? (
                  <p className="text-xs text-muted-foreground">
                    Showing latest 6. See full list below in Activity Timeline.
                  </p>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="w-5 h-5" />
              Deposit Payments
            </CardTitle>
            <CardDescription>
              Deposit status, method, proofs, and admin view timestamps
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-3 text-xs">
              <div className="rounded-xl border bg-muted/20 px-3 py-2.5">
                <p className="text-muted-foreground">Total deposit amount</p>
                <p className="font-semibold tabular-nums">
                  {formatMoney(expectedDepositCents)}
                </p>
              </div>
              <div className="rounded-xl border bg-muted/20 px-3 py-2.5">
                <p className="text-muted-foreground">Total paid</p>
                <p className="font-semibold tabular-nums">
                  {formatMoney(totalDepositPaidCents)}
                </p>
              </div>
              <div className="rounded-xl border bg-muted/20 px-3 py-2.5">
                <p className="text-muted-foreground">Status</p>
                <div className="mt-1">
                  {depositState.status === "paid" ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      Paid
                    </Badge>
                  ) : depositState.status === "partial" ? (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                      Partial
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                      Pending
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {depositPayments.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No deposit payments recorded.
              </p>
            ) : (
              <div className="space-y-3">
                {depositPayments.slice(0, 6).map((p: any) => (
                  <div
                    key={p.id}
                    className="rounded-xl border bg-background p-4"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold">
                          {formatMoney(Number(p.amount_cents || 0))}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {paymentDateLineActivity(p)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {paymentStatusAndMethodBadges(p)}
                      </div>
                    </div>
                    {proofsByPaymentId?.[p.id] ? (
                      <div className="mt-3 text-xs text-muted-foreground">
                        Proof uploaded{" "}
                        {formatDateOnly(proofsByPaymentId[p.id].created_at)} ·
                        Viewed{" "}
                        {formatDateOnly(proofsByPaymentId[p.id].reviewed_at)}
                      </div>
                    ) : null}
                  </div>
                ))}
                {depositPayments.length > 6 ? (
                  <p className="text-xs text-muted-foreground">
                    Showing latest 6. See full list below in Activity Timeline.
                  </p>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/60 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5" />
            Contract Information
          </CardTitle>
          <CardDescription>{contractCompletion.methodLabel}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2 text-xs">
            <div className="rounded-xl border bg-muted/20 px-3 py-2.5">
              <p className="text-muted-foreground">
                {contractCompletion.manual ? "Upload date" : "Signing date"}
              </p>
              <p className="font-medium">
                {formatDateTime(contractCompletion.signedAt)}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/20 px-3 py-2.5">
              <p className="text-muted-foreground">Preview / download</p>
              <p className="font-medium">
                {contractCompletion.docUrl ? "Available" : "Not available"}
              </p>
            </div>
          </div>
          {contractCompletion.docUrl ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                // Signed on click (COL-436): the contracts bucket is going
                // private, so the stored public URL is never opened directly.
                onClick={() => void openContractFile(contractCompletion.docUrl)}
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() =>
                  void openContractFile(contractCompletion.docUrl, true)
                }
              >
                <Download className="w-4 h-4" />
                Download
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-sm overflow-hidden">
        <CardHeader className="bg-gradient-to-br from-amber-500/10 via-transparent to-rose-500/5 border-b border-border/60">
          <CardTitle className="flex items-center gap-3 text-lg">
            <span className="h-9 w-9 rounded-xl bg-amber-500/10 text-amber-800 border border-amber-500/15 flex items-center justify-center shrink-0">
              <TriangleAlert className="w-5 h-5" />
            </span>
            <span className="min-w-0">
              <span className="block leading-tight">
                Payments &amp; Warnings
              </span>
              <span className="block text-xs font-medium text-muted-foreground mt-0.5">
                Rent reminders, drafts, and payment warnings for this booking
              </span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {paymentsWarningsLoading ? (
            <div className="py-8">
              <Skeleton className="h-5 w-40" />
            </div>
          ) : paymentsWarnings.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                No payment warnings yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentsWarnings
                .filter((e: any) =>
                  [
                    "reminder_sent",
                    "reminder_draft_saved",
                    "admin_email_sent",
                    "inkasso_referred",
                  ].includes(String(e.event_type || ""))
                )
                .map((e: any) => {
                  const id = String(e.id);
                  const open = supportOpenId === `pw:${id}`;
                  const label =
                    e.event_type === "reminder_draft_saved"
                      ? `Draft Stage ${e.stage ?? "—"}`
                      : e.event_type === "reminder_sent"
                        ? `Sent Stage ${e.stage ?? "—"}`
                        : e.event_type === "inkasso_referred"
                          ? "Inkasso referred"
                          : "Email sent";
                  const subject = String(e.email_subject || e.note || "—");
                  const html =
                    typeof e.email_html === "string" ? e.email_html : "";
                  const text =
                    typeof e.email_text === "string" ? e.email_text : "";
                  const note = typeof e.note === "string" ? e.note : "";
                  return (
                    <Collapsible
                      key={id}
                      open={open}
                      onOpenChange={(next) =>
                        setSupportOpenId(next ? `pw:${id}` : null)
                      }
                      className="rounded-xl border border-border/60"
                    >
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                variant="secondary"
                                className="h-6 px-2 rounded-full"
                              >
                                {label}
                              </Badge>
                              {e.email_to ? (
                                <span className="text-xs text-muted-foreground truncate">
                                  To {String(e.email_to)}
                                </span>
                              ) : null}
                            </div>
                            <div className="text-sm font-medium truncate mt-1">
                              {subject}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {formatDateTime(String(e.created_at || ""))}
                            </div>
                          </div>
                          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="border-t border-border/60 p-4 space-y-3 bg-muted/10">
                        {html ? (
                          <div className="rounded-xl overflow-hidden border bg-white">
                            <iframe
                              title="Email preview"
                              className="w-full h-[420px] bg-white"
                              sandbox="allow-same-origin"
                              srcDoc={String(html).replace(
                                /<script[\s\S]*?<\/script>/gi,
                                ""
                              )}
                            />
                          </div>
                        ) : text ? (
                          <pre className="text-xs whitespace-pre-wrap rounded-xl border bg-background p-3">
                            {text}
                          </pre>
                        ) : note ? (
                          <pre className="text-xs whitespace-pre-wrap rounded-xl border bg-background p-3">
                            {note}
                          </pre>
                        ) : e.note ? (
                          <div className="text-sm">{String(e.note)}</div>
                        ) : null}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-sm overflow-hidden">
        <CardHeader className="bg-gradient-to-br from-[#35CFC8]/10 via-transparent to-emerald-500/5 border-b border-border/60">
          <CardTitle className="flex items-center gap-3 text-lg">
            <span className="h-9 w-9 rounded-xl bg-[#35CFC8]/15 text-[#0f766e] border border-[#35CFC8]/20 flex items-center justify-center shrink-0">
              <Archive className="w-5 h-5" />
            </span>
            <span className="min-w-0">
              <span className="block leading-tight">Support archive</span>
              <span className="block text-xs font-medium text-muted-foreground mt-0.5">
                All support tickets and conversation history for this tenant
              </span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {supportTickets.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                No support tickets yet.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold">Open tickets</p>
                  <Badge variant="secondary" className="h-6 px-2 rounded-full">
                    {activeSupportTickets.length}
                  </Badge>
                </div>
                {activeSupportTickets.length ? (
                  <div className="space-y-3">
                    {activeSupportTickets.map((t: any) => {
                      const id = String(t.id);
                      const status = String(t.status || "").toLowerCase();
                      const open = supportOpenId === id;
                      return (
                        <Collapsible
                          key={id}
                          open={open}
                          onOpenChange={(next) =>
                            setSupportOpenId(next ? id : null)
                          }
                          className={cn(
                            "rounded-2xl border bg-background overflow-hidden transition-colors",
                            open
                              ? "border-[#35CFC8]/45 ring-1 ring-[#35CFC8]/15"
                              : "border-border/60"
                          )}
                        >
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="w-full text-left p-4 sm:p-5 hover:bg-muted/20 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-semibold truncate">
                                      {t.title || "Untitled ticket"}
                                    </p>
                                    <Badge
                                      variant="secondary"
                                      className="capitalize bg-amber-50 text-amber-700 border-amber-200"
                                    >
                                      {status
                                        ? status.split("_").join(" ")
                                        : "unknown"}
                                    </Badge>
                                  </div>
                                  {t.description ? (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                      {t.description}
                                    </p>
                                  ) : null}
                                  <p className="text-[11px] text-muted-foreground mt-2 tabular-nums">
                                    Created {formatDateTime(t.created_at)}
                                  </p>
                                </div>
                                <div className="shrink-0 mt-0.5">
                                  <ChevronDown
                                    className={cn(
                                      "h-5 w-5 text-muted-foreground transition-transform",
                                      open ? "rotate-180" : "rotate-0"
                                    )}
                                  />
                                </div>
                              </div>
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="border-t border-border/60 bg-muted/10">
                            <div className="p-4 sm:p-5">
                              <IssueChatThread
                                issueId={id}
                                currentUserId=""
                                queryKeyPrefix={`admin-tenant-activity:${userId}`}
                                readOnly
                                markReadOnOpen={false}
                                readScope="admin"
                                senderScope="admin"
                                supportLabel="TENANT"
                                tenantLabel="TENANT"
                                youLabel="ADMIN"
                                adminLabel="ADMIN"
                                showViewBadge={false}
                                showInternalToggle={false}
                              />
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No open tickets.
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/40 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Archive className="h-4 w-4 text-emerald-700" />
                      <p className="text-sm font-semibold text-emerald-900">
                        Archived (resolved/closed)
                      </p>
                    </div>
                    <Badge className="h-6 px-2 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                      {archivedSupportTickets.length}
                    </Badge>
                  </div>
                  <p className="text-xs text-emerald-800/80 mt-1">
                    Completed tickets are stored here for future reference.
                  </p>
                </div>
                {archivedSupportTickets.length ? (
                  <div className="space-y-3">
                    {archivedSupportTickets.map((t: any) => {
                      const id = String(t.id);
                      const status = String(t.status || "").toLowerCase();
                      const open = supportOpenId === id;
                      return (
                        <Collapsible
                          key={id}
                          open={open}
                          onOpenChange={(next) =>
                            setSupportOpenId(next ? id : null)
                          }
                          className={cn(
                            "rounded-2xl border bg-background overflow-hidden transition-colors",
                            open
                              ? "border-[#35CFC8]/45 ring-1 ring-[#35CFC8]/15"
                              : "border-border/60"
                          )}
                        >
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="w-full text-left p-4 sm:p-5 hover:bg-muted/20 transition-colors"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-sm font-semibold truncate">
                                      {t.title || "Untitled ticket"}
                                    </p>
                                    <Badge
                                      variant="secondary"
                                      className="capitalize bg-emerald-50 text-emerald-700 border-emerald-200"
                                    >
                                      {status
                                        ? status.split("_").join(" ")
                                        : "unknown"}
                                    </Badge>
                                  </div>
                                  {t.description ? (
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                      {t.description}
                                    </p>
                                  ) : null}
                                  <p className="text-[11px] text-muted-foreground mt-2 tabular-nums">
                                    Created {formatDateTime(t.created_at)}
                                  </p>
                                </div>
                                <div className="shrink-0 mt-0.5">
                                  <ChevronDown
                                    className={cn(
                                      "h-5 w-5 text-muted-foreground transition-transform",
                                      open ? "rotate-180" : "rotate-0"
                                    )}
                                  />
                                </div>
                              </div>
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="border-t border-border/60 bg-muted/10">
                            <div className="p-4 sm:p-5">
                              <IssueChatThread
                                issueId={id}
                                currentUserId=""
                                queryKeyPrefix={`admin-tenant-activity:${userId}`}
                                readOnly
                                markReadOnOpen={false}
                                readScope="admin"
                                senderScope="admin"
                                supportLabel="TENANT"
                                tenantLabel="TENANT"
                                youLabel="ADMIN"
                                adminLabel="ADMIN"
                                showViewBadge={false}
                                showInternalToggle={false}
                              />
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No archived tickets yet.
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Activity Timeline
          </CardTitle>
          <CardDescription>
            Chronological history of payments, contract actions, document
            uploads, and admin updates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
            </div>
          ) : allEvents.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm text-muted-foreground">
                No activity recorded yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {allEvents.map((e) => (
                <div
                  key={e.id}
                  className="relative rounded-2xl border bg-background p-5"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold">{e.title}</p>
                        {e.badge}
                      </div>
                      {e.subtitle ? (
                        <p className="text-xs text-muted-foreground mt-1">
                          {e.subtitle}
                        </p>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {formatDateTime(e.at)}
                    </div>
                  </div>
                  {e.details ? <div className="mt-4">{e.details}</div> : null}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={proofUploadOpen}
        onOpenChange={(open) => {
          setProofUploadOpen(open);
          if (!open) {
            setProofUploadPayment(null);
            setProofUploadProgress(0);
            setProofUploading(false);
            if (proofUploadFileRef.current)
              proofUploadFileRef.current.value = "";
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{proofUploadTitle}</DialogTitle>
            <DialogDescription>
              Upload a receipt/screenshot for this{" "}
              {proofUploadPayment?.type ?? "payment"} payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>File</Label>
              <input
                ref={proofUploadFileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="text-sm w-full"
              />
              <p className="text-xs text-muted-foreground">
                Max 10 MB. PDF or images.
              </p>
            </div>
            {proofUploading ? <Progress value={proofUploadProgress} /> : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setProofUploadOpen(false)}
              disabled={proofUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const paymentId = proofUploadPayment?.id;
                const file = proofUploadFileRef.current?.files?.[0];
                if (!paymentId) {
                  toast.error("Select a payment first.");
                  return;
                }
                if (!file) {
                  toast.error("Choose a file");
                  return;
                }
                try {
                  await uploadPaymentProofAsAdmin(paymentId, file);
                } catch (e: any) {
                  setProofUploading(false);
                  setProofUploadProgress(0);
                  toast.error(e?.message || "Upload failed");
                }
              }}
              disabled={proofUploading || !proofUploadPayment}
            >
              {proofUploading ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export type AdminTenantsProps = {
  openBookingIdFromUrl?: string | number | null;
  onClearBookingIdFromUrl?: () => void;
};

export const AdminTenants = (props: AdminTenantsProps) => {
  const { onClearBookingIdFromUrl } = props ?? {};
  const urlBookingId =
    props?.openBookingIdFromUrl != null
      ? String(props.openBookingIdFromUrl)
      : null;
  const { t } = useLanguage();
  const {
    isSuperAdmin,
    canManageTenant,
    canActAs,
    actAsTenant,
    user: currentAuthUser,
  } = useAuth();
  const canCreateTenant = canManageTenant;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  // Single status filter (feedback #9): the 10 journey stages, plus "all" and
  // "archived". Replaces the old coarse pipeline buckets/stat-cards.
  const [stageFilter, setStageFilter] = useState<
    "all" | "archived" | TenantJourneyStageId
  >("all");
  // Test/CoLiviq accounts (internal + seed test emails, plus any dynamic
  // ones an admin registered in the Directory) are hidden by default so
  // they don't skew tenant counts/search. Off by default.
  const [includeTestAccounts, setIncludeTestAccounts] = useState(false);
  const { brandText } = useOperatorBranding();
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [rentEditOpen, setRentEditOpen] = useState(false);
  // COL-220: Reassign-room dialog state. Holds the booking + tenant
  // name + originating room/property for the dialog to open against.
  // Null when the dialog is closed.
  const [reassignFor, setReassignFor] = useState<{
    bookingId: number;
    tenantName: string;
    fromRoomId: number;
    propertyId: number;
  } | null>(null);
  const [editMonthlyRent, setEditMonthlyRent] = useState("");
  const [editFirstMonthOverride, setEditFirstMonthOverride] = useState("");
  const [savingRent, setSavingRent] = useState(false);
  const [syncingContractStatus, setSyncingContractStatus] = useState(false);
  const [keynestAccessUrl, setKeynestAccessUrl] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Tenant groups (multiple room applications under one account) that are
  // expanded in the list. Collapsed by default — the header row shows the
  // application count until the admin opens it.
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(
    new Set()
  );
  const toggleTenantExpanded = (key: string) =>
    setExpandedTenants((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Super admin inline edit mode for the tenant detail Overview tab
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editBookingStatus, setEditBookingStatus] = useState("");
  const [editContractStatus, setEditContractStatus] = useState("");
  const [editPaymentStatus, setEditPaymentStatus] = useState("");
  const [editMoveInDate, setEditMoveInDate] = useState("");
  const [editMoveOutDate, setEditMoveOutDate] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const itemsPerPage = 10;
  const adminSignedContractInputRef = useRef<HTMLInputElement>(null);
  /** Per-booking contract template override — set by the dropdown before Generate Contract. */
  const [contractTemplateOverride, setContractTemplateOverride] =
    useState<string>("");
  const [generatingContract, setGeneratingContract] = useState(false);

  /**
   * Sync the dropdown to the selected booking's saved template. Without this,
   * the state bleeds across tenants (previous override stays selected) and
   * admins can't see which template is actually saved on the booking, so
   * Regenerate ends up sending no templateId and falls through to the system
   * default.
   */
  useEffect(() => {
    const savedId = selectedTenant?.booking?.contract_template_id ?? "";
    setContractTemplateOverride(savedId ?? "");
  }, [selectedTenant?.id, selectedTenant?.booking?.contract_template_id]);

  /** Contract templates for the template selector dropdown. */
  const { data: contractTemplates = [] } = useQuery({
    queryKey: ["contract-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contract_templates")
        .select("id, name, is_default")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const invokeSyncDocusignEnvelopeForBooking = async (bookingId: number) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const base = (supabaseUrl || "").replace(/\/+$/, "");
    const res = await fetch(`${base}/functions/v1/docusign-sync-envelope`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ bookingId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(
        body?.error || res.statusText || "Sync failed"
      ) as Error & { details?: string };
      err.details = body?.details;
      throw err;
    }
    return body;
  };

  /**
   * Same cache key as TenantPaymentsTab — Financial Information must match Payment history after edits.
   * A separate payments query was stale vs tenant-payments and showed old booking rent.
   */
  const { data: detailDialogTenantPayments } = useQuery({
    queryKey: ["tenant-payments", selectedTenant?.userId, selectedTenant?.id],
    queryFn: async () => {
      if (!selectedTenant?.userId) return [];
      const { data, error } = await supabase
        .from("payments")
        .select(
          "id, booking_id, type, amount_cents, status, due_date, paid_at, stripe_payment_intent_id, stripe_charge_id, description, created_at"
        )
        .eq("user_id", selectedTenant.userId)
        .in("type", ["rent", "deposit"])
        .order("due_date", { ascending: false });

      if (error) throw error;
      const list = data || [];
      const bookingId =
        selectedTenant.id != null ? selectedTenant.id : undefined;
      if (bookingId !== undefined) {
        return list.filter(
          (p: any) => p.booking_id === bookingId || p.booking_id == null
        );
      }
      return list;
    },
    enabled: !!selectedTenant?.userId && !!detailDialogOpen,
  });

  const detailDialogCurrentMonthRentPayment = useMemo(() => {
    if (!detailDialogTenantPayments?.length || selectedTenant == null)
      return null;
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const bid = selectedTenant.id;
    const rents = (detailDialogTenantPayments as any[]).filter(
      (p) => p.type === "rent" && p.due_date
    );
    const matches = rents.filter((p) => {
      if (String(p.due_date).slice(0, 7) !== ym) return false;
      if (bid !== undefined && bid != null) {
        return p.booking_id === bid || p.booking_id == null;
      }
      return true;
    });
    if (matches.length === 0) return null;
    return matches.find((p: any) => p.booking_id === bid) ?? matches[0] ?? null;
  }, [detailDialogTenantPayments, selectedTenant?.id]);

  // Fetch all bookings with tenant, room, and property info
  const { data: bookings, isLoading } = useQuery({
    queryKey: ["admin-all-bookings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `
          *,
          rooms (
            id,
            title,
            price,
            deposit_amount,
            apartment_id,
            pickup_method,
            pickup_instructions,
            keynest_access_url,
            keynest_partner_shop_name,
            keynest_partner_shop_address,
            keynest_locker_number,
            keynest_pickup_code,
            keynest_access_code,
            keynest_key_label,
            keynest_opening_hours_weekdays,
            keynest_opening_hours_saturday,
            keynest_status_label,
            properties (
              id,
              title,
              address,
              street_name,
              street_number,
              zip_code,
              city,
              default_contract_template_id
            )
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  const bookingIdsSortedKey = useMemo(() => {
    if (!bookings?.length) return "";
    return [...bookings]
      .map((b: any) => b.id)
      .filter((id: any) => id != null)
      .sort((a: number, b: number) => a - b)
      .join(",");
  }, [bookings]);

  /**
   * Submitted handover protocols per booking — the journey signals the tenant
   * PROFILE derives from `useHandoverProtocolBundles`.
   *
   * The list used to pass `false` for both, which made the two surfaces disagree
   * whenever a protocol was the only evidence for a stage: the profile saw the
   * submitted protocol and showed Active (or Key return pending), the list saw
   * nothing and showed the protocol stage plus a spurious "Status ahead" chip.
   * One narrow query (ids + kind, submitted only) closes that for good — every
   * gate now gets identical inputs on both surfaces.
   *
   * Carries the filing timestamps as well as the kind, because "submitted" is
   * not on its own final: a reopen granted after a record was filed retires it
   * (`isSupersededByReopen`). The reopen stamp lives on the booking row, so the
   * comparison happens at the render site below where that row is in hand —
   * keeping it out of this query means a fresh reopen takes effect as soon as
   * the bookings query refetches, without needing the stamps in this key.
   */
  const {
    data: submittedProtocolsByBookingId = new Map<
      number,
      SubmittedProtocolLite[]
    >(),
  } = useQuery<Map<number, SubmittedProtocolLite[]>>({
    queryKey: ["admin-bookings-submitted-protocols", bookingIdsSortedKey],
    queryFn: async () => {
      const m = new Map<number, SubmittedProtocolLite[]>();
      if (!bookings?.length) return m;
      const ids = bookings
        .map((b: any) => b.id)
        .filter((id: any) => id != null);
      if (!ids.length) return m;
      // Step down on a MISSING COLUMN only, exactly as
      // `selectHandoverProtocols` does: an environment a push behind
      // 20260818100000 / 20260820150000 lacks the filing timestamps, and a
      // wholesale 42703 here would blank the journey stage for every row in
      // the list rather than just losing the supersede rule. A real error
      // still throws.
      const run = (cols: string) =>
        supabase
          .from("handover_protocols")
          .select(cols)
          .in("booking_id", ids)
          .eq("status", "submitted");
      let { data, error } = await run(
        "booking_id, kind, submitted_at, recorded_at, auto_submitted_at"
      );
      if (error && isMissingColumn(error)) {
        ({ data, error } = await run("booking_id, kind, submitted_at"));
      }
      if (error) throw error;
      for (const row of (data || []) as any[]) {
        const bid = Number(row?.booking_id);
        if (!Number.isFinite(bid)) continue;
        if (!m.has(bid)) m.set(bid, []);
        m.get(bid)!.push({
          kind: String(row?.kind ?? ""),
          status: "submitted",
          submitted_at: row?.submitted_at ?? null,
          recorded_at: row?.recorded_at ?? null,
          auto_submitted_at: row?.auto_submitted_at ?? null,
        });
      }
      return m;
    },
    enabled: !!bookings?.length,
  });

  /**
   * Deposit settlements for the visible bookings, so the list's journey stage
   * agrees with the profile's.
   *
   * Without this the resolver never sees `depositRefundSettled` and every
   * tenancy past key return parks at "Deposit refund pending" — including ones
   * the profile shows as Moved out. One narrow query keyed to the ids already
   * loaded closes that, exactly as the submitted-protocols query above does.
   *
   * A missing table (the settlement migration not applied here) yields an empty
   * map rather than throwing, which reads as "not settled" — the same
   * conservative default the resolver takes.
   */
  const {
    data: settlementsByBookingId = new Map<number, DepositSettlementLite>(),
  } = useQuery<Map<number, DepositSettlementLite>>({
    queryKey: ["admin-bookings-deposit-settlements", bookingIdsSortedKey],
    queryFn: async () => {
      const m = new Map<number, DepositSettlementLite>();
      if (!bookings?.length) return m;
      const ids = bookings
        .map((b: any) => b.id)
        .filter((id: any) => id != null);
      if (!ids.length) return m;
      const { data, error } = await supabase
        .from("deposit_settlements")
        .select(
          "booking_id, refund_paid_at, deposit_held_cents, amounts_snapshot_at"
        )
        .in("booking_id", ids);
      if (error) {
        if (isMissingTable(error)) return m;
        throw error;
      }
      for (const row of (data || []) as any[]) {
        const bid = Number(row?.booking_id);
        if (!Number.isFinite(bid)) continue;
        m.set(bid, {
          refund_paid_at: row?.refund_paid_at ?? null,
          deposit_held_cents: row?.deposit_held_cents ?? null,
          amounts_snapshot_at: row?.amounts_snapshot_at ?? null,
        });
      }
      return m;
    },
    enabled: !!bookings?.length,
  });

  /** Rent `payments` rows (admin-edited amounts) so the pipeline table matches Payments tab / headline rent. */
  const { data: adminBookingRentPayments } = useQuery({
    queryKey: ["admin-bookings-rent-payments", bookingIdsSortedKey],
    queryFn: async () => {
      if (!bookings?.length) return [];
      const ids = bookings
        .map((b: any) => b.id)
        .filter((id: any) => id != null);
      if (!ids.length) return [];
      const { data, error } = await supabase
        .from("payments")
        .select("booking_id, amount_cents, due_date")
        .in("booking_id", ids)
        .eq("type", "rent");
      if (error) throw error;
      return data || [];
    },
    enabled: !!bookings?.length,
  });

  /**
   * Financial status for list badges (Stripe or approved manual proof).
   * We keep the date-based `tenant.status` for filtering, but we display an extra payment-based label to avoid confusion.
   */
  const { data: adminBookingPaymentsForStatus } = useQuery<any[]>({
    queryKey: ["admin-bookings-payments-for-status", bookingIdsSortedKey],
    queryFn: async () => {
      if (!bookings?.length) return [];
      const ids = bookings
        .map((b: any) => b.id)
        .filter((id: any) => id != null);
      if (!ids.length) return [];
      const { data, error } = await supabase
        .from("payments")
        .select(
          "id, booking_id, type, status, stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by, amount_cents, due_date, paid_at, created_at"
        )
        .in("booking_id", ids)
        .in("type", ["rent", "deposit"])
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return data || [];
    },
    enabled: !!bookings?.length,
  });

  const adminPaymentIdsForStatus = useMemo(
    () =>
      (adminBookingPaymentsForStatus || [])
        .map((p: any) => p?.id)
        .filter(Boolean),
    [adminBookingPaymentsForStatus]
  );

  const { data: adminProofsByPaymentIdForStatus = {} } = useQuery<
    Record<string, any>
  >({
    queryKey: [
      "admin-bookings-payment-proofs-for-status",
      adminPaymentIdsForStatus.join(","),
    ],
    queryFn: async () => {
      if (!adminPaymentIdsForStatus.length) return {};
      const { data, error } = await supabase
        .from("payment_proofs")
        .select("payment_id, status, created_at")
        .in("payment_id", adminPaymentIdsForStatus)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const map: Record<string, any> = {};
      (data || []).forEach((row: any) => {
        if (row?.payment_id == null) return;
        // Keep newest proof per payment id
        if (map[String(row.payment_id)]) return;
        map[String(row.payment_id)] = row;
      });
      return map;
    },
    enabled: adminPaymentIdsForStatus.length > 0,
  });

  const adminPaymentsByBookingIdForStatus = useMemo(() => {
    const m = new Map<number, any[]>();
    for (const p of adminBookingPaymentsForStatus || []) {
      const bid = Number(p?.booking_id);
      if (!Number.isFinite(bid)) continue;
      if (!m.has(bid)) m.set(bid, []);
      m.get(bid)!.push(p);
    }
    return m;
  }, [adminBookingPaymentsForStatus]);

  const adminListRentByBookingId = useMemo(() => {
    const m = new Map<number, number>();
    if (!bookings?.length) return m;
    for (const b of bookings) {
      const bid = Number(b.id);
      if (!Number.isFinite(bid)) continue;
      const v = getFullMonthlyRentFromBooking(b);
      if (v > 0) m.set(bid, v);
    }
    return m;
  }, [bookings]);

  // Fetch all profiles
  const { data: profiles } = useQuery({
    queryKey: ["admin-all-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch screening status
  const { data: screeningData } = useQuery({
    queryKey: ["admin-screening"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_screening")
        .select("*");

      if (error) throw error;
      return data || [];
    },
  });

  // Build tenant list with comprehensive data - includes ALL bookings (with or without user_id)
  const tenants = useMemo(() => {
    if (!bookings) return [];
    if (!profiles) return []; // Wait for profiles to load

    const profileMap = new Map(profiles.map((p: any) => [p.id, p]));
    const screeningMap = new Map(
      (screeningData || []).map((s: any) => [`${s.user_id}-${s.booking_id}`, s])
    );

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return (
      bookings
        // Show ALL bookings, including those without user_id (guest bookings)
        .map((booking: any) => {
          const profile = booking.user_id
            ? profileMap.get(booking.user_id)
            : null;
          const room = booking.rooms;
          const property = room?.properties;
          const screening = booking.user_id
            ? screeningMap.get(`${booking.user_id}-${booking.id}`)
            : null;

          const startDate = booking.start_date
            ? new Date(booking.start_date)
            : null;
          const endDate = booking.end_date ? new Date(booking.end_date) : null;

          let status: "active" | "past" | "future" | "pending" = "pending";
          if (booking.status === "pending") {
            status = "pending";
          } else if (booking.status === "cancelled") {
            status = "past";
          } else if (startDate && endDate) {
            if (startDate > now) {
              status = "future";
            } else if (endDate < now) {
              status = "past";
            } else {
              status = "active";
            }
          } else if (booking.status === "confirmed") {
            status = startDate && startDate <= now ? "active" : "future";
          }

          const proofFor = (paymentId: string) =>
            adminProofsByPaymentIdForStatus[paymentId] ?? null;
          const isPaymentEffectivelyPaidForStatus = (p: any): boolean =>
            isPaymentEffectivelyPaid(p, proofFor);

          const bookingPayments =
            adminPaymentsByBookingIdForStatus.get(Number(booking.id)) || [];
          // "Some deposit row is paid" is NOT the same as "the deposit is
          // settled" — a part-payment would clear the journey's deposit gate.
          // Use the canonical full-deposit resolver, the same one the tenant
          // profile uses, so both surfaces agree on the stage.
          const depositPaidForBooking = isDepositFullyPaid({
            booking: booking as any,
            payments: bookingPayments,
            proofFor,
          });
          const rentPaidForBooking = bookingPayments.some(
            (p: any) =>
              p?.type === "rent" && isPaymentEffectivelyPaidForStatus(p)
          );
          const leasePipelineStatus = resolveLeasePipelineStatus(
            booking,
            depositPaidForBooking
          );
          // New 10-stage journey (feedback #2) drives the Status column + filter.
          // Every signal here is the same one the tenant profile computes — the
          // canonical full-deposit resolver plus the submitted-protocol flags —
          // so the two surfaces cannot land on different stages for the same row.
          const submittedProtocols =
            submittedProtocolsByBookingId.get(Number(booking.id)) ?? [];
          /**
           * Does a submitted record for this kind still stand?
           *
           * A reopen granted after the record was filed retires it, so the
           * tenancy shows the protocol stage again — the same reading the
           * tenant's own card and the tenant profile take. Without this the
           * list would park a reopened tenancy at Active while both other
           * surfaces show the walkthrough as outstanding.
           */
          const hasStandingProtocol = (kind: "move_in" | "move_out") => {
            const reopenedAt =
              (kind === "move_in"
                ? (booking as any)?.move_in_protocol_reopened_at
                : (booking as any)?.move_out_protocol_reopened_at) ?? null;
            return submittedProtocols.some(
              (p) => p.kind === kind && !isSupersededByReopen(p, reopenedAt)
            );
          };
          const settlement = settlementsByBookingId.get(Number(booking.id));
          // Assembled by the shared helper, so this list, the tenant profile
          // and the room view feed the resolver byte-identical signals. The
          // submitted-kind set is reopen-aware: a record a reopen has
          // superseded does not count as "submitted" here either, matching
          // the tenant's own card and the tenant profile.
          const submittedKinds = new Set<string>(
            (["move_in", "move_out"] as const).filter((kind) =>
              hasStandingProtocol(kind)
            )
          );
          const journey = resolveTenantJourneyFromSources(booking, {
            payments: bookingPayments,
            proofFor,
            submittedProtocolKinds: submittedKinds,
            settlement: settlement ?? null,
          });
          const journeyStageId = journey.stageId;
          // Distinguish a cancelled/terminated deal (never a tenant) from a
          // genuine move-out — both derive to the "Moved out" stage otherwise.
          const journeyTerminalStatus = journey.terminalStatus;

          const manualContractUpload = !!booking.manual_contract_upload;
          const fullMonthlyRent = getFullMonthlyRentFromBooking(booking);
          const moveInIso = booking.move_in_date || booking.start_date;
          let rentDisp: { amount: number; isPartialFirstMonth: boolean } = {
            amount: Number(fullMonthlyRent) || 0,
            isPartialFirstMonth: false,
          };
          try {
            const computed = getDisplayedRentEUR({
              fullMonthlyRent,
              moveInIso,
              firstMonthOverride: booking.first_month_rent_override ?? null,
              asOfIsoDate: new Date().toISOString().slice(0, 10),
            });
            rentDisp = {
              amount: Number(computed?.amount) || Number(fullMonthlyRent) || 0,
              isPartialFirstMonth: !!computed?.isPartialFirstMonth,
            };
          } catch (error) {
            console.error(
              "Failed to compute displayed rent for booking",
              booking?.id,
              error
            );
          }

          return {
            id: booking.id,
            userId: booking.user_id,
            booking,
            profile,
            room,
            property,
            screening,
            status,
            leasePipelineStatus,
            journeyStageId,
            journeyTerminalStatus,
            journeyStatusAhead: journey.statusAhead,
            journeyClaimedStageId: journey.claimedStageId,
            journeyBlockedRequirement: journey.blockedBy?.requirement ?? null,
            startDate: booking.start_date,
            endDate: booking.end_date,
            moveInDate: booking.move_in_date || booking.start_date,
            moveOutDate: booking.move_out_date || booking.end_date,
            monthlyRent: fullMonthlyRent,
            firstMonthRentOverride: booking.first_month_rent_override ?? null,
            displayedRent: rentDisp.amount,
            isPartialFirstMonthRent: rentDisp.isPartialFirstMonth,
            depositAmount: booking.deposit_amount || 0,
            contractSigned: isBookingContractSigned(booking),
            contractStatus: booking.contract_status || "not_ready",
            contractVersion: booking.contract_version ?? 1,
            contractPdfUpdatedAt: booking.contract_pdf_updated_at ?? null,
            contractPdfUrl: booking.contract_pdf_url,
            signedContractUrl: booking.signed_contract_url,
            docusignEnvelopeId: booking.docusign_envelope_id,
            docusignSigningUrl: booking.docusign_signing_url,
            adminArchivedAt: booking.admin_archived_at ?? null,
            adminArchiveReason: booking.admin_archive_reason ?? null,
            manualContractUpload,
            showManualContractLabel: computeShowManualContractLabel(booking),
            manuallyMarkedPaid: !!booking.manually_marked_paid,
            depositPaid: depositPaidForBooking,
            rentPaid: rentPaidForBooking,
            // Guest booking info (from booking fields when no user_id)
            guestName: booking.name || null,
            guestEmail: booking.email || null,
            guestPhone: booking.phone || null,
          };
        })
    );
  }, [
    bookings,
    profiles,
    screeningData,
    adminPaymentsByBookingIdForStatus,
    adminProofsByPaymentIdForStatus,
    submittedProtocolsByBookingId,
    settlementsByBookingId,
  ]);

  // Dynamically-added test emails (Directory-managed), combined with the
  // static domain/seed rules in isTestAccount().
  const { data: testAccountFilters } = useTestAccountFilters();
  const dynamicTestEmails = useMemo(
    () => (testAccountFilters ?? []).map((f) => f.email),
    [testAccountFilters]
  );

  // Test/CoLiviq accounts hidden unless the toggle is on — this feeds both
  // the search/stage filter and the per-status counts below, so counts,
  // pagination and search results all agree on what's excluded.
  const visibleTenants = useMemo(() => {
    if (includeTestAccounts) return tenants;
    return tenants.filter(
      (tenant) =>
        !isTestAccount(
          tenant.profile?.email || tenant.guestEmail,
          dynamicTestEmails
        )
    );
  }, [tenants, includeTestAccounts, dynamicTestEmails]);

  const testAccountCount = tenants.length - visibleTenants.length;

  // Filter tenants by search + list bucket (All / Active / Pending / Archived)
  const filteredTenants = useMemo(() => {
    // Parsed once per search: "+49 151…", "0049…" and "0151…" all find the
    // same tenant, whether the phone sits on the profile or the booking.
    const phoneQuery = parsePhoneQuery(search);
    return visibleTenants.filter((tenant) => {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        tenant.profile?.name?.toLowerCase().includes(searchLower) ||
        tenant.profile?.email?.toLowerCase().includes(searchLower) ||
        tenant.guestName?.toLowerCase().includes(searchLower) ||
        tenant.guestEmail?.toLowerCase().includes(searchLower) ||
        tenant.property?.title?.toLowerCase().includes(searchLower) ||
        tenant.room?.title?.toLowerCase().includes(searchLower) ||
        phoneMatchesQuery(tenant.profile?.phone, phoneQuery) ||
        phoneMatchesQuery(tenant.guestPhone, phoneQuery);

      const archived = !!tenant.adminArchivedAt;

      // Single status filter over the 10 journey stages (feedback #9):
      //  • "archived" → admin-archived bookings only
      //  • "all"      → everything, archived included, so search finds it all
      //  • a stage    → that journey stage, excluding archived
      // Cancelled/terminated deals derive to the "Moved out" stage but never
      // became tenants, so they're kept out of the "Moved out" stage filter —
      // they surface only under "all" (with their own distinct badge).
      const isTerminalNonMoveout =
        tenant.journeyTerminalStatus === "cancelled" ||
        tenant.journeyTerminalStatus === "terminated";
      let inFilter: boolean;
      if (stageFilter === "archived") inFilter = archived;
      else if (stageFilter === "all") inFilter = true;
      else
        inFilter =
          !archived &&
          tenant.journeyStageId === stageFilter &&
          !isTerminalNonMoveout;

      return matchesSearch && inFilter;
    });
  }, [visibleTenants, search, stageFilter]);

  // Per-filter counts for the status dropdown, using the SAME predicates as the
  // filter above (search is ignored — these are the totals in each bucket).
  // Counts are per application/booking, matching the journey-stage filter.
  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0, archived: 0 };
    for (const s of TENANT_JOURNEY_STAGES) counts[s.id] = 0;
    for (const t of visibleTenants) {
      counts.all += 1;
      if (t.adminArchivedAt) {
        counts.archived += 1;
        continue;
      }
      const isTerminalNonMoveout =
        t.journeyTerminalStatus === "cancelled" ||
        t.journeyTerminalStatus === "terminated";
      if (!isTerminalNonMoveout && counts[t.journeyStageId] != null) {
        counts[t.journeyStageId] += 1;
      }
    }
    return counts;
  }, [visibleTenants]);

  // Group the flat booking list so every room application from the same
  // account nests under one tenant, then by property inside that tenant.
  // Identity key: the account user_id, falling back to guest email.
  const groupedTenants = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; first: any; applications: any[] }
    >();
    const order: string[] = [];
    for (const t of filteredTenants) {
      const email = (t.guestEmail || t.profile?.email || "")
        .toString()
        .toLowerCase()
        .trim();
      const key = t.userId
        ? `u:${t.userId}`
        : email
          ? `g:${email}`
          : `b:${t.id}`;
      if (!groups.has(key)) {
        groups.set(key, { key, first: t, applications: [] });
        order.push(key);
      }
      groups.get(key)!.applications.push(t);
    }
    return order.map((key) => {
      const g = groups.get(key)!;
      const propMap = new Map<
        string,
        { propertyId: any; propertyTitle: string; rooms: any[] }
      >();
      const propOrder: string[] = [];
      for (const app of g.applications) {
        const pid = String(app.property?.id ?? "none");
        if (!propMap.has(pid)) {
          propMap.set(pid, {
            propertyId: app.property?.id,
            propertyTitle: app.property?.title || "N/A",
            rooms: [],
          });
          propOrder.push(pid);
        }
        propMap.get(pid)!.rooms.push(app);
      }
      return {
        key,
        first: g.first,
        applications: g.applications,
        properties: propOrder.map((pid) => propMap.get(pid)!),
      };
    });
  }, [filteredTenants]);

  // Paginate over tenant groups (one page slot per tenant, not per booking).
  const totalPages = Math.ceil(groupedTenants.length / itemsPerPage);
  const paginatedGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return groupedTenants.slice(startIndex, endIndex);
  }, [groupedTenants, currentPage, itemsPerPage]);

  // Renders one booking (room application) as a table row nested under a
  // tenant header. The row shares a left "containment rail" + tint so the
  // tenant and its rooms read as one unit; the identity lives in the header,
  // so the first cell is just the rail/indent and the second carries the room.
  // `highlight` tints rows that match an active search.
  // Shared by the per-application row and the no-fold single-application
  // header row. The header row hides the manual-contract badge via
  // `showManualContract: false` — that pill stays visible only in the
  // expanded per-application view, not the flat overview row.
  const renderManualFlags = (
    tenant: any,
    { showManualContract = true }: { showManualContract?: boolean } = {}
  ) =>
    ((showManualContract && tenant.showManualContractLabel) ||
      tenant.manuallyMarkedPaid) && (
      <div className="flex flex-wrap gap-1.5">
        {showManualContract && tenant.showManualContractLabel && (
          <Badge
            variant="outline"
            className="h-5 rounded-full border-teal-200 bg-teal-500/10 px-2 text-[10px] font-semibold uppercase tracking-wide text-teal-800 dark:border-teal-800 dark:bg-teal-500/15 dark:text-teal-200"
          >
            Manual contract
          </Badge>
        )}
        {tenant.manuallyMarkedPaid && (
          <Badge
            variant="outline"
            className="h-5 rounded-full border-violet-200 bg-violet-500/10 px-2 text-[10px] font-semibold uppercase tracking-wide text-violet-800 dark:border-violet-800 dark:bg-violet-500/15 dark:text-violet-200"
          >
            Manually paid
          </Badge>
        )}
      </div>
    );

  const renderTenantRow = (tenant: any, highlight = false) => (
    <TableRow
      key={tenant.id}
      onClick={() => handleViewDetails(tenant)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleViewDetails(tenant);
        }
      }}
      tabIndex={0}
      className={`cursor-pointer border-border/40 transition-colors hover:bg-muted/30 ${
        highlight ? "bg-primary/[0.07]" : "bg-muted/[0.18]"
      }`}
    >
      <TableCell className="border-l-2 border-primary/40 py-5 pl-10 align-top">
        <span className="sr-only">
          {t("admin.tenants.roomApplicationSrOnly", "Room application")}
        </span>
      </TableCell>
      <TableCell className="py-5 align-top">
        <span className="truncate text-[13px] font-medium text-foreground/90">
          {tenant.property?.title || "N/A"}
        </span>
      </TableCell>
      <TableCell className="py-5 align-top">
        <div className="min-w-0 space-y-1.5">
          {renderManualFlags(tenant)}
          <span className="truncate text-[13px] font-semibold">
            {tenant.room?.title || "N/A"}
          </span>
        </div>
      </TableCell>
      <TableCell className="py-5 align-top">
        <div className="flex flex-col items-start gap-1.5">
          <TenantJourneyStageBadge
            stageId={tenant.journeyStageId}
            terminalStatus={tenant.journeyTerminalStatus}
            archived={!!tenant.adminArchivedAt}
            archiveReason={tenant.adminArchiveReason}
          />
        </div>
      </TableCell>
      <TableCell
        className="py-5 text-right align-top whitespace-nowrap"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Combined actions: tenant-lifecycle (Approve/Reassign/Act as/
            Archive) + account management (login link/passwords/deactivate/
            delete), the latter hidden for guest bookings with no account. */}
        <TenantRowActions
          tenant={tenant}
          isSuperAdmin={isSuperAdmin}
          canManageAccount={canManageTenant}
          canActAs={canActAs}
          currentUserId={currentAuthUser?.id ?? null}
          onApprove={(id) => approveBookingMutation.mutate(id)}
          approving={approveBookingMutation.isPending}
          onReassign={setReassignFor}
          onActAsTenant={actAsTenant}
          onArchive={(p) => archiveBookingMutation.mutate(p)}
          archiving={archiveBookingMutation.isPending}
        />
      </TableCell>
    </TableRow>
  );

  // Reset to page 1 when filters change. dynamicTestEmails.length covers the
  // (rare) case where the test-account list finishes loading after the page
  // was already on a later page than the now-smaller result set supports.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, stageFilter, includeTestAccounts, dynamicTestEmails.length]);

  useEffect(() => {
    if (!rentEditOpen || !selectedTenant) return;
    setEditMonthlyRent(
      selectedTenant.monthlyRent != null && selectedTenant.monthlyRent !== ""
        ? String(selectedTenant.monthlyRent)
        : ""
    );
    setEditFirstMonthOverride(
      selectedTenant.firstMonthRentOverride != null
        ? String(selectedTenant.firstMonthRentOverride)
        : ""
    );
  }, [rentEditOpen, selectedTenant]);

  const parseEurInput = (raw: string): number | null => {
    const s = String(raw ?? "")
      .trim()
      .replace(",", ".");
    if (!s) return null;
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n * 100) / 100;
  };

  const computeDisplayedRentForSelectedTenant = (
    nextMonthlyRent: number,
    nextFirstMonthOverride: number | null
  ) => {
    const moveInIso =
      selectedTenant?.moveInDate ||
      selectedTenant?.booking?.move_in_date ||
      selectedTenant?.booking?.start_date ||
      null;
    const rentDisp = getDisplayedRentEUR({
      fullMonthlyRent: nextMonthlyRent,
      moveInIso,
      firstMonthOverride: nextFirstMonthOverride,
      asOfIsoDate: new Date().toISOString().slice(0, 10),
    });
    return rentDisp;
  };

  const handleSaveRent = async () => {
    if (!selectedTenant?.id) return;
    const nextMonthlyRent = parseEurInput(editMonthlyRent);
    if (nextMonthlyRent == null) {
      toast.error(
        t(
          "admin.tenants.invalidMonthlyRent",
          "Please enter a valid monthly rent amount."
        )
      );
      return;
    }
    const overrideParsed = parseEurInput(editFirstMonthOverride);
    const nextFirstMonthOverride = editFirstMonthOverride.trim()
      ? overrideParsed
      : null;
    if (editFirstMonthOverride.trim() && nextFirstMonthOverride == null) {
      toast.error(
        t(
          "admin.tenants.invalidFirstMonthOverride",
          "Please enter a valid first-month override amount (or leave it empty for auto pro\u2011rata)."
        )
      );
      return;
    }

    setSavingRent(true);
    try {
      // Deposit is contractually fixed at signing — a rent edit must not
      // rewrite bookings.deposit_amount.
      const { error } = await supabase
        .from("bookings")
        .update({
          monthly_rent: nextMonthlyRent,
          first_month_rent_override: nextFirstMonthOverride,
        })
        .eq("id", selectedTenant.id);
      if (error) throw error;

      const roomId = await resolveRoomIdForBookingTenant(
        supabase,
        selectedTenant,
        selectedTenant.id
      );
      if (roomId != null) {
        const { error: roomErr } = await supabase
          .from("rooms")
          .update({ price: nextMonthlyRent })
          .eq("id", roomId);
        if (roomErr) throw roomErr;
      }

      // Keep unpaid rent `payments` rows in sync with booking terms (pro‑rata first month + override).
      const moveInIso =
        selectedTenant.moveInDate ||
        selectedTenant.startDate ||
        selectedTenant.booking?.move_in_date ||
        selectedTenant.booking?.start_date ||
        null;
      const { data: rentRowsToSync, error: syncFetchErr } = await supabase
        .from("payments")
        .select("id, due_date, amount_cents")
        .eq("booking_id", selectedTenant.id)
        .eq("type", "rent")
        .in("status", ["pending", "scheduled", "overdue", "failed"]);
      if (syncFetchErr) throw syncFetchErr;
      // repriceRentRows, not a raw base-rent recompute: a tenant with an
      // active discount must stay discounted after this contract-level rent
      // edit, not get bumped back to the full undiscounted amount (COL-360).
      const repriced = repriceRentRows({
        rows: rentRowsToSync || [],
        fullMonthlyRent: nextMonthlyRent,
        moveInIso,
        firstMonthOverride: nextFirstMonthOverride,
        discount: selectedTenant?.booking,
      });
      for (const row of repriced) {
        const { error: syncUpdErr } = await supabase
          .from("payments")
          .update({ amount_cents: row.amountCents })
          .eq("id", row.id);
        if (syncUpdErr) throw syncUpdErr;
      }

      // Refresh bookings so rent is consistent everywhere.
      await invalidateOccupancyViews(queryClient);
      await queryClient.invalidateQueries({
        queryKey: ["admin-bookings-rent-payments"],
      });
      await queryClient.invalidateQueries({ queryKey: ["admin-bookings"] });
      await queryClient.invalidateQueries({
        queryKey: ["admin-analytics-bookings"],
      });
      await queryClient.invalidateQueries({ queryKey: ["active-booking"] });
      await queryClient.invalidateQueries({ queryKey: ["approvedBookings"] });
      await queryClient.invalidateQueries({ queryKey: ["allBookings"] });
      await queryClient.invalidateQueries({ queryKey: ["payments-upcoming"] });
      if (selectedTenant.userId) {
        await queryClient.invalidateQueries({
          queryKey: ["payments", selectedTenant.userId],
        });
        await queryClient.invalidateQueries({
          queryKey: [
            "tenant-payments",
            selectedTenant.userId,
            selectedTenant.id,
          ],
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
      await queryClient.invalidateQueries({ queryKey: ["rooms"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-all-rooms"] });
      await queryClient.invalidateQueries({
        queryKey: ["user-properties-display-rent"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["booking-rent-payments-pricing"],
      });
      await queryClient.invalidateQueries({ queryKey: ["booking-for-room"] });
      // This edit changes both sides of the properties-grid pill: the
      // expected rent (monthly_rent/first_month_rent_override) and the
      // repriced payments rows. Also feeds the owners-list payout-readiness
      // tile (COL-461).
      await queryClient.invalidateQueries({
        queryKey: [PROPERTY_RENT_PAID_STATS_KEY],
      });
      invalidateOwnerCaches(queryClient);

      const rentDisp = computeDisplayedRentForSelectedTenant(
        nextMonthlyRent,
        nextFirstMonthOverride
      );
      setSelectedTenant((prev: any) =>
        prev?.id
          ? {
              ...prev,
              monthlyRent: nextMonthlyRent,
              firstMonthRentOverride: nextFirstMonthOverride,
              displayedRent: rentDisp.amount,
              isPartialFirstMonthRent: rentDisp.isPartialFirstMonth,
              booking: {
                ...prev.booking,
                monthly_rent: nextMonthlyRent,
                first_month_rent_override: nextFirstMonthOverride,
              },
              room: prev.room
                ? { ...prev.room, price: nextMonthlyRent }
                : prev.room,
            }
          : prev
      );

      toast.success(t("admin.tenants.rentUpdatedToast", "Rent updated."));
      setRentEditOpen(false);
    } catch (e: any) {
      toast.error(
        e?.message ||
          t("admin.tenants.updateRentFailed", "Failed to update rent.")
      );
    } finally {
      setSavingRent(false);
    }
  };

  // Open tenant profile when navigated from Properties (e.g. /admin?tab=tenants&bookingId=123)
  useEffect(() => {
    const bookingId = urlBookingId ? Number(urlBookingId) : null;
    if (
      bookingId == null ||
      !Number.isInteger(bookingId) ||
      tenants.length === 0
    )
      return;
    const tenant = tenants.find((t) => Number(t.id) === bookingId);
    if (tenant) {
      setSelectedTenant(tenant);
      setDetailDialogOpen(true);
    }
    onClearBookingIdFromUrl?.();
  }, [urlBookingId, tenants, onClearBookingIdFromUrl]);

  /**
   * Re-point the open profile at the freshly fetched row.
   *
   * `selectedTenant` is a SNAPSHOT taken when the row was clicked, and nothing
   * used to replace it: an edit invalidated the queries, `tenants` rebuilt with
   * the new values, and the modal carried on rendering the object captured
   * before the save. TenantProfileDialog papers over the worst of it by reading
   * the booking and profile rows live, but everything else on the snapshot —
   * room, property, screening, and every field the list derives — stayed at its
   * click-time value until a page reload. That is the "only reflected after I
   * refreshed" report.
   *
   * `tenants` is a useMemo over the query data, so this fires when a refetch
   * lands, not on every render. The fresh row wins outright: the optimistic
   * `setSelectedTenant` patches elsewhere in this file exist to bridge the gap
   * until exactly this moment, and the refetch they were anticipating carries
   * the same values. A row that has disappeared (booking deleted, filtered out
   * server-side) keeps the snapshot rather than blanking the open modal.
   *
   * Deliberately depends on `tenants` ALONE, and reads the current selection
   * through the updater rather than the closure. Depending on `selectedTenant`
   * too would fire this on every optimistic patch — finding the not-yet-
   * refetched list row and instantly reverting the patch, which is the very
   * flicker those patches exist to prevent.
   */
  useEffect(() => {
    setSelectedTenant((prev: any) => {
      if (prev?.id == null) return prev;
      return tenants.find((t) => t.id === prev.id) ?? prev;
    });
  }, [tenants]);

  // Always remove bookingId from URL (e.g. in deployment when tenants load slowly)
  useEffect(() => {
    if (!urlBookingId || !onClearBookingIdFromUrl) return;
    const t = setTimeout(() => {
      onClearBookingIdFromUrl();
    }, 1500);
    return () => clearTimeout(t);
  }, [urlBookingId, onClearBookingIdFromUrl]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const exportToCSV = () => {
    if (filteredTenants.length === 0) {
      toast.error(t("admin.tenants.noDataToExport", "No data to export"));
      return;
    }

    // Define CSV headers
    const headers = [
      "Tenant Name",
      "Email",
      "Phone",
      "Property",
      "Room",
      "Lifecycle status",
      "Archived",
      "Manual contract upload",
      "Manually paid",
      "Move-In Date",
      "Move-Out Date",
      "Monthly Rent (€)",
      "Deposit Amount (€)",
      "Lease status",
      "Booking ID",
      "User ID",
      "Guest Booking",
      // Appended (not inserted) so existing column positions stay stable for
      // anything parsing this export. "Journey stage" is what the Status column
      // in the UI shows — "Lease status" above is the legacy pipeline value,
      // which honours a raw bookings.status = active even when the journey's
      // prerequisites aren't fulfilled. Exporting both makes that gap visible
      // rather than letting the CSV claim "Active" on its own.
      "Journey stage",
      "Status ahead of conditions",
    ];

    // Convert tenant data to CSV rows
    const rows = filteredTenants.map((tenant) => {
      const escapeCSV = (value: string | null | undefined) => {
        if (value === null || value === undefined) return "";
        const str = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return [
        escapeCSV(tenant.profile?.name || tenant.guestName || "N/A"),
        escapeCSV(tenant.profile?.email || tenant.guestEmail || "N/A"),
        escapeCSV(tenant.profile?.phone || tenant.guestPhone || "N/A"),
        escapeCSV(tenant.property?.title || "N/A"),
        escapeCSV(tenant.room?.title || "N/A"),
        escapeCSV(tenant.status || "N/A"),
        escapeCSV(tenant.adminArchivedAt ? "Yes" : "No"),
        escapeCSV(tenant.showManualContractLabel ? "Yes" : "No"),
        escapeCSV(tenant.manuallyMarkedPaid ? "Yes" : "No"),
        escapeCSV(tenant.moveInDate ? formatDate(tenant.moveInDate) : "N/A"),
        escapeCSV(tenant.moveOutDate ? formatDate(tenant.moveOutDate) : "N/A"),
        escapeCSV(
          Number(
            adminListRentByBookingId.get(Number(tenant.id)) ??
              tenant.monthlyRent
          ).toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        ),
        escapeCSV(
          Number(tenant.depositAmount).toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        ),
        escapeCSV(
          // CSV export is a stable, language-independent artifact (external
          // tooling parses these columns) — use the raw English label, not
          // the current admin's UI language.
          (
            LEASE_PIPELINE_STATUS_STYLES[tenant.leasePipelineStatus] ??
            LEASE_PIPELINE_STATUS_STYLES.signature_pending
          ).label
        ),
        escapeCSV(tenant.id?.toString() || "N/A"),
        escapeCSV(tenant.userId?.toString() || "N/A"),
        escapeCSV(!tenant.userId ? "Yes" : "No"),
        escapeCSV(
          tenant.journeyTerminalStatus === "cancelled"
            ? "Cancelled"
            : tenant.journeyTerminalStatus === "terminated"
              ? "Terminated"
              : tenantJourneyStageMeta(tenant.journeyStageId).label
        ),
        escapeCSV(
          tenant.journeyStatusAhead
            ? `Yes — missing: ${tenant.journeyBlockedRequirement ?? "an earlier step"}`
            : "No"
        ),
      ].join(",");
    });

    // Combine headers and rows
    const csvContent = [headers.join(","), ...rows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `tenants-export-${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(
      `${t("admin.tenants.exportedPrefix", "Exported")} ${filteredTenants.length} ${t("admin.tenants.tenantsUnitParen", "tenant(s)")} ${t("admin.tenants.toCsvSuffix", "to CSV")}`
    );
  };

  const handleViewDetails = (tenant: any) => {
    setSelectedTenant(tenant);
    setDetailDialogOpen(true);
  };

  // Approving prepares the contract as a DRAFT (approveBookingForContract).
  // This used to generate AND email the tenant a signing link off the back of
  // an approval click, which is the accidental send the draft flow exists to
  // stop. The document is ready for review; a human still has to verify it
  // and press send on the tenant's profile.
  const approveBookingMutation = useMutation({
    mutationFn: approveBookingForContract,
    // Runs after the draft attempt too, so these refetches see its contract.
    onSuccess: (result, bookingId) => {
      invalidateOccupancyViews(queryClient);
      toast.success(
        t("admin.tenants.bookingApprovedToast", "Booking approved")
      );

      if (result.draftCreated) {
        toast.success(
          t(
            "admin.tenants.bookingApprovedDraftCreated",
            "Booking approved. Contract draft created — not sent."
          ),
          {
            description: t(
              "admin.tenants.bookingApprovedDraftCreatedDescription",
              "Open the tenant's profile \u2192 Contract to read the draft, verify it, then send it to the tenant."
            ),
            duration: 8000,
          }
        );
      } else {
        console.error("Contract generation error:", result.error);
        const { title, description } = formatContractEdgeError(result.error, t);
        toast.warning(
          t(
            "admin.tenants.bookingApprovedContractGenFailed",
            "Booking approved, but contract generation failed. You can generate it manually."
          ),
          {
            description: description ? `${title}\n${description}` : title,
            duration: 12000,
          }
        );
      }
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t("admin.tenants.approveBookingFailed", "Failed to approve booking")
      );
    },
  });

  const archiveBookingMutation = useMutation({
    mutationFn: async ({ id, archive }: { id: number; archive: boolean }) => {
      const { error } = await supabase
        .from("bookings")
        .update({
          admin_archived_at: archive ? new Date().toISOString() : null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { archive }) => {
      invalidateOccupancyViews(queryClient);
      toast.success(
        archive
          ? t("admin.tenants.movedToArchive", "Moved to archive")
          : t("admin.tenants.restoredToPipeline", "Restored to pipeline")
      );
      const ts = archive ? new Date().toISOString() : null;
      setSelectedTenant((prev: any) =>
        prev
          ? {
              ...prev,
              adminArchivedAt: ts,
              // NULL either way: archiving from this switch is a person's
              // decision, never a system one, and un-archiving drops the
              // reason in the DB as well (clear_archive_reason_on_unarchive).
              adminArchiveReason: null,
              booking: {
                ...prev.booking,
                admin_archived_at: ts,
                admin_archive_reason: null,
              },
            }
          : null
      );
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t(
            "admin.tenants.archiveStatusFailed",
            "Could not update archive status"
          )
      );
    },
  });

  useEffect(() => {
    const url = selectedTenant?.room?.keynest_access_url;
    setKeynestAccessUrl(url ? String(url) : "");
  }, [selectedTenant?.id]);

  // Reset super admin edit form whenever a different tenant is selected
  useEffect(() => {
    if (!selectedTenant) return;
    setEditName(selectedTenant.profile?.name || selectedTenant.guestName || "");
    setEditEmail(
      selectedTenant.profile?.email || selectedTenant.guestEmail || ""
    );
    setEditPhone(
      selectedTenant.profile?.phone || selectedTenant.guestPhone || ""
    );
    setEditBookingStatus(
      selectedTenant.booking?.status || selectedTenant.status || ""
    );
    setEditContractStatus(
      selectedTenant.contractStatus ||
        selectedTenant.booking?.contract_status ||
        ""
    );
    setEditPaymentStatus(selectedTenant.booking?.payment_status || "");
    setEditMoveInDate(
      selectedTenant.booking?.move_in_date || selectedTenant.moveInDate || ""
    );
    setEditMoveOutDate(
      selectedTenant.booking?.move_out_date || selectedTenant.moveOutDate || ""
    );
  }, [selectedTenant?.id]);

  const superAdminUpdateMutation = useMutation({
    mutationFn: async ({
      bookingId,
      userId,
      bookingPatch,
      profilePatch,
    }: {
      bookingId: number;
      userId?: string | null;
      bookingPatch: {
        status?: string;
        contract_status?: string;
        payment_status?: string;
        move_in_date?: string | null;
        move_out_date?: string | null;
        name?: string;
        email?: string;
        phone?: string;
      };
      profilePatch?: {
        name?: string;
        full_name?: string;
        email?: string;
        phone?: string;
      };
    }) => {
      if (Object.keys(bookingPatch).length > 0) {
        // The Reminders views ignore the stored `payment_status` and recompute
        // overdue from `last_payment_received_at` / `manually_marked_paid`. So
        // setting status → "paid" here must also write the reminder signal,
        // otherwise the tenant stays on the overdue list. Mirror the exact
        // convention the Reminders "Mark paid" action uses (see markPaid in
        // AdminReminders): clear the stage and accrued fees too.
        const patchToApply: Record<string, unknown> = { ...bookingPatch };
        if (bookingPatch.payment_status === "paid") {
          // Ledger first (COL-227): the payments table is the source of truth
          // every payment surface reads — booking columns are derived signals.
          await recordBookingPaidInLedger(
            bookingId,
            "Marked paid by super-admin from the tenant edit form."
          );
          patchToApply.last_payment_received_at = new Date().toISOString();
          patchToApply.reminder_stage = 0;
          patchToApply.accumulated_fees_eur = 0;
        }
        const { error } = await supabase
          .from("bookings")
          .update(patchToApply)
          .eq("id", bookingId);
        if (error) throw error;
      }
      if (userId && profilePatch && Object.keys(profilePatch).length > 0) {
        const { error } = await supabase
          .from("profiles")
          .update(profilePatch)
          .eq("id", userId);
        if (error) throw error;
      }
    },
    onSuccess: (_, { bookingId, userId, bookingPatch, profilePatch }) => {
      toast.success(
        t("admin.tenants.tenantUpdatedToast", "Tenant updated successfully.")
      );
      invalidateOccupancyViews(queryClient);
      queryClient.invalidateQueries({ queryKey: ["admin-all-profiles"] });
      if (bookingPatch.payment_status === "paid") {
        // A ledger row (+ attestation proof) was recorded — refresh the
        // payment surfaces too.
        queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
        queryClient.invalidateQueries({ queryKey: ["payments"] });
        queryClient.invalidateQueries({
          queryKey: ["v2", "admin-tenant-payments"],
        });
        queryClient.invalidateQueries({
          queryKey: ["admin-analytics-payments"],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["v2", "bookings", bookingId],
      });
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["v2", "profile", userId] });
      }
      setSelectedTenant((prev: any) => {
        if (!prev) return prev;
        const updatedBooking = { ...prev.booking, ...bookingPatch };
        // Recompute the pipeline status badge so the header updates immediately
        const newLeasePipelineStatus = resolveLeasePipelineStatus(
          updatedBooking,
          !!prev.manuallyMarkedPaid
        );
        return {
          ...prev,
          booking: updatedBooking,
          leasePipelineStatus: newLeasePipelineStatus,
          profile: profilePatch
            ? {
                ...prev.profile,
                name: profilePatch.name ?? prev.profile?.name,
                full_name: profilePatch.full_name ?? prev.profile?.full_name,
                email: profilePatch.email ?? prev.profile?.email,
                phone: profilePatch.phone ?? prev.profile?.phone,
              }
            : prev.profile,
          contractStatus: bookingPatch.contract_status ?? prev.contractStatus,
          moveInDate: bookingPatch.move_in_date ?? prev.moveInDate,
          moveOutDate: bookingPatch.move_out_date ?? prev.moveOutDate,
          status: bookingPatch.status ?? prev.status,
        };
      });
      setEditSaving(false);
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t("admin.tenants.updateTenantFailed", "Failed to update tenant.")
      );
      setEditSaving(false);
    },
  });

  const adminUploadSignedContractMutation = useMutation({
    mutationFn: async ({
      bookingId,
      file,
      currentContractStatus,
    }: {
      bookingId: number;
      file: File;
      currentContractStatus?: string;
    }) => {
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) throw new Error("Please upload a PDF file");

      const fileName = `signed-contracts/admin-booking-${bookingId}-${Date.now()}.pdf`;
      const { error: uploadError } = await supabase.storage
        .from("contracts")
        .upload(fileName, file, {
          contentType: "application/pdf",
          upsert: false,
        });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("contracts")
        .getPublicUrl(fileName);

      const patch: Record<string, unknown> = {
        signed_contract_url: urlData.publicUrl,
        manual_contract_upload: true,
        contract_signed_at: new Date().toISOString(),
        // A custom card name belongs to the file it was given to.
        contract_display_name: null,
      };
      const cs = currentContractStatus || "";
      if (cs !== "approved" && cs !== "signed") {
        patch.contract_status = "approved";
      }

      const { error } = await supabase
        .from("bookings")
        .update(patch)
        .eq("id", bookingId);
      if (error) throw error;
      return { publicUrl: urlData.publicUrl, patch };
    },
    onSuccess: (data, variables) => {
      invalidateOccupancyViews(queryClient);
      toast.success(
        t(
          "admin.tenants.signedContractUploadedToast",
          "Signed contract uploaded. Manual contract label will show in the list."
        )
      );
      // Owner "New Lease Agreement Signed" mail — fire-and-forget.
      void supabase.functions
        .invoke("notify-owner-lifecycle", {
          body: { event: "lease_signed", bookingId: variables.bookingId },
        })
        .catch(() => {});
      setSelectedTenant((prev: any) => {
        if (!prev || prev.id !== variables.bookingId) return prev;
        const nextBooking = {
          ...prev.booking,
          signed_contract_url: data.publicUrl,
          contract_display_name: null,
          manual_contract_upload: true,
          contract_signed_at:
            (data.patch.contract_signed_at as string) ??
            prev.booking?.contract_signed_at,
          ...(data.patch.contract_status
            ? { contract_status: data.patch.contract_status }
            : {}),
        };
        return {
          ...prev,
          signedContractUrl: data.publicUrl,
          manualContractUpload: true,
          showManualContractLabel: true,
          contractStatus:
            (data.patch.contract_status as string) || prev.contractStatus,
          contractSigned: true,
          booking: nextBooking,
        };
      });
      if (adminSignedContractInputRef.current)
        adminSignedContractInputRef.current.value = "";
    },
    onError: (error: any) => {
      toast.error(
        error.message || t("admin.tenants.uploadFailedToast", "Upload failed")
      );
    },
  });

  // Accent per pipeline bucket — shared by the stat cards and, so the active
  // filter reads as one connected state, echoed on the status Select trigger.
  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card className="border-border/60 shadow-sm overflow-hidden">
        <CardHeader className="border-b border-border/40 bg-muted/20 pb-4">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-xl tracking-tight">
              {t("admin.tenants.bookingsAndTenantsTitle", "Bookings & Tenants")}
            </CardTitle>
            {canCreateTenant && (
              <Button
                size="sm"
                className="gap-1.5 h-9 rounded-xl shrink-0"
                onClick={() => navigate("/admin/tenants/new")}
              >
                <Plus className="w-4 h-4" />
                {t("admin.tenants.createTenantButton", "Create Tenant")}
              </Button>
            )}
          </div>
          <CardDescription className="text-sm">
            {t(
              "admin.tenants.filterByJourneyStage",
              "Filter by journey stage."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-4 mb-4">
            <div className="flex-1 min-w-0 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={t(
                  "admin.tenants.searchPlaceholderWithPhone",
                  "Search by name, email, phone, property, or room..."
                )}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-11 rounded-xl border-border/80 bg-background"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center shrink-0 w-full lg:w-auto lg:ml-auto">
              <Select
                value={stageFilter}
                onValueChange={(v) =>
                  setStageFilter(v as "all" | TenantJourneyStageId)
                }
              >
                <SelectTrigger
                  className="h-11 w-full sm:w-[min(100%,220px)] lg:w-[220px] rounded-xl bg-background"
                  aria-label={t(
                    "admin.tenants.filterByTenantStatusAria",
                    "Filter by tenant status"
                  )}
                >
                  <SelectValue
                    placeholder={t(
                      "admin.tenants.allStatusesPlaceholder",
                      "All statuses"
                    )}
                  />
                </SelectTrigger>
                <SelectContent
                  align="end"
                  className="rounded-xl min-w-[var(--radix-select-trigger-width)]"
                >
                  <SelectItem value="all">
                    All statuses ({stageCounts.all})
                  </SelectItem>
                  {TENANT_JOURNEY_STAGES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {tenantJourneyStageLabel(t, s.id)} (
                      {stageCounts[s.id] ?? 0})
                    </SelectItem>
                  ))}
                  <SelectItem value="archived">
                    Archived ({stageCounts.archived})
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                className="gap-2 h-11 rounded-xl shrink-0 border-border/80 w-full sm:w-auto"
                onClick={exportToCSV}
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
          </div>

          {/* Test/CoLiviq accounts toggle — off by default, so internal and
              test bookings stay out of the list, search and status counts
              above unless an admin explicitly opts back in. */}
          <div className="flex items-center gap-2.5 mb-6">
            <Switch
              id="include-test-accounts"
              checked={includeTestAccounts}
              onCheckedChange={setIncludeTestAccounts}
            />
            <Label
              htmlFor="include-test-accounts"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              {brandText(
                t(
                  "admin.tenants.includeTestAccounts",
                  "Include test/coliviq accounts"
                )
              )}
              {testAccountCount > 0 ? ` (${testAccountCount})` : ""}
            </Label>
          </div>

          {/* Tenant Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredTenants.length > 0 ? (
            <>
              <div className="overflow-hidden overflow-x-auto rounded-2xl border border-border/60 bg-card shadow-sm ring-1 ring-border/20">
                <Table className="min-w-[820px]">
                  <TableHeader>
                    <TableRow className="sticky top-0 z-10 border-border/60 bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                      <TableHead className="py-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Tenant
                      </TableHead>
                      <TableHead className="py-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Property
                      </TableHead>
                      <TableHead className="py-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Room
                      </TableHead>
                      <TableHead className="py-3.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Status
                      </TableHead>
                      <TableHead className="py-3.5 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedGroups.map((group) => {
                      // A tenant with only one room application never folds —
                      // its resting row shows View + Actions directly. Folding
                      // only kicks in once there's more than one application
                      // to group. An active search force-expands matching
                      // groups so hits are never hidden inside a collapsed row.
                      const singleApp = group.applications.length === 1;
                      const searching = !!search.trim();
                      const expanded =
                        !singleApp &&
                        (searching || expandedTenants.has(group.key));
                      const head = group.first;
                      const headName =
                        head.profile?.name || head.guestName || "Unknown";
                      // Missing personal info → a small red alert replaces the
                      // (now-removed) email line. Checks name / DOB / email /
                      // phone; DOB lives only on the profile.
                      // Cap at 2 named fields + "+N more" so a bare guest
                      // booking (often missing several) stays a short line.
                      // Shared with the profile's attention strip and the room
                      // card (src/lib/tenantInfoGaps.ts).
                      const missingLabel =
                        tenantInfoGapLabel(
                          {
                            name: head.profile?.name || head.guestName,
                            dateOfBirth: head.profile?.date_of_birth,
                            email: head.profile?.email || head.guestEmail,
                            phone: head.profile?.phone || head.guestPhone,
                          },
                          2,
                          t
                        ) ?? "";
                      const total = group.applications.length;
                      // Rooms still in the pre-move-in pipeline with a due/overdue
                      // move-in drive the amber "needs attention" tint on the
                      // header row (derived from the journey stage — the same
                      // source of truth as each room's Status badge).
                      const nowTs = Date.now();
                      let pendingCount = 0;
                      for (const a of group.applications) {
                        if (isPreMoveInJourneyStage(a.journeyStageId)) {
                          const miTs = a.moveInDate
                            ? new Date(a.moveInDate).getTime()
                            : NaN;
                          if (Number.isNaN(miTs) || miTs <= nowTs) {
                            pendingCount++;
                          }
                        }
                      }
                      // A room counts as fully committed when its contract is
                      // signed AND its rent is paid (or manually marked paid).
                      // "Signed" mirrors the row's badge: a DocuSign/portal
                      // signature (contractSigned) OR a manually supplied
                      // contract (the "MANUAL CONTRACT" label) — a manual upload
                      // never sets contract_signed_at, so contractSigned alone
                      // would miss it.
                      const isSignedApp = (a: any) =>
                        a.contractSigned || a.showManualContractLabel;
                      const isPaidApp = (a: any) =>
                        a.rentPaid || a.manuallyMarkedPaid;
                      const paidRoomCount = group.applications.filter(
                        (a: any) => isSignedApp(a) && isPaidApp(a)
                      ).length;
                      // Fully committed: every room signed & paid and nothing
                      // pending — these get a distinct emerald highlight so they
                      // stand apart from in-progress tenants at a glance.
                      const fullyCommitted =
                        paidRoomCount > 0 &&
                        paidRoomCount === total &&
                        pendingCount === 0;
                      // Distinct journey statuses across the tenant's rooms —
                      // shown as badges in the header instead of raw counts, so
                      // the header reflects the actual status (source of truth).
                      const statusBadges: {
                        key: string;
                        stageId: TenantJourneyStageId;
                        terminalStatus: any;
                        archived: boolean;
                        archiveReason: string | null;
                      }[] = [];
                      const seenStatus = new Set<string>();
                      for (const a of group.applications) {
                        // Archived is part of the identity: the same stage
                        // archived and live are two different things to show,
                        // so they must not dedupe into one badge.
                        const archived = !!a.adminArchivedAt;
                        // The reason is part of the identity too: "Room filled"
                        // and a hand-archived row are different things to show,
                        // and folding them into one badge would hide which.
                        const archiveReason = archived
                          ? (a.adminArchiveReason ?? null)
                          : null;
                        const key = `${a.journeyStageId}|${a.journeyTerminalStatus ?? ""}|${archived}|${archiveReason ?? ""}`;
                        if (seenStatus.has(key)) continue;
                        seenStatus.add(key);
                        statusBadges.push({
                          key,
                          stageId: a.journeyStageId,
                          terminalStatus: a.journeyTerminalStatus,
                          archived,
                          archiveReason,
                        });
                      }
                      return (
                        <Fragment key={group.key}>
                          {/* Tenant header — collapses all room applications
                              from one account into a single expandable row,
                              unless there's only one application, in which
                              case this row IS the application row (no fold).
                              When open, a left rail ties it to its rooms. */}
                          <TableRow
                            onClick={
                              singleApp
                                ? () => handleViewDetails(group.applications[0])
                                : () => toggleTenantExpanded(group.key)
                            }
                            onKeyDown={(e) => {
                              if (e.target !== e.currentTarget) return;
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (singleApp) {
                                  handleViewDetails(group.applications[0]);
                                } else {
                                  toggleTenantExpanded(group.key);
                                }
                              }
                            }}
                            tabIndex={0}
                            aria-expanded={singleApp ? undefined : expanded}
                            className={`group cursor-pointer border-border/40 transition-colors ${
                              pendingCount > 0
                                ? "bg-amber-500/[0.035] hover:bg-amber-500/[0.08]"
                                : fullyCommitted
                                  ? "bg-emerald-500/[0.04] hover:bg-emerald-500/[0.09]"
                                  : "hover:bg-muted/40"
                            }`}
                          >
                            {/* Tenant */}
                            <TableCell
                              className={`py-4 pl-4 ${
                                pendingCount > 0
                                  ? "border-l-[3px] border-amber-400"
                                  : fullyCommitted
                                    ? "border-l-[3px] border-emerald-400"
                                    : "border-l-[3px] border-transparent group-hover:border-primary/30"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {/* Fixed-width slot keeps the avatar column
                                    aligned whether or not this row folds. */}
                                <div className="grid h-4 w-4 shrink-0 place-items-center">
                                  {!singleApp && (
                                    <ChevronDown
                                      className={`h-4 w-4 text-muted-foreground/70 transition-transform ${
                                        expanded ? "" : "-rotate-90"
                                      }`}
                                    />
                                  )}
                                </div>
                                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary/20 to-primary/[0.06] text-[13px] font-semibold text-primary ring-1 ring-primary/15">
                                  {String(headName)
                                    .trim()
                                    .slice(0, 1)
                                    .toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="truncate text-[13.5px] font-semibold text-foreground">
                                      {headName}
                                    </span>
                                    {!head.userId && (
                                      <span className="shrink-0 rounded-full border border-border/60 bg-background px-1.5 py-px text-[10px] font-medium text-muted-foreground">
                                        Guest
                                      </span>
                                    )}
                                  </div>
                                  {missingLabel && (
                                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600 ring-1 ring-red-100 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20">
                                      <AlertCircle className="h-3 w-3 shrink-0" />
                                      <span className="truncate">
                                        {missingLabel}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            {/* Property */}
                            <TableCell className="py-4 text-[12.5px] text-muted-foreground">
                              <span className="truncate">
                                {group.properties.length === 1
                                  ? group.properties[0]?.propertyTitle || "—"
                                  : `${group.properties.length} properties`}
                              </span>
                            </TableCell>
                            {/* Room */}
                            <TableCell className="py-4 text-[12.5px] text-muted-foreground">
                              {singleApp ? (
                                <div className="space-y-1.5">
                                  {renderManualFlags(group.applications[0], {
                                    showManualContract: false,
                                  })}
                                  <span className="truncate">
                                    {group.applications[0]?.room?.title || "—"}
                                  </span>
                                </div>
                              ) : (
                                `${total} rooms`
                              )}
                            </TableCell>
                            {/* Status */}
                            <TableCell className="py-4">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {statusBadges.map((s) => (
                                  <TenantJourneyStageBadge
                                    key={s.key}
                                    stageId={s.stageId}
                                    terminalStatus={s.terminalStatus}
                                    archived={s.archived}
                                    archiveReason={s.archiveReason}
                                  />
                                ))}
                              </div>
                            </TableCell>
                            {/* Actions — the single application's Actions
                                menu when there's nothing to fold, otherwise a
                                subtle expand affordance. */}
                            <TableCell
                              className="py-4 pr-4 text-right"
                              onClick={
                                singleApp
                                  ? (e) => e.stopPropagation()
                                  : undefined
                              }
                            >
                              {singleApp ? (
                                <TenantRowActions
                                  tenant={group.applications[0]}
                                  isSuperAdmin={isSuperAdmin}
                                  canManageAccount={canManageTenant}
                                  canActAs={canActAs}
                                  currentUserId={currentAuthUser?.id ?? null}
                                  onApprove={(id) =>
                                    approveBookingMutation.mutate(id)
                                  }
                                  approving={approveBookingMutation.isPending}
                                  onReassign={setReassignFor}
                                  onActAsTenant={actAsTenant}
                                  onArchive={(p) =>
                                    archiveBookingMutation.mutate(p)
                                  }
                                  archiving={archiveBookingMutation.isPending}
                                />
                              ) : (
                                <span className="text-[11px] font-medium text-muted-foreground/50 opacity-0 transition-opacity group-hover:opacity-100">
                                  {expanded
                                    ? t(
                                        "admin.tenants.collapseLabel",
                                        "Collapse"
                                      )
                                    : t("admin.tenants.expandLabel", "Expand")}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                          {/* Each room application on its own row, carrying its
                              own Property + Room columns (the former per-property
                              band is folded into the Property column). Skipped
                              for a single application — its details already live
                              in the header row above. */}
                          {!singleApp &&
                            expanded &&
                            group.applications.map((room: any) =>
                              renderTenantRow(room, searching)
                            )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mt-4">
                  <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(
                      currentPage * itemsPerPage,
                      groupedTenants.length
                    )}{" "}
                    of {groupedTenants.length} tenants
                  </div>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => {
                          // Show first page, last page, current page, and pages around current
                          if (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          ) {
                            return (
                              <Button
                                key={page}
                                variant={
                                  currentPage === page ? "default" : "outline"
                                }
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className="min-w-[40px]"
                              >
                                {page}
                              </Button>
                            );
                          } else if (
                            page === currentPage - 2 ||
                            page === currentPage + 2
                          ) {
                            return (
                              <span key={page} className="px-2">
                                ...
                              </span>
                            );
                          }
                          return null;
                        }
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-14 rounded-xl border border-dashed border-border/60 bg-muted/10">
              <User className="w-12 h-12 text-muted-foreground/35 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">
                {stageFilter === "archived"
                  ? t(
                      "admin.tenants.emptyArchived",
                      "No archived bookings yet."
                    )
                  : stageFilter !== "all"
                    ? t(
                        "admin.tenants.emptyStage",
                        "No tenants at the “{stage}” stage."
                      ).replace(
                        "{stage}",
                        tenantJourneyStageLabel(t, stageFilter)
                      )
                    : search.trim()
                      ? t(
                          "admin.tenants.emptySearch",
                          "No tenants match your search."
                        )
                      : t("admin.tenants.emptyAll", "No bookings yet.")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tenant Detail — unified profile (modal that expands to full page).
          Replaces the former 4-tab dialog; see tenantJourney + TenantProfileDialog. */}
      <TenantProfileDialog
        tenant={(selectedTenant as unknown as TenantProfileTenant) ?? null}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        isSuperAdmin={isSuperAdmin}
        canManageTenant={canManageTenant}
        currentUserId={currentAuthUser?.id ?? null}
        onGenerateContract={(bookingId, opts) =>
          invokeCreateContractForBooking(bookingId, opts).then(() => undefined)
        }
        onSendContract={(bookingId) =>
          invokeSendContractToTenant(bookingId).then(() => undefined)
        }
        headerActions={
          selectedTenant ? (
            // Same menu as the table row; selectedTenant is re-synced from the
            // live list above, so its items track approve/archive/deactivate.
            <TenantRowActions
              tenant={selectedTenant}
              isSuperAdmin={isSuperAdmin}
              canManageAccount={canManageTenant}
              canActAs={canActAs}
              currentUserId={currentAuthUser?.id ?? null}
              onApprove={(id) => approveBookingMutation.mutate(id)}
              approving={approveBookingMutation.isPending}
              onReassign={setReassignFor}
              onActAsTenant={actAsTenant}
              onArchive={(p) => archiveBookingMutation.mutate(p)}
              archiving={archiveBookingMutation.isPending}
              onDeleted={() => setDetailDialogOpen(false)}
              triggerClassName="h-[38px] w-[38px] rounded-[11px] border border-[#E7E9EC] bg-white text-[#475569] hover:bg-slate-50"
            />
          ) : null
        }
      />

      {/* Rent Edit Dialog */}
      <Dialog open={rentEditOpen} onOpenChange={setRentEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t("admin.tenants.editRentTitle", "Edit rent")}
            </DialogTitle>
            <DialogDescription>
              Update the contract monthly rent, and optionally override the
              first month (auto pro‑rata is daily — it charges only the days
              occupied in the move‑in month).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="admin-edit-monthly-rent">
                Monthly rent (full contract) *
              </Label>
              <Input
                id="admin-edit-monthly-rent"
                inputMode="decimal"
                value={editMonthlyRent}
                onChange={(e) => setEditMonthlyRent(e.target.value)}
                placeholder="e.g. 600.00"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="admin-edit-first-month-override">
                  First month override (optional)
                </Label>
                <div className="flex flex-wrap gap-1.5 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditFirstMonthOverride("")}
                  >
                    Auto
                  </Button>
                  {[25, 50, 75, 100].map((p) => (
                    <Button
                      key={p}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // `?? 0` on the right of `Number(...)` is unreachable — Number()
                        // returns 0 for nullish input, never null/undefined. Drop it.
                        const base =
                          parseEurInput(editMonthlyRent) ??
                          Number(selectedTenant?.monthlyRent ?? 0);
                        const amt =
                          base > 0
                            ? Math.round(base * (p / 100) * 100) / 100
                            : 0;
                        setEditFirstMonthOverride(amt > 0 ? String(amt) : "");
                      }}
                    >
                      {p}%
                    </Button>
                  ))}
                </div>
              </div>
              <Input
                id="admin-edit-first-month-override"
                inputMode="decimal"
                value={editFirstMonthOverride}
                onChange={(e) => setEditFirstMonthOverride(e.target.value)}
                placeholder="Leave empty for auto pro‑rata"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRentEditOpen(false)}
              disabled={savingRent}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveRent}
              disabled={savingRent}
            >
              {savingRent ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COL-220 reassign dialog — mounted at the component root so it
          renders above any open row state / detail dialogs without being
          unmounted when the actions cell re-renders. */}
      {isSuperAdmin && reassignFor && (
        <ReassignRoomDialog
          open={reassignFor !== null}
          onOpenChange={(o) => {
            if (!o) setReassignFor(null);
          }}
          initialBookingId={reassignFor.bookingId}
          initialTenantName={reassignFor.tenantName}
          initialFromRoomId={reassignFor.fromRoomId}
          propertyId={reassignFor.propertyId}
        />
      )}
    </div>
  );
};
